import React, { useState, useEffect, useRef } from 'react';
import { Form, Row, Col, FormCheck, Nav, Tab } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import Select, { MultiValue } from 'react-select';
import CreatableSelect from 'react-select/creatable';
import AsyncSelect from 'react-select/async';
import { toast } from 'react-toastify';
import secureApi from '../../../utils/secureApi';
import { VideoCategory, EntityType, DifficultyLevel } from '../../../api/community/types';
import { communityService } from '../../../api/community/communityService';
import './AddNewCommunityForm.css';

interface AddNewCommunityFormProps {
  show: boolean;
  onHide: () => void;
  category: VideoCategory;
  onSuccess?: () => void;
  video?: any; // Optional video data for edit mode
  mode?: 'create' | 'edit'; // Form mode
  inline?: boolean; // If true, render inline instead of sidebar
  initialTab?: string; // Optional initial tab to show (e.g., 'chapters')
  onChapterAdded?: () => void; // Optional callback when a single chapter is added (for refreshing chapters list without closing form)
}

interface TagOption {
  value: string;
  label: string;
}

interface EntityOption {
  value: string; // entityId
  label: string; // entityName
  entityId: string;
  entityName: string;
  profilePic: string;
  entityType: EntityType;
  sports?: string[]; // For coaches, store sports array
  basicInfo?: any; // For coaches, store basicInfo from API
}

const AddNewCommunityForm: React.FC<AddNewCommunityFormProps> = ({
  show,
  onHide,
  category,
  onSuccess,
  video,
  mode = 'create',
  inline = false,
  initialTab,
  onChapterAdded
}) => {
  const { user } = useSelector((state: any) => state.Login);
  const isEditMode = mode === 'edit' && video;
  
  // For Level Up create mode: track whether user selected standalone or series
  const [levelUpContentType, setLevelUpContentType] = useState<'standalone' | 'series' | null>(
    category === VideoCategory.LEVEL_UP && !isEditMode ? null : null
  );
  
  // Determine if this is a standalone Level Up video
  // In edit mode: check videoType/contentType - standalone if videoType is 'single' OR (contentType is 'long_form' AND videoType is NOT 'series' AND no seriesId)
  // In create mode: check levelUpContentType state (set by user selection in form)
  const isStandaloneLevelUp = category === VideoCategory.LEVEL_UP && (
    isEditMode 
      ? (video?.videoType === 'single' || (video?.contentType === 'long_form' && video?.videoType !== 'series' && !video?.seriesId))
      : levelUpContentType === 'standalone'
  );
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    schedulePublish: false,
    publishDate: '',
    publishTime: ''
  });

  const [selectedTags, setSelectedTags] = useState<MultiValue<TagOption>>([]);
  const [existingTags, setExistingTags] = useState<TagOption[]>([]);
  const [loadingTags, setLoadingTags] = useState(false);
  
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [videoPreview, setVideoPreview] = useState<string | null>(null);
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
  const [thumbnailPreview, setThumbnailPreview] = useState<string | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  
  // Auto-upload state for Actions category
  const [videoUploadStatus, setVideoUploadStatus] = useState<'idle' | 'uploading' | 'uploaded' | 'error'>('idle');
  const [videoUploadProgress, setVideoUploadProgress] = useState<number>(0);
  const [videoUploadError, setVideoUploadError] = useState<string | null>(null);
  const [uploadedVideoData, setUploadedVideoData] = useState<{ videoId: string; videoKey: string } | null>(null);
  
  const [thumbnailUploadStatus, setThumbnailUploadStatus] = useState<'idle' | 'uploading' | 'uploaded' | 'error'>('idle');
  const [thumbnailUploadProgress, setThumbnailUploadProgress] = useState<number>(0);
  const [thumbnailUploadError, setThumbnailUploadError] = useState<string | null>(null);
  const [uploadedThumbnailKey, setUploadedThumbnailKey] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>('video-details');
  const [hasFormChanges, setHasFormChanges] = useState(false);
  const [isPortrait, setIsPortrait] = useState<boolean>(false);
  const [isFeatured, setIsFeatured] = useState<boolean>(false);
  
  const [postType, setPostType] = useState<'video' | 'image'>('video');
  
  const [selectedEntityType, setSelectedEntityType] = useState<EntityType | null>(null);
  const [selectedEntity, setSelectedEntity] = useState<EntityOption | null>(null);

  const [seriesData, setSeriesData] = useState({
    title: '',
    description: '',
    subCategory: '',
    sport: '',
    difficultyLevel: DifficultyLevel.BEGINNER,
    thumbnailUrl: '',
    coverImage: '',
    featured: false
  });
  const [seriesThumbnailFile, setSeriesThumbnailFile] = useState<File | null>(null);
  const [seriesThumbnailPreview, setSeriesThumbnailPreview] = useState<string | null>(null);
  const [seriesCoverFile, setSeriesCoverFile] = useState<File | null>(null);
  const [seriesCoverPreview, setSeriesCoverPreview] = useState<string | null>(null);
  const [coachProfilePicFile, setCoachProfilePicFile] = useState<File | null>(null);
  const [coachProfilePicPreview, setCoachProfilePicPreview] = useState<string | null>(null);
  const [createdSeriesId, setCreatedSeriesId] = useState<string | null>(null);
  const [isCreatingSeries, setIsCreatingSeries] = useState(false);

  interface Chapter {
    id: string;
    _id?: string; // Added for uploaded chapters
    title: string;
    description: string;
    videoFile: File | null;
    videoPreview: string | null;
    thumbnailFile: File | null;
    thumbnailPreview: string | null;
    uploading: boolean;
    uploaded?: boolean; // Track if chapter has been successfully uploaded
  }
  const [chapters, setChapters] = useState<any[]>([]);
  const [uploadingChapters, setUploadingChapters] = useState(false);
  const [loadingChapters, setLoadingChapters] = useState(false);
  const [hasUploadedChapters, setHasUploadedChapters] = useState(false);
  const [selectedChapterForEdit, setSelectedChapterForEdit] = useState<any>(null);
  const [originalChapterData, setOriginalChapterData] = useState<any>(null);
  const [hasChapterChanges, setHasChapterChanges] = useState(false);
  const [showAddChapterForm, setShowAddChapterForm] = useState<boolean>(false);
  const [openChapterMenuId, setOpenChapterMenuId] = useState<string | null>(null);
  const [unpublishingChapterId, setUnpublishingChapterId] = useState<string | null>(null);
  const hasAutoAddedFirstChapter = useRef(false);

  useEffect(() => {
    if (!show && category === VideoCategory.LEVEL_UP && !isEditMode) {
      setLevelUpContentType(null);
    }
  }, [show, category, isEditMode]);

  useEffect(() => {
    if (show) {
      loadTags();
      
      if (initialTab) {
        setActiveTab(initialTab);
        if (initialTab === 'chapters' && isEditMode && video?.videoType === 'series' && video?._id) {
          setCreatedSeriesId(video._id);
          setChapters([]);
        }
        return;
      }
      
      if (category === VideoCategory.LEVEL_UP) {
        if (isStandaloneLevelUp) {
          setActiveTab('video-details');
        } else if (isEditMode && (video?.videoType === 'chapter' || (video?.seriesId && video?.videoType !== 'series'))) {
          setActiveTab('video-details');
        } else if (isEditMode && video?.videoType === 'series') {
          setActiveTab('series-details');
        } else if (levelUpContentType === 'series') {
          setActiveTab('series-details');
        } else if (levelUpContentType === 'standalone') {
          setActiveTab('video-details');
        }
      } else {
        setActiveTab('video-details');
      }
    }
  }, [show, isEditMode, category, video?.seriesId, video?.videoType, video?.contentType, isStandaloneLevelUp, levelUpContentType, initialTab]);

  useEffect(() => {
    if (show && !inline) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [show, inline]);

  useEffect(() => {
    if (!show || category !== VideoCategory.ACTION) {
      setVideoUploadStatus('idle');
      setVideoUploadProgress(0);
      setVideoUploadError(null);
      setUploadedVideoData(null);
      setThumbnailUploadStatus('idle');
      setThumbnailUploadProgress(0);
      setThumbnailUploadError(null);
      setUploadedThumbnailKey(null);
    }
  }, [show, category]);

  useEffect(() => {
    setUploadError(null);
  }, [show]);

  useEffect(() => {
    if (show && initialTab === 'chapters' && isEditMode && video?.videoType === 'series' && createdSeriesId && chapters.length === 0 && !hasAutoAddedFirstChapter.current) {
      const timer = setTimeout(() => {
        if (chapters.length === 0 && !hasAutoAddedFirstChapter.current && createdSeriesId) {
          hasAutoAddedFirstChapter.current = true;
          addNewChapter();
        }
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [show, initialTab, isEditMode, video?.videoType, createdSeriesId, chapters.length]);

  useEffect(() => {
    if (!show) {
      hasAutoAddedFirstChapter.current = false;
    }
  }, [show]);

  useEffect(() => {
    if (isEditMode && video && show) {
      
      const isStandalone = category === VideoCategory.LEVEL_UP && (
        video.videoType === 'single' || 
        (video.contentType === 'long_form' && video.videoType !== 'series' && !video.seriesId)
      );
      
      const isSeries = category === VideoCategory.LEVEL_UP && !isStandalone && video.videoType === 'series';
      const isChapter = category === VideoCategory.LEVEL_UP && !isStandalone && (video.videoType === 'chapter' || (video.seriesId && video.videoType !== 'series'));
      
      if (category === VideoCategory.LEVEL_UP && !isStandalone && (isSeries || isChapter)) {
        const loadSeriesAndChapterData = async () => {
          try {
            setLoadingChapters(true);
            
            let currentVideoData = video;
            
            if (isSeries) {
              const seriesResponse = await communityService.getSeriesById(video._id);
              let seriesDataFromApi = null;
              
              if (seriesResponse?.data && typeof seriesResponse.data === 'object') {
                seriesDataFromApi = seriesResponse.data;
              } else if (seriesResponse && typeof seriesResponse === 'object' && (seriesResponse._id || seriesResponse.title)) {
                seriesDataFromApi = seriesResponse;
              } else if (seriesResponse?.series && typeof seriesResponse.series === 'object') {
                seriesDataFromApi = seriesResponse.series;
              }
              
              if (seriesDataFromApi) {
              setHasFormChanges(false);
              
              setSeriesData({
                title: seriesDataFromApi.title || '',
                description: seriesDataFromApi.description || '',
                subCategory: seriesDataFromApi.subCategory || '',
                sport: seriesDataFromApi.sport || '',
                difficultyLevel: seriesDataFromApi.difficultyLevel || DifficultyLevel.BEGINNER,
                thumbnailUrl: seriesDataFromApi.thumbnailUrl || '',
                coverImage: seriesDataFromApi.coverImage || '',
                featured: seriesDataFromApi.featured || false
              });
              
              if (seriesDataFromApi.featured !== undefined) {
                setIsFeatured(seriesDataFromApi.featured);
              }
              
              if (seriesDataFromApi.thumbnailUrl) {
                const thumbnailUrl = seriesDataFromApi.thumbnailUrl.startsWith('http') 
                  ? seriesDataFromApi.thumbnailUrl 
                  : `https://cdn-dev.sportsforlife.in/${seriesDataFromApi.thumbnailUrl}`;
                setSeriesThumbnailPreview(thumbnailUrl);
              }
              
              if (seriesDataFromApi.coverImage) {
                const coverUrl = seriesDataFromApi.coverImage.startsWith('http') 
                  ? seriesDataFromApi.coverImage 
                  : `https://cdn-dev.sportsforlife.in/${seriesDataFromApi.coverImage}`;
                setSeriesCoverPreview(coverUrl);
              }
              
              if (seriesDataFromApi.coachId && seriesDataFromApi.coachName) {
                setSelectedEntityType(EntityType.COACH);
                
                const basicInfo = seriesDataFromApi.metadata?.basicInfo || seriesDataFromApi.coachBasicInfo || {};
                const coachSports = Array.isArray(seriesDataFromApi.coachSports) ? seriesDataFromApi.coachSports : [];
                const coachProfilePic = seriesDataFromApi.coachProfilePicture || '';
                
                // Check if basicInfo is empty or only has minimal data
                const hasMinimalBasicInfo = Object.keys(basicInfo).length === 0 || 
                  (Object.keys(basicInfo).length === 1 && basicInfo.sports);
                
                // If basicInfo is empty or minimal, fetch coach details from API
                let coachBasicInfo = basicInfo;
                if (hasMinimalBasicInfo) {
                  try {
                    // Try searching by coach name first, then filter by ID
                    const coaches = await communityService.getAllCoachesForAdmin(seriesDataFromApi.coachName);
                    const matchingCoach = coaches.find((coach: any) => 
                      (coach._id || coach.coachId || coach.id) === seriesDataFromApi.coachId
                    );
                    
                    if (matchingCoach) {
                      // Extract sports array - ensure it's always an array
                      const sportsArray = Array.isArray(matchingCoach.basicInfo?.sports) 
                        ? matchingCoach.basicInfo.sports 
                        : Array.isArray(matchingCoach.sports) 
                        ? matchingCoach.sports 
                        : [];
                      
                      const coachName = matchingCoach.name || matchingCoach.coachName || matchingCoach.fullName || matchingCoach.basicInfo?.fullName || seriesDataFromApi.coachName || '';
                      const coachPic = matchingCoach.basicInfo?.profilePic || matchingCoach.profilePic || matchingCoach.profilePicture || matchingCoach.profileImage || coachProfilePic || '';
                      
                      // Use the coach's basicInfo if available, otherwise construct it from the coach object
                      // Use the same logic as loadEntities to ensure consistency
                      const coachBasicInfoFromEntity = matchingCoach.basicInfo || {};
                      coachBasicInfo = {
                        ...coachBasicInfoFromEntity,
                        // Ensure all fields are included, checking both basicInfo and top-level properties
                        ...(sportsArray.length > 0 ? { sports: sportsArray } : {}),
                        ...(coachPic ? { profilePic: coachPic } : {}),
                        ...(coachName ? { fullName: coachName } : {}),
                        // Always include these fields, even if null/empty
                        email: coachBasicInfoFromEntity.email !== undefined ? coachBasicInfoFromEntity.email : (matchingCoach.email !== undefined ? matchingCoach.email : null),
                        dob: coachBasicInfoFromEntity.dob !== undefined ? coachBasicInfoFromEntity.dob : (matchingCoach.dob !== undefined ? matchingCoach.dob : null),
                        ageCategory: Array.isArray(coachBasicInfoFromEntity.ageCategory) ? coachBasicInfoFromEntity.ageCategory : (Array.isArray(matchingCoach.ageCategory) ? matchingCoach.ageCategory : []),
                        certification: Array.isArray(coachBasicInfoFromEntity.certification) ? coachBasicInfoFromEntity.certification : (Array.isArray(matchingCoach.certification) ? matchingCoach.certification : []),
                        experience: coachBasicInfoFromEntity.experience !== undefined ? coachBasicInfoFromEntity.experience : (matchingCoach.experience !== undefined ? matchingCoach.experience : null),
                        ...(coachBasicInfoFromEntity.location !== undefined ? { location: coachBasicInfoFromEntity.location } : (matchingCoach.location !== undefined ? { location: matchingCoach.location } : {})),
                        _id: coachBasicInfoFromEntity._id || matchingCoach._id || matchingCoach.coachId || matchingCoach.id || seriesDataFromApi.coachId || ''
                      };
                    } else {
                      // If coach not found, at least ensure we have the fields from series response
                      coachBasicInfo = {
                        ...coachBasicInfo,
                        _id: seriesDataFromApi.coachId || ''
                      };
                    }
                  } catch (error) {
                    console.error('Error fetching coach details:', error);
                    // If fetch fails, ensure we at least have coachId
                    coachBasicInfo = {
                      ...coachBasicInfo,
                      _id: seriesDataFromApi.coachId || ''
                    };
                  }
                }
                
                // Merge all available data into basicInfoComplete
                // IMPORTANT: Always explicitly set all fields, don't rely on spreading empty objects
                const basicInfoComplete: any = {
                  // Always set these fields explicitly (don't rely on spread of empty object)
                  fullName: seriesDataFromApi.coachName || coachBasicInfo.fullName || '',
                  profilePic: coachProfilePic || coachBasicInfo.profilePic || null,
                  sports: coachSports.length > 0 ? coachSports : (coachBasicInfo.sports || []),
                  // Explicitly ensure all expected fields are included (preserve null/empty if they exist in coachBasicInfo, otherwise set defaults)
                  dob: coachBasicInfo.dob !== undefined ? coachBasicInfo.dob : null,
                  email: coachBasicInfo.email !== undefined ? coachBasicInfo.email : null,
                  ageCategory: Array.isArray(coachBasicInfo.ageCategory) ? coachBasicInfo.ageCategory : [],
                  certification: Array.isArray(coachBasicInfo.certification) ? coachBasicInfo.certification : [],
                  experience: coachBasicInfo.experience !== undefined ? coachBasicInfo.experience : null,
                  _id: coachBasicInfo._id || seriesDataFromApi.coachId || ''
                };
                
                // Only add location if it exists (optional field)
                if (coachBasicInfo.location !== undefined) {
                  basicInfoComplete.location = coachBasicInfo.location;
                }
                
                // Preserve any other fields from coachBasicInfo that we haven't explicitly set
                Object.keys(coachBasicInfo).forEach(key => {
                  if (!basicInfoComplete.hasOwnProperty(key)) {
                    basicInfoComplete[key] = coachBasicInfo[key];
                  }
                });
                
                // Final validation: Ensure basicInfoComplete is never empty
                if (!basicInfoComplete || Object.keys(basicInfoComplete).length === 0) {
                  console.error('ERROR: basicInfoComplete is empty after construction! Using fallback values');
                  basicInfoComplete.fullName = seriesDataFromApi.coachName || '';
                  basicInfoComplete.profilePic = coachProfilePic || null;
                  basicInfoComplete.sports = coachSports.length > 0 ? coachSports : [];
                  basicInfoComplete.dob = null;
                  basicInfoComplete.email = null;
                  basicInfoComplete.ageCategory = [];
                  basicInfoComplete.certification = [];
                  basicInfoComplete.experience = null;
                  basicInfoComplete._id = seriesDataFromApi.coachId || '';
                }
                
                
                setSelectedEntity({
                  value: seriesDataFromApi.coachId,
                  label: seriesDataFromApi.coachName,
                  entityId: seriesDataFromApi.coachId,
                  entityName: seriesDataFromApi.coachName,
                  profilePic: coachProfilePic,
                  entityType: EntityType.COACH,
                  sports: coachSports,
                  basicInfo: basicInfoComplete
                });
              }
              
              if (seriesDataFromApi.tags && Array.isArray(seriesDataFromApi.tags)) {
                const tagOptions = seriesDataFromApi.tags.map((tag: string) => ({ value: tag, label: tag }));
                setSelectedTags(tagOptions);
              }
              
              setCreatedSeriesId(seriesDataFromApi._id || video._id);
              
              if (isSeries && isEditMode && !initialTab) {
                setActiveTab('series-details');
              }
              
              if (initialTab !== 'chapters') {
              if (seriesDataFromApi.chapters && Array.isArray(seriesDataFromApi.chapters)) {
                setChapters(seriesDataFromApi.chapters);
              } else {
                  setChapters([]);
                }
                }
              }
            } else if (isChapter) {
              currentVideoData = video;
              
              // Only fetch video data if we don't have essential fields
              if (video._id && (!video.title || !video.description)) {
                try {
                  const videoResponse = await communityService.getVideoById(video._id);
                  if (videoResponse && (videoResponse.data || videoResponse.video || videoResponse)) {
                    const fetchedVideo = videoResponse.data?.video || videoResponse.data || videoResponse.video || videoResponse;
                    // Merge fetched data with existing video object to preserve seriesId if it was set
                    currentVideoData = {
                      ...video,
                      ...fetchedVideo,
                      seriesId: video.seriesId || fetchedVideo.seriesId // Preserve seriesId if it exists
                    };
                  }
                } catch (videoError) {
                  console.warn('Could not load individual video data, using provided video object:', videoError);
                }
              }
              
              // Ensure we have seriesId before fetching series data
              // If seriesId is missing, try to find it (should have been set in fetchVideoDetails, but as fallback)
              let seriesIdToUse = currentVideoData.seriesId || video.seriesId;
              
              if (!seriesIdToUse && video.videoType === 'chapter') {
                // Fallback: Try to find seriesId by searching series (this should rarely happen)
                console.warn('seriesId missing for chapter, attempting to find it...');
                try {
                  const seriesListResponse = await communityService.getAllSeries(1, 100, 'published,draft');
                  let seriesList: any[] = [];
                  if (seriesListResponse && (seriesListResponse as any).videos && Array.isArray((seriesListResponse as any).videos)) {
                    seriesList = (seriesListResponse as any).videos;
                  } else if ((seriesListResponse as any)?.data?.series && Array.isArray((seriesListResponse as any).data.series)) {
                    seriesList = (seriesListResponse as any).data.series;
                  } else if (Array.isArray((seriesListResponse as any)?.data)) {
                    seriesList = (seriesListResponse as any).data;
                  }
                  
                  for (const seriesItem of seriesList) {
                    if (seriesItem.chapters && Array.isArray(seriesItem.chapters)) {
                      const chapterFound = seriesItem.chapters.find((ch: any) => ch._id === video._id);
                      if (chapterFound) {
                        seriesIdToUse = seriesItem._id;
                        currentVideoData.seriesId = seriesItem._id;
                        break;
                      }
                    }
                  }
                } catch (seriesError) {
                  console.warn('Could not find seriesId for chapter in form:', seriesError);
                }
              }
              
              if (seriesIdToUse) {
                const seriesResponse = await communityService.getSeriesById(seriesIdToUse);
                let seriesDataFromApi = null;
                
                if (seriesResponse?.data && typeof seriesResponse.data === 'object') {
                  seriesDataFromApi = seriesResponse.data;
                } else if (seriesResponse && typeof seriesResponse === 'object' && (seriesResponse._id || seriesResponse.title)) {
                  seriesDataFromApi = seriesResponse;
                } else if (seriesResponse?.series && typeof seriesResponse.series === 'object') {
                  seriesDataFromApi = seriesResponse.series;
                }
                
                if (seriesDataFromApi) {
                  setCreatedSeriesId(seriesIdToUse);
                  
                  if (seriesDataFromApi.chapters && Array.isArray(seriesDataFromApi.chapters)) {
                    setChapters(seriesDataFromApi.chapters);
                  }
                  
                  // Extract coach information from series data and set selectedEntity
                  // This ensures basicInfo is available when uploading/updating chapters
                  if (seriesDataFromApi.coachId && seriesDataFromApi.coachName) {
                    setSelectedEntityType(EntityType.COACH);
                    
                    const basicInfo = seriesDataFromApi.metadata?.basicInfo || seriesDataFromApi.coachBasicInfo || {};
                    const coachSports = Array.isArray(seriesDataFromApi.coachSports) ? seriesDataFromApi.coachSports : [];
                    const coachProfilePic = seriesDataFromApi.coachProfilePicture || '';
                    
                    // Check if basicInfo is empty or only has minimal data
                    const hasMinimalBasicInfo = Object.keys(basicInfo).length === 0 || 
                      (Object.keys(basicInfo).length === 1 && basicInfo.sports);
                    
                    // If basicInfo is empty or minimal, fetch coach details from API
                    let coachBasicInfo = basicInfo;
                    if (hasMinimalBasicInfo) {
                      try {
                        // Try searching by coach name first, then filter by ID
                        const coaches = await communityService.getAllCoachesForAdmin(seriesDataFromApi.coachName);
                        const matchingCoach = coaches.find((coach: any) => 
                          (coach._id || coach.coachId || coach.id) === seriesDataFromApi.coachId
                        );
                        
                        if (matchingCoach) {
                          // Extract sports array - ensure it's always an array
                          const sportsArray = Array.isArray(matchingCoach.basicInfo?.sports) 
                            ? matchingCoach.basicInfo.sports 
                            : Array.isArray(matchingCoach.sports) 
                            ? matchingCoach.sports 
                            : [];
                          
                          const coachName = matchingCoach.name || matchingCoach.coachName || matchingCoach.fullName || matchingCoach.basicInfo?.fullName || seriesDataFromApi.coachName || '';
                          const coachPic = matchingCoach.basicInfo?.profilePic || matchingCoach.profilePic || matchingCoach.profilePicture || matchingCoach.profileImage || coachProfilePic || '';
                          
                          // Use the coach's basicInfo if available, otherwise construct it from the coach object
                          // Use the same logic as loadEntities to ensure consistency
                          const coachBasicInfoFromEntity = matchingCoach.basicInfo || {};
                          coachBasicInfo = {
                            ...coachBasicInfoFromEntity,
                            // Ensure all fields are included, checking both basicInfo and top-level properties
                            ...(sportsArray.length > 0 ? { sports: sportsArray } : {}),
                            ...(coachPic ? { profilePic: coachPic } : {}),
                            ...(coachName ? { fullName: coachName } : {}),
                            // Always include these fields, even if null/empty
                            email: coachBasicInfoFromEntity.email !== undefined ? coachBasicInfoFromEntity.email : (matchingCoach.email !== undefined ? matchingCoach.email : null),
                            dob: coachBasicInfoFromEntity.dob !== undefined ? coachBasicInfoFromEntity.dob : (matchingCoach.dob !== undefined ? matchingCoach.dob : null),
                            ageCategory: Array.isArray(coachBasicInfoFromEntity.ageCategory) ? coachBasicInfoFromEntity.ageCategory : (Array.isArray(matchingCoach.ageCategory) ? matchingCoach.ageCategory : []),
                            certification: Array.isArray(coachBasicInfoFromEntity.certification) ? coachBasicInfoFromEntity.certification : (Array.isArray(matchingCoach.certification) ? matchingCoach.certification : []),
                            experience: coachBasicInfoFromEntity.experience !== undefined ? coachBasicInfoFromEntity.experience : (matchingCoach.experience !== undefined ? matchingCoach.experience : null),
                            ...(coachBasicInfoFromEntity.location !== undefined ? { location: coachBasicInfoFromEntity.location } : (matchingCoach.location !== undefined ? { location: matchingCoach.location } : {})),
                            _id: coachBasicInfoFromEntity._id || matchingCoach._id || matchingCoach.coachId || matchingCoach.id || seriesDataFromApi.coachId || ''
                          };
                        } else {
                          // If coach not found, at least ensure we have the fields from series response
                          coachBasicInfo = {
                            ...coachBasicInfo,
                            _id: seriesDataFromApi.coachId || ''
                          };
                        }
                      } catch (error) {
                        console.error('Error fetching coach details:', error);
                        // If fetch fails, ensure we at least have coachId
                        coachBasicInfo = {
                          ...coachBasicInfo,
                          _id: seriesDataFromApi.coachId || ''
                        };
                      }
                    }
                    
                    // Merge all available data into basicInfoComplete
                    // IMPORTANT: Always explicitly set all fields, don't rely on spreading empty objects
                    const basicInfoComplete: any = {
                      // Always set these fields explicitly (don't rely on spread of empty object)
                      fullName: seriesDataFromApi.coachName || coachBasicInfo.fullName || '',
                      profilePic: coachProfilePic || coachBasicInfo.profilePic || null,
                      sports: coachSports.length > 0 ? coachSports : (coachBasicInfo.sports || []),
                      // Explicitly ensure all expected fields are included (preserve null/empty if they exist in coachBasicInfo, otherwise set defaults)
                      dob: coachBasicInfo.dob !== undefined ? coachBasicInfo.dob : null,
                      email: coachBasicInfo.email !== undefined ? coachBasicInfo.email : null,
                      ageCategory: Array.isArray(coachBasicInfo.ageCategory) ? coachBasicInfo.ageCategory : [],
                      certification: Array.isArray(coachBasicInfo.certification) ? coachBasicInfo.certification : [],
                      experience: coachBasicInfo.experience !== undefined ? coachBasicInfo.experience : null,
                      _id: coachBasicInfo._id || seriesDataFromApi.coachId || ''
                    };
                    
                    // Only add location if it exists (optional field)
                    if (coachBasicInfo.location !== undefined) {
                      basicInfoComplete.location = coachBasicInfo.location;
                    }
                    
                    // Preserve any other fields from coachBasicInfo that we haven't explicitly set
                    Object.keys(coachBasicInfo).forEach(key => {
                      if (!basicInfoComplete.hasOwnProperty(key)) {
                        basicInfoComplete[key] = coachBasicInfo[key];
                      }
                    });
                    
                    // Final validation: Ensure basicInfoComplete is never empty
                    if (!basicInfoComplete || Object.keys(basicInfoComplete).length === 0) {
                      console.error('ERROR: basicInfoComplete is empty after construction (chapter edit)! Using fallback values');
                      basicInfoComplete.fullName = seriesDataFromApi.coachName || '';
                      basicInfoComplete.profilePic = coachProfilePic || null;
                      basicInfoComplete.sports = coachSports.length > 0 ? coachSports : [];
                      basicInfoComplete.dob = null;
                      basicInfoComplete.email = null;
                      basicInfoComplete.ageCategory = [];
                      basicInfoComplete.certification = [];
                      basicInfoComplete.experience = null;
                      basicInfoComplete._id = seriesDataFromApi.coachId || '';
                    }
                    
                    
                    setSelectedEntity({
                      value: seriesDataFromApi.coachId,
                      label: seriesDataFromApi.coachName,
                      entityId: seriesDataFromApi.coachId,
                      entityName: seriesDataFromApi.coachName,
                      profilePic: coachProfilePic,
                      entityType: EntityType.COACH,
                      sports: coachSports,
                      basicInfo: basicInfoComplete
                    });
                  }
                }
              } else {
                console.warn('Cannot fetch series data: seriesId is missing for chapter');
              }
              
              // Reset hasFormChanges when loading chapter data in edit mode
              setHasFormChanges(false);
              
              // Store original chapter data for change tracking (when editing from sidebar/listing)
              // Store the full CDN URLs to match what will be in videoPreview/thumbnailPreview
              const originalVideoUrl = currentVideoData.contentUrl 
                ? (currentVideoData.contentUrl.startsWith('http') 
                    ? currentVideoData.contentUrl 
                    : `https://cdn-dev.sportsforlife.in/${currentVideoData.contentUrl}`)
                : null;
              const originalThumbnailUrl = currentVideoData.thumbnailUrl 
                ? (currentVideoData.thumbnailUrl.startsWith('http') 
                    ? currentVideoData.thumbnailUrl 
                    : `https://cdn-dev.sportsforlife.in/${currentVideoData.thumbnailUrl}`)
                : null;
              
              setOriginalChapterData({
                title: currentVideoData.chapterTitle || currentVideoData.title || '',
                description: currentVideoData.description || '',
                contentUrl: originalVideoUrl,
                thumbnailUrl: originalThumbnailUrl,
                isPortrait: currentVideoData.isPortrait || false
              });
              
              // Reset chapter change tracking
              setHasChapterChanges(false);
              
              // Set form data for chapter
            setFormData({
                title: currentVideoData.chapterTitle || currentVideoData.title || '',
                description: currentVideoData.description || '',
              schedulePublish: false,
              publishDate: '',
              publishTime: ''
            });
            
              if (currentVideoData.contentUrl) {
                setVideoPreview(originalVideoUrl);
            }
            
              if (currentVideoData.thumbnailUrl) {
                setThumbnailPreview(originalThumbnailUrl);
            }
            
              if (currentVideoData.isPortrait !== undefined) {
                setIsPortrait(currentVideoData.isPortrait);
            }
            }
          } catch (error: any) {
            console.error('Error loading series data:', error);
            toast.error(error?.message || 'Failed to load series data');
            if (isSeries && video._id) {
              setCreatedSeriesId(video._id);
            } else if (isChapter && video.seriesId) {
              setCreatedSeriesId(video.seriesId);
            }
          } finally {
            setLoadingChapters(false);
          }
        };
        
        loadSeriesAndChapterData();
        return;
      }
      
      // Reset hasFormChanges when loading form data in edit mode
      setHasFormChanges(false);
      
      setFormData({
        title: video.title || '',
        description: video.description || '',
        schedulePublish: false,
        publishDate: '',
        publishTime: ''
      });
      
      if (video.tags && Array.isArray(video.tags)) {
        const tagOptions = video.tags.map((tag: string) => ({ value: tag, label: tag }));
        setSelectedTags(tagOptions);
      }
      
      if (video.isPortrait !== undefined) {
        setIsPortrait(video.isPortrait);
      }
      
      if ((category === VideoCategory.ACTION || category === VideoCategory.SOCIAL || (category === VideoCategory.LEVEL_UP && isStandaloneLevelUp)) && video.featured !== undefined) {
        setIsFeatured(video.featured);
      }
      
      if (category === VideoCategory.SOCIAL) {
        const isImagePost = video.mediaType === 'image' || 
                           video.contentType === 'image' ||
                           (video.contentUrl && /\.(jpg|jpeg|png|gif|webp)$/i.test(video.contentUrl));
        const determinedPostType = isImagePost ? 'image' : 'video';
        setPostType(determinedPostType);
        
        if (isImagePost && video.contentUrl) {
          const imageUrl = video.contentUrl.startsWith('http') 
            ? video.contentUrl 
            : `https://cdn-dev.sportsforlife.in/${video.contentUrl}`;
          setImagePreview(imageUrl);
        } else {
          if (video.contentUrl) {
            const videoUrl = video.contentUrl.startsWith('http') 
              ? video.contentUrl 
              : `https://cdn-dev.sportsforlife.in/${video.contentUrl}`;
            setVideoPreview(videoUrl);
          }
          
          if (video.thumbnailUrl) {
            const thumbnailUrl = video.thumbnailUrl.startsWith('http') 
              ? video.thumbnailUrl 
              : `https://cdn-dev.sportsforlife.in/${video.thumbnailUrl}`;
            setThumbnailPreview(thumbnailUrl);
          }
        }
      } else {
        if (video.contentUrl) {
          const videoUrl = video.contentUrl.startsWith('http') 
            ? video.contentUrl 
            : `https://cdn-dev.sportsforlife.in/${video.contentUrl}`;
          setVideoPreview(videoUrl);
        }
        
        if (video.thumbnailUrl) {
          const thumbnailUrl = video.thumbnailUrl.startsWith('http') 
            ? video.thumbnailUrl 
            : `https://cdn-dev.sportsforlife.in/${video.thumbnailUrl}`;
          setThumbnailPreview(thumbnailUrl);
        }
      }
      
      // Load entity data for Action, Social, and standalone Level Up videos
      // Check if it's standalone Level Up: videoType === 'single' OR (contentType === 'long_form' AND videoType !== 'series' AND no seriesId)
      const isStandaloneLevelUpVideo = category === VideoCategory.LEVEL_UP && (
        video.videoType === 'single' || 
        (video.contentType === 'long_form' && video.videoType !== 'series' && !video.seriesId)
      );
      
      if (video.metadata && (category === VideoCategory.ACTION || category === VideoCategory.SOCIAL || isStandaloneLevelUpVideo)) {
        const metadata = video.metadata;
        let entityType: EntityType | null = null;
        
        // For Level Up standalone videos, check metadata.coachId or metadata.basicInfo.coachId
        if (isStandaloneLevelUpVideo) {
          // Level Up standalone videos always use Coach
          entityType = EntityType.COACH;
        } else if (metadata.type) {
          entityType = metadata.type as EntityType;
        } else if (metadata.entityType) {
          entityType = metadata.entityType as EntityType;
        } else if (metadata.playerId) {
          entityType = EntityType.PLAYER;
        } else if (metadata.coachId) {
          entityType = EntityType.COACH;
        } else if (metadata.academyId) {
          entityType = EntityType.ACADEMY;
        } else if (metadata.type === EntityType.SFL_USER || (metadata.id === 'sfl' && metadata.userName === 'sfl')) {
          entityType = EntityType.SFL_USER;
        }
        
        if (entityType) {
          setSelectedEntityType(entityType);
          
          if (entityType === EntityType.SFL_USER) {
            setSelectedEntity({
              value: 'sfl',
              label: 'SFL',
              entityId: 'sfl',
              entityName: 'sfl',
              profilePic: (metadata.userProfilePic && metadata.userProfilePic !== 'string') 
                ? metadata.userProfilePic 
                : 'shared/sfl-logo.png',
              entityType: EntityType.SFL_USER
            });
          } else {
            // For Level Up standalone, extract from metadata.coachId or metadata.basicInfo.coachId
            let entityId: string | null = null;
            let entityName: string = '';
            let profilePic: string = '';
            
            if (isStandaloneLevelUpVideo) {
              // Level Up standalone: coachId can be in metadata.coachId or metadata.basicInfo.coachId
              // Also check metadata.id as fallback (from transformation: metadata.coachId = basicInfo.coachId || metadata.id)
              entityId = metadata.coachId || metadata.basicInfo?.coachId || metadata.id || metadata.userId || null;
              entityName = metadata.userName || metadata.basicInfo?.fullName || '';
              profilePic = (metadata.userProfilePic && metadata.userProfilePic !== 'string') 
                ? metadata.userProfilePic 
                : (metadata.basicInfo?.profilePic && metadata.basicInfo.profilePic !== 'string')
                ? metadata.basicInfo.profilePic
                : '';
              
            } else {
              // Action/Social: use standard metadata fields
              entityId = metadata.id || metadata.entityId || metadata.playerId || metadata.coachId || metadata.academyId || null;
              entityName = metadata.userName || metadata.entityName || metadata.playerName || metadata.coachName || metadata.academyName || '';
              profilePic = (metadata.userProfilePic && metadata.userProfilePic !== 'string') 
              ? metadata.userProfilePic 
              : metadata.profilePic && metadata.profilePic !== 'string'
              ? metadata.profilePic
              : metadata.profilePicture && metadata.profilePicture !== 'string'
              ? metadata.profilePicture
                : '';
            }
            
            if (entityId && entityName && entityType !== null) {
              const sports = metadata.basicInfo?.sports || [];
              
              setSelectedEntity({
                value: entityId,
                label: entityName,
                entityId: entityId,
                entityName: entityName,
                profilePic: profilePic,
                entityType: entityType as EntityType,
                sports: sports,
                basicInfo: metadata.basicInfo || {}
              });
            }
          }
        }
      }
    } else if (!isEditMode && show) {
      setFormData({
        title: '',
        description: '',
        schedulePublish: false,
        publishDate: '',
        publishTime: ''
      });
      setSelectedTags([]);
      setSelectedEntityType(null);
      setSelectedEntity(null);
      setVideoFile(null);
      setThumbnailFile(null);
      setVideoPreview(null);
      setThumbnailPreview(null);
      setImageFile(null);
      setImagePreview(null);
      setPostType('video');
      // Reset levelUpContentType when form is reset (only for create mode)
      if (category === VideoCategory.LEVEL_UP && !isEditMode) {
        setLevelUpContentType(null);
        // Don't set tab yet - wait for user selection
      } else {
        setActiveTab('video-details');
      }
      setIsPortrait(false);
      setIsFeatured(false);
      
      if (category === VideoCategory.LEVEL_UP) {
        setSeriesData({
          title: '',
          description: '',
          subCategory: '',
          sport: '',
          difficultyLevel: DifficultyLevel.BEGINNER,
          thumbnailUrl: '',
                coverImage: '',
                featured: false
        });
        setSeriesThumbnailFile(null);
        setSeriesThumbnailPreview(null);
        setSeriesCoverFile(null);
        setSeriesCoverPreview(null);
        setCoachProfilePicFile(null);
        setCoachProfilePicPreview(null);
        setCreatedSeriesId(null);
        setChapters([]);
      }
    }
  }, [isEditMode, video, category, show]);

  const loadEntities = async (inputValue: string, entityType: EntityType): Promise<EntityOption[]> => {
    try {
      let entities: any[] = [];
      
      switch (entityType) {
        case EntityType.PLAYER:
          entities = await communityService.getAllPlayersForAdmin(inputValue);
          return entities.map((entity: any) => ({
            value: entity._id || entity.playerId || entity.id,
            label: entity.name || entity.playerName || entity.fullName || 'Unknown Player',
            entityId: entity._id || entity.playerId || entity.id,
            entityName: entity.name || entity.playerName || entity.fullName || 'Unknown Player',
            profilePic: entity.profilePic || entity.profilePicture || entity.profileImage || '',
            entityType: EntityType.PLAYER
          }));
        
        case EntityType.COACH:
          entities = await communityService.getAllCoachesForAdmin(inputValue);
          return entities.map((entity: any) => {
            // Extract sports array - ensure it's always an array
            const sportsArray = Array.isArray(entity.basicInfo?.sports) 
              ? entity.basicInfo.sports 
              : Array.isArray(entity.sports) 
              ? entity.sports 
              : [];
            
            const entityId = entity._id || entity.coachId || entity.id;
            const entityName = entity.name || entity.coachName || entity.fullName || entity.basicInfo?.fullName || 'Unknown Coach';
            const profilePic = entity.basicInfo?.profilePic || entity.profilePic || entity.profilePicture || entity.profileImage || '';
            
            // Ensure basicInfo includes all available fields
            const basicInfo = entity.basicInfo || {};
            const basicInfoWithAllFields = {
              ...basicInfo,
              ...(sportsArray.length > 0 ? { sports: sportsArray } : {}),
              ...(profilePic ? { profilePic: profilePic } : {}),
              ...(entityName ? { fullName: entityName } : {}),
              ...(entity.basicInfo?.email ? { email: entity.basicInfo.email } : {}),
              ...(entity.basicInfo?.dob ? { dob: entity.basicInfo.dob } : {}),
              ...(entity.basicInfo?.ageCategory ? { ageCategory: entity.basicInfo.ageCategory } : {}),
              ...(entity.basicInfo?.certification ? { certification: entity.basicInfo.certification } : {}),
              ...(entity.basicInfo?.experience ? { experience: entity.basicInfo.experience } : {}),
              ...(entity.basicInfo?.location ? { location: entity.basicInfo.location } : {})
            };
            
            return {
              value: entityId,
              label: entityName,
              entityId: entityId,
              entityName: entityName,
              profilePic: profilePic,
            entityType: EntityType.COACH,
              sports: sportsArray,
              basicInfo: basicInfoWithAllFields
            };
          });
        
        case EntityType.ACADEMY:
          entities = await communityService.getAllAcademiesForAdmin(inputValue);
          return entities.map((entity: any) => ({
            value: entity._id || entity.academyId || entity.id,
            label: entity.basicDetails?.academyName || entity.academyName || entity.name || entity.academyInfo?.basicDetails?.academyName || 'Unknown Academy',
            entityId: entity._id || entity.academyId || entity.id,
            entityName: entity.basicDetails?.academyName || entity.academyName || entity.name || entity.academyInfo?.basicDetails?.academyName || 'Unknown Academy',
            profilePic: entity.basicDetails?.logo || entity.logo || entity.profilePic || entity.profilePicture || entity.academyInfo?.basicDetails?.logo || '',
            entityType: EntityType.ACADEMY
          }));
        
        default:
          return [];
      }
    } catch (error) {
      console.error(`Error loading ${entityType}:`, error);
      return [];
    }
  };

  useEffect(() => {
    return () => {
      if (videoPreview) {
        URL.revokeObjectURL(videoPreview);
      }
      if (thumbnailPreview) {
        URL.revokeObjectURL(thumbnailPreview);
      }
    };
  }, [videoPreview, thumbnailPreview]);

  const loadTags = async () => {
    setLoadingTags(true);
    try {
      const tags = await communityService.getTags();
      const tagOptions: TagOption[] = tags.map(tag => ({
        value: tag,
        label: tag
      }));
      setExistingTags(tagOptions);
    } catch (error) {
      console.error('Error loading tags:', error);
    } finally {
      setLoadingTags(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    // Mark form as changed in edit mode
    if (isEditMode) {
      setHasFormChanges(true);
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleVideoFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const fileSizeMB = file.size / (1024 * 1024);
      if (fileSizeMB > 2048) {
        setUploadError('Video file must not exceed 2GB');
        return;
      }
      const fileName = file.name.toLowerCase();
      const isValidFormat = 
        file.type.includes('mp4') || 
        file.type.includes('quicktime') ||
        fileName.endsWith('.mp4') || 
        fileName.endsWith('.mov');
      
      if (!isValidFormat) {
        setUploadError('Only MP4 and MOV formats are supported');
        return;
      }
      
      setVideoFile(file);
      setUploadError(null);
      
      const videoUrl = URL.createObjectURL(file);
      setVideoPreview(videoUrl);
      
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.onloadedmetadata = () => {
        const isPortraitVideo = video.videoWidth < video.videoHeight;
        setIsPortrait(isPortraitVideo);
        video.src = '';
      };
      video.onerror = () => {
        console.warn('Failed to load video metadata, defaulting to landscape');
        setIsPortrait(false);
      };
      video.src = videoUrl;
      
      // Mark form as changed in edit mode
      if (isEditMode) {
        setHasFormChanges(true);
      }
    }
  };

  const getVideoContentType = (file: File): string => {
    const fileName = file.name.toLowerCase();
    if (file.type.includes('quicktime') || fileName.endsWith('.mov')) {
      return 'mov';
    }
    return 'mp4';
  };

  const handleThumbnailFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setUploadError('Thumbnail must be an image file');
        return;
      }
      
      setThumbnailFile(file);
      setUploadError(null);
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setThumbnailPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
      
      // Mark form as changed in edit mode
      if (isEditMode) {
        setHasFormChanges(true);
      }
    }
  };

  const handleImageFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setUploadError('Please select an image file');
        return;
      }
      const fileSizeMB = file.size / (1024 * 1024);
      if (fileSizeMB > 50) {
        setUploadError('Image file must not exceed 50MB');
        return;
      }
      setImageFile(file);
      setUploadError(null);
      
      // Mark form as changed in edit mode when image file is selected
      if (isEditMode) {
        setHasFormChanges(true);
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSeriesInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    // Mark form as changed in edit mode
    if (isEditMode) {
      setHasFormChanges(true);
    }
    
    setSeriesData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleEditChapter = (chapter: any) => {
    setSelectedChapterForEdit(chapter);
    setShowAddChapterForm(true);
    
    // Store original chapter data for change tracking
    // Store the full CDN URLs to match what will be in videoPreview/thumbnailPreview
    const originalVideoUrl = chapter.contentUrl 
      ? (chapter.contentUrl.startsWith('http') 
          ? chapter.contentUrl 
          : `https://cdn-dev.sportsforlife.in/${chapter.contentUrl}`)
      : null;
    const originalThumbnailUrl = chapter.thumbnailUrl 
      ? (chapter.thumbnailUrl.startsWith('http') 
          ? chapter.thumbnailUrl 
          : `https://cdn-dev.sportsforlife.in/${chapter.thumbnailUrl}`)
      : null;
    
    setOriginalChapterData({
      title: chapter.title || '',
      description: chapter.description || '',
      contentUrl: originalVideoUrl,
      thumbnailUrl: originalThumbnailUrl,
      isPortrait: chapter.isPortrait || false
    });
    
    // Reset change tracking
    setHasChapterChanges(false);
    
    if (chapter.title) {
      setFormData(prev => ({
        ...prev,
        title: chapter.title || '',
        description: chapter.description || ''
      }));
    }
    if (chapter.contentUrl) {
      setVideoPreview(originalVideoUrl);
    }
    if (chapter.thumbnailUrl) {
      setThumbnailPreview(originalThumbnailUrl);
    }
    if (chapter.isPortrait !== undefined) {
      setIsPortrait(chapter.isPortrait);
    }
  };
  
  // Close chapter menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.chapter-menu-wrapper') && !target.closest('.chapter-menu-button')) {
        setOpenChapterMenuId(null);
      }
    };

    if (openChapterMenuId) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [openChapterMenuId]);

  // Check if chapter has changes
  // This works for both:
  // 1. Editing from chapters tab (selectedChapterForEdit is set)
  // 2. Editing from sidebar/listing (isEditMode && video.videoType === 'chapter' && originalChapterData is set)
  useEffect(() => {
    // Check if we're editing a chapter (either from chapters tab or sidebar)
    const isEditingChapterFromTab = !!selectedChapterForEdit && !!originalChapterData;
    const isEditingChapterFromSidebar = isEditMode && category === VideoCategory.LEVEL_UP && 
      (video?.videoType === 'chapter' || (video?.seriesId && video?.videoType !== 'series')) && 
      !!originalChapterData;
    
    if (!isEditingChapterFromTab && !isEditingChapterFromSidebar) {
      setHasChapterChanges(false);
      return;
    }
    
    const titleChanged = formData.title.trim() !== originalChapterData.title;
    const descriptionChanged = formData.description.trim() !== originalChapterData.description;
    // Video changed if a new file is selected OR preview URL changed from original (normalize URLs for comparison)
    const normalizeUrl = (url: string | null) => {
      if (!url) return null;
      // Remove CDN prefix for comparison
      return url.replace('https://cdn-dev.sportsforlife.in/', '').replace('https://cdn.sportsforlife.in/', '');
    };
    const videoChanged = !!videoFile || (videoPreview && originalChapterData.contentUrl && normalizeUrl(videoPreview) !== normalizeUrl(originalChapterData.contentUrl));
    // Thumbnail changed if a new file is selected OR preview URL changed from original
    const thumbnailChanged = !!thumbnailFile || (thumbnailPreview && originalChapterData.thumbnailUrl && normalizeUrl(thumbnailPreview) !== normalizeUrl(originalChapterData.thumbnailUrl));
    const portraitChanged = isPortrait !== originalChapterData.isPortrait;
    
    setHasChapterChanges(titleChanged || descriptionChanged || videoChanged || thumbnailChanged || portraitChanged);
  }, [formData.title, formData.description, videoFile, videoPreview, thumbnailFile, thumbnailPreview, isPortrait, selectedChapterForEdit, originalChapterData, isEditMode, category, video?.videoType, video?.seriesId]);

  // Helper function to reload series data including chapters
  const reloadSeriesData = async (seriesId: string) => {
    try {
      const seriesResponse = await communityService.getSeriesById(seriesId, true); // Use cache bust
      let seriesDataFromApi = null;
      
      if (seriesResponse?.data && typeof seriesResponse.data === 'object') {
        seriesDataFromApi = seriesResponse.data;
      } else if (seriesResponse && typeof seriesResponse === 'object' && (seriesResponse._id || seriesResponse.title)) {
        seriesDataFromApi = seriesResponse;
      } else if (seriesResponse?.series && typeof seriesResponse.series === 'object') {
        seriesDataFromApi = seriesResponse.series;
      }
      
      if (seriesDataFromApi) {
        // Update chapters list - merge with existing chapters to preserve optimistically added ones
        if (seriesDataFromApi.chapters && Array.isArray(seriesDataFromApi.chapters)) {
          setChapters(prevChapters => {
            // Create a map of API chapters by _id for quick lookup
            const apiChaptersMap = new Map(seriesDataFromApi.chapters.map((ch: any) => [ch._id, ch]));
            
            // Start with API chapters
            const mergedChapters: any[] = [...seriesDataFromApi.chapters];
            
            // Add any optimistically added chapters that aren't in the API response yet
            prevChapters.forEach(chapter => {
              if (chapter._id) {
                // Check if this chapter is already in the API response
                const existsInApi = apiChaptersMap.has(chapter._id);
                if (!existsInApi) {
                  // This is an optimistically added chapter that hasn't appeared in API yet
                  mergedChapters.push(chapter);
                }
              }
            });
            
            // Sort by chapterNumber if available
            return mergedChapters.sort((a: any, b: any) => {
              const numA = a.chapterNumber || 0;
              const numB = b.chapterNumber || 0;
              return numA - numB;
            });
          });
        } else {
          // If API doesn't have chapters, keep existing chapters (don't clear optimistically added ones)
          // Only clear if we're sure there should be no chapters (e.g., series was just created)
          setChapters(prevChapters => prevChapters.length > 0 ? prevChapters : []);
        }
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error reloading series data:', error);
      return false;
    }
  };

  // Handle unpublish chapter
  const handleUnpublishChapter = async (chapter: any) => {
    if (!chapter._id) {
      toast.error('Chapter ID is missing');
      return;
    }

    setUnpublishingChapterId(chapter._id);
    setOpenChapterMenuId(null); // Close menu

    try {
      await communityService.rejectVideo(chapter._id);
      toast.success('Chapter unpublished successfully');
      
      // Update chapter status in local state
      setChapters(prev => prev.map(ch => 
        ch._id === chapter._id 
          ? { ...ch, status: 'rejected' }
          : ch
      ));

      // Reload series data to get updated chapters
      if (createdSeriesId) {
        await reloadSeriesData(createdSeriesId);
      }
    } catch (error: any) {
      console.error('Error unpublishing chapter:', error);
      toast.error(error?.message || 'Failed to unpublish chapter');
    } finally {
      setUnpublishingChapterId(null);
    }
  };

  const handleUpdateSeries = async () => {
    // Use createdSeriesId if available, otherwise fallback to video._id for series
    const seriesIdToUpdate = createdSeriesId || (video?.videoType === 'series' ? video._id : null);
    
    if (!seriesIdToUpdate) {
      setUploadError('Series ID is missing');
      return;
    }

    // Validation
    if (!seriesData.title.trim()) {
      setUploadError('Title is required');
      return;
    }

    if (!selectedEntity || selectedEntityType !== EntityType.COACH) {
      setUploadError('Please select a coach');
      return;
    }

    if (!seriesData.subCategory.trim()) {
      setUploadError('Sub Category is required');
      return;
    }

    // Validate sports for Coach
    if (!selectedEntity.sports || selectedEntity.sports.length === 0) {
      setUploadError('Coach must have at least one sport');
      return;
    }

    setUploading(true);
    setUploadError(null);

    try {
      const updatePayload: any = {
        title: seriesData.title.trim(),
        description: seriesData.description.trim(),
        subCategory: seriesData.subCategory.trim(),
        difficultyLevel: seriesData.difficultyLevel,
        tags: selectedTags.map(tag => tag.value),
        coachId: selectedEntity.entityId,
        coachName: selectedEntity.entityName,
        featured: isFeatured
      };
      
      // Add coach-specific fields (series only supports Coach)
      if (selectedEntity.sports && selectedEntity.sports.length > 0) {
        updatePayload.coachSports = selectedEntity.sports;
        updatePayload.sport = String(selectedEntity.sports[0]);
      }
      
      // Add metadata for Coach
      updatePayload.metadata = {
        id: selectedEntity.entityId,
        type: selectedEntityType,
        userName: selectedEntity.entityName,
        userProfilePic: selectedEntity.profilePic || undefined,
        basicInfo: selectedEntity.basicInfo || {}
      };

      // Handle cover image update if changed
      if (seriesCoverFile) {
        const coverExtension = seriesCoverFile.name.split('.').pop()?.toLowerCase() || 'jpg';
        const coverContentType = `image/${coverExtension === 'png' ? 'png' : 'jpeg'}`;
        const { imageUploadUrl: coverUploadUrl, imageKey: coverS3Key } = 
          await communityService.generateSeriesImageUploadUrl(coverContentType);
        
        // Upload cover to S3
        await fetch(coverUploadUrl, {
          method: 'PUT',
          body: seriesCoverFile,
          headers: {
            'Content-Type': coverContentType
          }
        });
        
        updatePayload.coverImage = coverS3Key;
      }

      // Handle coach profile picture (series only supports Coach)
      if (coachProfilePicFile && selectedEntity && (!selectedEntity.profilePic || selectedEntity.profilePic === null || selectedEntity.profilePic === 'null' || selectedEntity.profilePic === '')) {
        // Generate unique ID and create path in format: shared/coachProfilePictures/{uniqueId}/xyzimage.png
        const uniqueId = generateUniqueId();
        const fileExtension = coachProfilePicFile.name.split('.').pop()?.toLowerCase() || 'png';
        const fileName = `profile.${fileExtension}`;
        const coachPicPath = `shared/coachProfilePictures/${uniqueId}/${fileName}`;
        
        // Upload to S3 using presigned URL
        const coachPicS3Key = await uploadImageWithPresignedUrl(coachProfilePicFile, coachPicPath);
        
        // Update coach profile picture via API
        try {
          await communityService.updateCoachDetails(selectedEntity.entityId, {
            basicInfo: {
              profilePic: coachPicS3Key
            }
          });
          // Update selectedEntity to reflect the new profile picture
          setSelectedEntity(prev => prev ? {
            ...prev,
            profilePic: coachPicS3Key,
            basicInfo: prev?.basicInfo ? {
              ...prev.basicInfo,
            profilePic: coachPicS3Key
            } : { profilePic: coachPicS3Key }
          } : null);
        } catch (error: any) {
          console.error('Error updating coach profile picture:', error);
          toast.warning('Coach profile picture uploaded but failed to update coach record. Series will still be updated.');
        }
        
        updatePayload.coachProfilePicture = coachPicS3Key;
      }


      await communityService.updateSeries(seriesIdToUpdate, updatePayload);
      
      toast.success('Series updated successfully!');
      
      // Reset form changes flag after successful update
      setHasFormChanges(false);
      
      // Reload series data to get updated chapters
      await reloadSeriesData(seriesIdToUpdate);
      
      onSuccess?.();
    } catch (error: any) {
      console.error('Error updating series:', error);
      const errorMsg = error.response?.data?.error || error.response?.data?.message || error.message || 'Failed to update series';
      setUploadError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setUploading(false);
    }
  };

  const handleSeriesThumbnailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setUploadError('Please select an image file');
        return;
      }
      const fileSizeMB = file.size / (1024 * 1024);
      if (fileSizeMB > 50) {
        setUploadError('Image file must not exceed 50MB');
        return;
      }
      setSeriesThumbnailFile(file);
      setUploadError(null);
      
      // Mark form as changed in edit mode
      if (isEditMode) {
        setHasFormChanges(true);
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setSeriesThumbnailPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSeriesCoverChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setUploadError('Please select an image file');
        return;
      }
      const fileSizeMB = file.size / (1024 * 1024);
      if (fileSizeMB > 50) {
        setUploadError('Image file must not exceed 50MB');
        return;
      }
      setSeriesCoverFile(file);
      setUploadError(null);
      
      // Mark form as changed in edit mode
      if (isEditMode) {
        setHasFormChanges(true);
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setSeriesCoverPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCoachProfilePicChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setUploadError('Please select an image file');
        return;
      }
      const fileSizeMB = file.size / (1024 * 1024);
      if (fileSizeMB > 50) {
        setUploadError('Image file must not exceed 50MB');
        return;
      }
      setCoachProfilePicFile(file);
      setUploadError(null);
      
      // Mark form as changed in edit mode
      if (isEditMode) {
        setHasFormChanges(true);
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setCoachProfilePicPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const sanitizeSeriesName = (title: string): string => {
    return title
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
      .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens
  };

  // Generate unique ID for coach profile picture
  const generateUniqueId = (): string => {
    return `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
  };

  // Helper function to upload image using presigned URL with custom path
  const uploadImageWithPresignedUrl = async (
    file: File,
    s3Path: string
  ): Promise<string> => {
    const pathParts = s3Path.split('/');
    const fileName = pathParts.pop() || 'image.jpg';
    let context = pathParts.join('/');
    
    // Remove "shared/" prefix from context if it exists
    if (context.startsWith('shared/')) {
      context = context.substring(7); // Remove "shared/" (7 characters)
    }
    
    const response = await secureApi.post(
      `/api/v1/general/presigned-url`,
      {
        fileName: fileName,
        contentType: file.type,
        context: context
      }
    );

    const presignedUrl = response.data?.presignedUrl;
    const s3Key = response.data?.key || s3Path;

    if (!presignedUrl) {
      throw new Error('Failed to get presigned URL');
    }

    const uploadResponse = await fetch(presignedUrl, {
      method: 'PUT',
      headers: { 'Content-Type': file.type },
      body: file
    });

    if (!uploadResponse.ok) {
      throw new Error('Failed to upload image to S3');
    }

    return s3Key;
  };

  const handleCreateSeries = async () => {
    if (!selectedEntity || selectedEntityType !== EntityType.COACH) {
      setUploadError('Please select a coach');
      return;
    }

    if (!seriesData.title.trim()) {
      setUploadError('Series title is required');
      return;
    }

    if (!seriesData.subCategory.trim()) {
      setUploadError('Sub Category is required');
      return;
    }

    if (!seriesCoverFile) {
      setUploadError('Cover image is required');
      return;
    }

    // Only validate sports for Coach, not for SFL User
    let coachSports: string[] = [];
    let sport = '';
    
    if (selectedEntityType === EntityType.COACH) {
      coachSports = selectedEntity.sports && selectedEntity.sports.length > 0 
      ? selectedEntity.sports 
      : [];
    
    if (coachSports.length === 0) {
      setUploadError('Selected coach must have at least one sport');
      return;
    }

      sport = coachSports[0];
    }

    setIsCreatingSeries(true);
    setUploadError(null);

    try {
      const seriesName = sanitizeSeriesName(seriesData.title.trim());
      if (!seriesName) {
        setUploadError('Series title must contain at least one alphanumeric character');
        setIsCreatingSeries(false);
        return;
      }

      const coverPath = `app/community/series/${seriesName}/cover.jpg`;
      const coverS3Key = await uploadImageWithPresignedUrl(seriesCoverFile, coverPath);

      let coachProfilePictureS3Key = '';
      
      // Handle coach profile picture (series only supports Coach)
      if (!selectedEntity.profilePic || selectedEntity.profilePic === null || selectedEntity.profilePic === 'null' || selectedEntity.profilePic === '') {
        if (coachProfilePicFile) {
          // Generate unique ID and create path in format: shared/coachProfilePictures/{uniqueId}/xyzimage.png
          const uniqueId = generateUniqueId();
          const fileExtension = coachProfilePicFile.name.split('.').pop()?.toLowerCase() || 'png';
          const fileName = `profile.${fileExtension}`;
          const coachPicPath = `shared/coachProfilePictures/${uniqueId}/${fileName}`;
          
          // Upload to S3
          coachProfilePictureS3Key = await uploadImageWithPresignedUrl(coachProfilePicFile, coachPicPath);
          
          // Update coach profile picture via API
          try {
            await communityService.updateCoachDetails(selectedEntity.entityId, {
              basicInfo: {
                profilePic: coachProfilePictureS3Key
              }
            });
            // Update selectedEntity to reflect the new profile picture
            setSelectedEntity(prev => prev ? {
              ...prev,
              profilePic: coachProfilePictureS3Key,
              basicInfo: prev?.basicInfo ? {
                ...prev.basicInfo,
              profilePic: coachProfilePictureS3Key
              } : { profilePic: coachProfilePictureS3Key }
            } : null);
          } catch (error: any) {
            console.error('Error updating coach profile picture:', error);
            toast.warning('Coach profile picture uploaded but failed to update coach record. Series will still be created.');
          }
        } else {
          coachProfilePictureS3Key = '';
        }
      } else {
        coachProfilePictureS3Key = String(selectedEntity.profilePic);
      }
      
      // Build payload based on entity type
      const seriesPayload: any = {
        title: seriesData.title.trim(),
        description: seriesData.description.trim(),
        category: 'coach_corner',
        coachId: selectedEntity.entityId,
        coachName: selectedEntity.entityName,
        coachProfilePicture: coachProfilePictureS3Key,
        subCategory: seriesData.subCategory.trim(),
        tags: selectedTags.map(tag => tag.value),
        difficultyLevel: seriesData.difficultyLevel,
        coverImage: coverS3Key,
        featured: isFeatured
      };
      
      // Add coach-specific fields (series only supports Coach)
      seriesPayload.coachSports = coachSports;
      seriesPayload.sport = String(sport);
      
      // Add metadata for Coach
      // Ensure basicInfo.profilePic is set correctly (use uploaded profile pic if available)
      const basicInfoWithProfilePic = selectedEntity.basicInfo ? {
        ...selectedEntity.basicInfo,
        profilePic: coachProfilePictureS3Key || selectedEntity.basicInfo.profilePic || selectedEntity.profilePic || null
      } : {
        profilePic: coachProfilePictureS3Key || selectedEntity.profilePic || null
      };
      
      seriesPayload.metadata = {
        id: selectedEntity.entityId,
        type: selectedEntityType,
        userName: selectedEntity.entityName,
        userProfilePic: coachProfilePictureS3Key || selectedEntity.profilePic || undefined,
        basicInfo: basicInfoWithProfilePic
      };

      const response = await communityService.createSeries(seriesPayload);
      const seriesId = response?.series?._id || response?._id || response?.data?.series?._id || response?.data?._id;
      
      if (!seriesId) {
        throw new Error('Failed to create series - no series ID returned');
      }

      setCreatedSeriesId(seriesId);
      toast.success('Series created successfully!');
      
      // Trigger success callback to refresh the listing (form stays open for adding chapters)
      // Use setTimeout to ensure state updates are complete before triggering refresh
      setTimeout(() => {
        onSuccess?.();
      }, 100);
      
      setActiveTab('chapters');
    } catch (error: any) {
      console.error('Error creating series:', error);
      const errorMsg = error.response?.data?.error || error.response?.data?.message || error.message || 'Failed to create series';
      setUploadError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setIsCreatingSeries(false);
    }
  };

  const addNewChapter = () => {
    const newChapter: Chapter = {
      id: Date.now().toString(),
      title: '',
      description: '',
      videoFile: null,
      videoPreview: null,
      thumbnailFile: null,
      thumbnailPreview: null,
      uploading: false
    };
    setChapters(prev => [...prev, newChapter]);
  };

  const removeChapter = (chapterId: string) => {
    setChapters(prev => prev.filter(ch => ch.id !== chapterId));
  };

  const updateChapter = (chapterId: string, field: keyof Chapter, value: any) => {
    setChapters(prev => prev.map(ch => 
      ch.id === chapterId ? { ...ch, [field]: value } : ch
    ));
  };

  const handleChapterVideoChange = (chapterId: string, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const fileSizeMB = file.size / (1024 * 1024);
      if (fileSizeMB > 2048) {
        setUploadError('Video file must not exceed 2GB');
        return;
      }
      const fileName = file.name.toLowerCase();
      const isValidFormat = 
        file.type.includes('mp4') || 
        file.type.includes('quicktime') ||
        fileName.endsWith('.mp4') || 
        fileName.endsWith('.mov');
      
      if (!isValidFormat) {
        setUploadError('Only MP4 and MOV formats are supported');
        return;
      }
      
      const videoUrl = URL.createObjectURL(file);
      updateChapter(chapterId, 'videoFile', file);
      updateChapter(chapterId, 'videoPreview', videoUrl);
      setUploadError(null);
    }
  };

  const handleChapterThumbnailChange = (chapterId: string, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setUploadError('Please select an image file');
        return;
      }
      const fileSizeMB = file.size / (1024 * 1024);
      if (fileSizeMB > 50) {
        setUploadError('Image file must not exceed 50MB');
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        updateChapter(chapterId, 'thumbnailFile', file);
        updateChapter(chapterId, 'thumbnailPreview', reader.result as string);
      };
      reader.readAsDataURL(file);
      setUploadError(null);
    }
  };

  // Upload a single chapter to the series
  const handleSingleChapterUpload = async (chapterId: string) => {
    const seriesId = createdSeriesId || video?._id;
    if (!seriesId) {
      setUploadError('Series ID is required');
      return;
    }

    const chapter = chapters.find(ch => ch.id === chapterId);
    if (!chapter) {
      setUploadError('Chapter not found');
      return;
    }

    // Validate required fields
    if (!chapter.title.trim()) {
      setUploadError('Chapter title is required');
      return;
    }
    if (!chapter.videoFile) {
      setUploadError('Chapter video is required');
      return;
    }
    if (!chapter.thumbnailFile) {
      setUploadError('Chapter thumbnail is required');
      return;
    }

    updateChapter(chapterId, 'uploading', true);
    setUploadError(null);

    try {
      const videoContentType = chapter.videoFile ? getVideoContentType(chapter.videoFile) : 'mp4';
      const thumbnailContentType = chapter.thumbnailFile ? (chapter.thumbnailFile.type.split('/')[1] || 'png') : 'png';

      const uploadUrls = await communityService.generateUploadUrls(
        videoContentType,
        thumbnailContentType
      );
      
      if (chapter.videoFile && uploadUrls.videoUploadUrl) {
        const videoMimeType = chapter.videoFile.type || (videoContentType === 'mov' ? 'video/quicktime' : 'video/mp4');
        await communityService.uploadToS3(
          uploadUrls.videoUploadUrl,
          chapter.videoFile,
          videoMimeType
        );
      }

      if (chapter.thumbnailFile && uploadUrls.thumbnailUploadUrl) {
        await communityService.uploadToS3(
          uploadUrls.thumbnailUploadUrl,
          chapter.thumbnailFile,
          chapter.thumbnailFile.type
        );
      }

      // Get current chapter count from series to determine chapter number
      // For individual chapter upload, we need to fetch the current chapters count from the series
      // This ensures accurate chapter numbering even when adding multiple chapters
      let chapterNumber = 1;
      try {
        if (seriesId) {
          const seriesResponse = await communityService.getSeriesById(seriesId);
          let seriesData = null;
          if (seriesResponse?.data?.series) {
            seriesData = seriesResponse.data.series;
          } else if (seriesResponse?.data) {
            seriesData = seriesResponse.data;
          } else if (seriesResponse?.series) {
            seriesData = seriesResponse.series;
          } else if (seriesResponse && typeof seriesResponse === 'object' && seriesResponse._id) {
            seriesData = seriesResponse;
          }
          
          if (seriesData?.chapters && Array.isArray(seriesData.chapters)) {
            // Use the actual count from the series + 1 for the new chapter
            chapterNumber = seriesData.chapters.length + 1;
          } else {
            // Fallback: count uploaded chapters in local state
            const uploadedChapters = chapters.filter(ch => ch._id);
            chapterNumber = uploadedChapters.length + 1;
          }
        } else {
          // Fallback: count uploaded chapters in local state
          const uploadedChapters = chapters.filter(ch => ch._id);
          chapterNumber = uploadedChapters.length + 1;
        }
      } catch (error) {
        console.warn('Could not fetch series data for chapter number, using local count:', error);
        // Fallback: count uploaded chapters in local state
        const uploadedChapters = chapters.filter(ch => ch._id);
        chapterNumber = uploadedChapters.length + 1;
      }

      const tags = selectedTags.map(tag => tag.value);
      const payload: any = {
        videoId: uploadUrls.videoId,
        title: chapter.title.trim(),
        description: chapter.description.trim(),
        category: 'level_up',
        contentType: 'long_form',
        mediaType: 'video',
        videoType: 'chapter',
        tags: tags,
        uploadedBy: user?._id || user?.id || '',
        uploadedByRole: user?.role || 'admin',
        isPortrait: false,
        seriesId: seriesId,
        chapterNumber: chapterNumber
      };

      if (selectedEntity && selectedEntityType) {
        const baseMetadata = {
          id: selectedEntityType === EntityType.SFL_USER ? 'sfl' : selectedEntity.entityId,
          type: selectedEntityType,
          userProfilePic: selectedEntityType === EntityType.SFL_USER ? 'shared/sfl-logo.png' : (selectedEntity.profilePic || undefined)
        };

        if (selectedEntityType === EntityType.COACH) {
          // Start with basicInfo from selectedEntity (this should contain all coach data)
          // CRITICAL: If basicInfo is empty {}, we need to use selectedEntity properties directly
          let basicInfo = selectedEntity.basicInfo;
          
          // If basicInfo is null, undefined, or empty object, construct it from selectedEntity
          if (!basicInfo || (typeof basicInfo === 'object' && Object.keys(basicInfo).length === 0)) {
            basicInfo = {
              fullName: selectedEntity.entityName || '',
              profilePic: selectedEntity.profilePic || null,
              sports: Array.isArray(selectedEntity.sports) ? selectedEntity.sports : [],
              dob: null,
              email: null,
              ageCategory: [],
              certification: [],
              experience: null,
              _id: selectedEntity.entityId || selectedEntity.value || ''
            };
          }
          
          // Check if basicInfo is empty or only has minimal data (only sports field)
          // If basicInfo was constructed from selectedEntity above, it should already have all fields
          // But we still check in case it's still minimal
          const hasMinimalBasicInfo = !basicInfo || Object.keys(basicInfo).length === 0 || 
            (Object.keys(basicInfo).length === 1 && basicInfo.sports) ||
            (Object.keys(basicInfo).length <= 2 && basicInfo.sports && (basicInfo.fullName || basicInfo._id));
          
          // If basicInfo is still empty or minimal, try to fetch coach data on-the-fly
          // But first, ensure we have at least the basic fields from selectedEntity
          if (hasMinimalBasicInfo) {
            // If we don't have fullName or _id, construct from selectedEntity
            if (!basicInfo.fullName && selectedEntity.entityName) {
              basicInfo.fullName = selectedEntity.entityName;
            }
            if (!basicInfo._id && selectedEntity.entityId) {
              basicInfo._id = selectedEntity.entityId;
            }
            if (!basicInfo.profilePic && selectedEntity.profilePic) {
              basicInfo.profilePic = selectedEntity.profilePic;
            }
            if (!Array.isArray(basicInfo.sports) || basicInfo.sports.length === 0) {
              if (Array.isArray(selectedEntity.sports) && selectedEntity.sports.length > 0) {
                basicInfo.sports = selectedEntity.sports;
              }
            }
          }
          
          // If basicInfo is still empty or minimal after using selectedEntity, try to fetch coach data on-the-fly
          if (hasMinimalBasicInfo && selectedEntity.entityId) {
            try {
              const coaches = await communityService.getAllCoachesForAdmin(selectedEntity.entityName || '');
              const matchingCoach = coaches.find((coach: any) => 
                (coach._id || coach.coachId || coach.id) === selectedEntity.entityId
              );
              
              if (matchingCoach) {
                if (matchingCoach.basicInfo) {
                  basicInfo = matchingCoach.basicInfo;
                } else {
                  // Construct basicInfo from coach object
                  const sportsArray = Array.isArray(matchingCoach.basicInfo?.sports) 
                    ? matchingCoach.basicInfo.sports 
                    : Array.isArray(matchingCoach.sports) 
                    ? matchingCoach.sports 
                    : [];
                  
                  basicInfo = {
                    ...(matchingCoach.fullName ? { fullName: matchingCoach.fullName } : {}),
                    ...(matchingCoach.email ? { email: matchingCoach.email } : {}),
                    ...(matchingCoach.dob ? { dob: matchingCoach.dob } : {}),
                    ...(matchingCoach.ageCategory ? { ageCategory: matchingCoach.ageCategory } : {}),
                    ...(matchingCoach.certification ? { certification: matchingCoach.certification } : {}),
                    ...(matchingCoach.experience ? { experience: matchingCoach.experience } : {}),
                    ...(matchingCoach.location ? { location: matchingCoach.location } : {}),
                    ...(sportsArray.length > 0 ? { sports: sportsArray } : {}),
                    _id: matchingCoach._id || matchingCoach.coachId || matchingCoach.id || selectedEntity.entityId
                  };
                }
              }
            } catch (error) {
              console.error('Error fetching coach details on-the-fly:', error);
              // Continue with existing basicInfo if fetch fails
            }
          }
          
          // Get values, prioritizing basicInfo but falling back to selectedEntity properties
          // CRITICAL: Even if basicInfo is {}, we should still have values from selectedEntity
          const sports = (basicInfo && Array.isArray(basicInfo.sports) && basicInfo.sports.length > 0)
            ? basicInfo.sports 
            : (Array.isArray(selectedEntity.sports) && selectedEntity.sports.length > 0 
              ? selectedEntity.sports 
              : []);
          
          const profilePic = (basicInfo && basicInfo.profilePic) || selectedEntity.profilePic || null;
          const fullName = (basicInfo && basicInfo.fullName) || selectedEntity.entityName || '';
          const coachId = (basicInfo && basicInfo._id) || selectedEntity.entityId || selectedEntity.value || '';
          
          // Construct complete basicInfo ensuring all expected fields are present
          // CRITICAL: Always explicitly set ALL fields, never rely on spreading empty objects
          // This ensures basicInfo is never empty, even if basicInfo from selectedEntity is {}
          const basicInfoComplete: any = {
            // Always set these required fields explicitly - use selectedEntity as fallback
            fullName: fullName || selectedEntity.entityName || '',
            profilePic: profilePic || selectedEntity.profilePic || null,
            sports: sports.length > 0 ? sports : (Array.isArray(selectedEntity.sports) ? selectedEntity.sports : []),
            // Always set these fields explicitly (use null/empty if not available)
            dob: (basicInfo && basicInfo.dob !== undefined) ? basicInfo.dob : null,
            email: (basicInfo && basicInfo.email !== undefined) ? basicInfo.email : null,
            ageCategory: (basicInfo && Array.isArray(basicInfo.ageCategory)) ? basicInfo.ageCategory : [],
            certification: (basicInfo && Array.isArray(basicInfo.certification)) ? basicInfo.certification : [],
            experience: (basicInfo && basicInfo.experience !== undefined) ? basicInfo.experience : null,
            _id: coachId
          };
          
          // Only add location if it exists (optional field)
          if (basicInfo && basicInfo.location !== undefined) {
            basicInfoComplete.location = basicInfo.location;
          }
          
          // Preserve any other fields from basicInfo that we haven't explicitly set
          if (basicInfo && typeof basicInfo === 'object' && !Array.isArray(basicInfo)) {
            Object.keys(basicInfo).forEach(key => {
              if (!basicInfoComplete.hasOwnProperty(key) && basicInfo[key] !== undefined) {
                basicInfoComplete[key] = basicInfo[key];
              }
            });
          }
          
          // Final validation: Ensure basicInfoComplete is never empty and has all required fields
          const requiredFields = ['fullName', 'profilePic', 'sports', 'dob', 'email', 'ageCategory', 'certification', 'experience', '_id'];
          const missingFields = requiredFields.filter(field => !basicInfoComplete.hasOwnProperty(field));
          
          if (missingFields.length > 0 || Object.keys(basicInfoComplete).length === 0) {
            console.error('ERROR: basicInfoComplete is missing fields or empty! Missing:', missingFields);
            // Set all missing fields with defaults
            missingFields.forEach(field => {
              if (field === 'fullName') basicInfoComplete.fullName = selectedEntity.entityName || '';
              else if (field === 'profilePic') basicInfoComplete.profilePic = selectedEntity.profilePic || null;
              else if (field === 'sports') basicInfoComplete.sports = Array.isArray(selectedEntity.sports) ? selectedEntity.sports : [];
              else if (field === 'dob') basicInfoComplete.dob = null;
              else if (field === 'email') basicInfoComplete.email = null;
              else if (field === 'ageCategory') basicInfoComplete.ageCategory = [];
              else if (field === 'certification') basicInfoComplete.certification = [];
              else if (field === 'experience') basicInfoComplete.experience = null;
              else if (field === '_id') basicInfoComplete._id = selectedEntity.entityId || selectedEntity.value || '';
            });
          }
          
          // Verify the object is not empty before using it
          if (Object.keys(basicInfoComplete).length === 0) {
            console.error('CRITICAL ERROR: basicInfoComplete is still empty after all attempts!');
            // Last resort: create minimal valid object
            basicInfoComplete.fullName = selectedEntity.entityName || 'Unknown';
            basicInfoComplete.profilePic = selectedEntity.profilePic || null;
            basicInfoComplete.sports = Array.isArray(selectedEntity.sports) ? selectedEntity.sports : [];
            basicInfoComplete.dob = null;
            basicInfoComplete.email = null;
            basicInfoComplete.ageCategory = [];
            basicInfoComplete.certification = [];
            basicInfoComplete.experience = null;
            basicInfoComplete._id = selectedEntity.entityId || selectedEntity.value || '';
          }
          
          payload.metadata = {
            ...baseMetadata,
            basicInfo: basicInfoComplete
          };
          
          // Final check: If basicInfo is still empty in payload, log error and use fallback
          if (!payload.metadata.basicInfo || Object.keys(payload.metadata.basicInfo).length === 0) {
            console.error('CRITICAL: payload.metadata.basicInfo is empty! Using emergency fallback');
            payload.metadata.basicInfo = {
              fullName: selectedEntity.entityName || 'Unknown',
              profilePic: selectedEntity.profilePic || null,
              sports: Array.isArray(selectedEntity.sports) ? selectedEntity.sports : [],
              dob: null,
              email: null,
              ageCategory: [],
              certification: [],
              experience: null,
              _id: selectedEntity.entityId || selectedEntity.value || ''
            };
          }
        } else {
          payload.metadata = baseMetadata;
        }
      } else {
        console.warn('Chapter upload - selectedEntity or selectedEntityType is missing!', {
          selectedEntity,
          selectedEntityType,
          seriesId
        });
        
        // Fallback: If selectedEntity is missing, fetch from series data
        if (seriesId) {
          try {
            console.log('Attempting to fetch series data to get coach info for chapter upload');
            const seriesResponse = await communityService.getSeriesById(seriesId);
            let seriesDataFromApi = null;
            
            if (seriesResponse?.data && typeof seriesResponse.data === 'object') {
              seriesDataFromApi = seriesResponse.data;
            } else if (seriesResponse && typeof seriesResponse === 'object' && (seriesResponse._id || seriesResponse.title)) {
              seriesDataFromApi = seriesResponse;
            } else if (seriesResponse?.series && typeof seriesResponse.series === 'object') {
              seriesDataFromApi = seriesResponse.series;
            }
            
            if (seriesDataFromApi?.coachId && seriesDataFromApi?.coachName) {
              console.log('Found coach from series data:', seriesDataFromApi.coachId, seriesDataFromApi.coachName);
              
              // Fetch coach details
              const coaches = await communityService.getAllCoachesForAdmin(seriesDataFromApi.coachName);
              const matchingCoach = coaches.find((coach: any) => 
                (coach._id || coach.coachId || coach.id) === seriesDataFromApi.coachId
              );
              
              if (matchingCoach) {
                const sportsArray = Array.isArray(matchingCoach.basicInfo?.sports) 
                  ? matchingCoach.basicInfo.sports 
                  : Array.isArray(matchingCoach.sports) 
                  ? matchingCoach.sports 
                  : Array.isArray(seriesDataFromApi.coachSports) 
                  ? seriesDataFromApi.coachSports 
                  : [];
                
                const coachBasicInfoFromEntity = matchingCoach.basicInfo || {};
                const basicInfoComplete = {
                  ...coachBasicInfoFromEntity,
                  sports: sportsArray.length > 0 ? sportsArray : [],
                  profilePic: seriesDataFromApi.coachProfilePicture || matchingCoach.basicInfo?.profilePic || matchingCoach.profilePic || null,
                  fullName: seriesDataFromApi.coachName || matchingCoach.fullName || matchingCoach.basicInfo?.fullName || '',
                  dob: coachBasicInfoFromEntity.dob !== undefined ? coachBasicInfoFromEntity.dob : (matchingCoach.dob !== undefined ? matchingCoach.dob : null),
                  email: coachBasicInfoFromEntity.email !== undefined ? coachBasicInfoFromEntity.email : (matchingCoach.email !== undefined ? matchingCoach.email : null),
                  ageCategory: Array.isArray(coachBasicInfoFromEntity.ageCategory) ? coachBasicInfoFromEntity.ageCategory : (Array.isArray(matchingCoach.ageCategory) ? matchingCoach.ageCategory : []),
                  certification: Array.isArray(coachBasicInfoFromEntity.certification) ? coachBasicInfoFromEntity.certification : (Array.isArray(matchingCoach.certification) ? matchingCoach.certification : []),
                  experience: coachBasicInfoFromEntity.experience !== undefined ? coachBasicInfoFromEntity.experience : (matchingCoach.experience !== undefined ? matchingCoach.experience : null),
                  ...(coachBasicInfoFromEntity.location !== undefined ? { location: coachBasicInfoFromEntity.location } : (matchingCoach.location !== undefined ? { location: matchingCoach.location } : {})),
                  _id: coachBasicInfoFromEntity._id || matchingCoach._id || matchingCoach.coachId || matchingCoach.id || seriesDataFromApi.coachId || ''
                };
                
                
                payload.metadata = {
                  id: seriesDataFromApi.coachId,
                  type: 'coach',
                  userName: seriesDataFromApi.coachName,
                  userProfilePic: seriesDataFromApi.coachProfilePicture || undefined,
                  basicInfo: basicInfoComplete
                };
              } else {
                console.warn('Coach not found, using minimal metadata');
                payload.metadata = {
                  id: seriesDataFromApi.coachId,
                  type: 'coach',
                  userName: seriesDataFromApi.coachName,
                  userProfilePic: seriesDataFromApi.coachProfilePicture || undefined,
                  basicInfo: {
                    fullName: seriesDataFromApi.coachName || '',
                    profilePic: seriesDataFromApi.coachProfilePicture || null,
                    sports: Array.isArray(seriesDataFromApi.coachSports) ? seriesDataFromApi.coachSports : [],
                    dob: null,
                    email: null,
                    ageCategory: [],
                    certification: [],
                    experience: null,
                    _id: seriesDataFromApi.coachId || ''
                  }
                };
              }
            } else {
              console.warn('Series data does not contain coach information');
              payload.metadata = {
                id: 'unknown',
                type: 'coach',
                basicInfo: {}
              };
            }
          } catch (error) {
            console.error('Error fetching series/coach data for chapter upload:', error);
            payload.metadata = {
              id: 'unknown',
              type: 'coach',
              basicInfo: {}
            };
          }
        } else {
          console.warn('No seriesId available, cannot fetch coach data');
          payload.metadata = {
            id: 'unknown',
            type: 'coach',
            basicInfo: {}
          };
        }
      }

      if (uploadUrls.videoKey) {
        payload.videoKey = uploadUrls.videoKey;
      }
      if (uploadUrls.thumbnailKey) {
        payload.thumbnailKey = uploadUrls.thumbnailKey;
      }

      const response = await communityService.completeUpload(payload);
      
      setChapters(prev => prev.map(ch => 
        ch.id === chapterId 
          ? { ...ch, uploaded: true, uploading: false, _id: response?.data?._id || response?._id }
          : ch
      ));
      
      setHasUploadedChapters(true);
      toast.success('Chapter added successfully!');
      
      if (initialTab !== 'chapters') {
        onSuccess?.();
      }
    } catch (error: any) {
      console.error('Error uploading chapter:', error);
      const errorMsg = error.message || 'Failed to upload chapter';
      setUploadError(errorMsg);
      toast.error(errorMsg);
    } finally {
      updateChapter(chapterId, 'uploading', false);
    }
  };

  const handleBulkChaptersUpload = async () => {
    const seriesId = createdSeriesId || video?._id;
    if (!seriesId || chapters.length === 0) {
      setUploadError('Please add at least one chapter');
      return;
    }

    // Filter out already uploaded chapters for bulk upload
    const chaptersToUpload = chapters.filter(ch => !ch.uploaded);
    
    if (chaptersToUpload.length === 0) {
      toast.info('All chapters have already been uploaded.');
      return;
    }

    const invalidChapters = chaptersToUpload.filter(ch => 
      !ch.title.trim() || !ch.videoFile || !ch.thumbnailFile
    );

    if (invalidChapters.length > 0) {
      setUploadError('Please fill all required fields for each chapter');
      return;
    }

    setUploadingChapters(true);
    setUploadError(null);

    try {
      let baseChapterNumber = 1;
      try {
        const seriesResponse = await communityService.getSeriesById(seriesId);
        let seriesData = null;
        if (seriesResponse?.data?.series) {
          seriesData = seriesResponse.data.series;
        } else if (seriesResponse?.data) {
          seriesData = seriesResponse.data;
        } else if (seriesResponse?.series) {
          seriesData = seriesResponse.series;
        } else if (seriesResponse && typeof seriesResponse === 'object' && seriesResponse._id) {
          seriesData = seriesResponse;
        }
        
        if (seriesData?.chapters && Array.isArray(seriesData.chapters)) {
          baseChapterNumber = seriesData.chapters.length + 1;
        }
      } catch (error) {
        console.warn('Could not fetch series data for chapter numbering:', error);
      }

      for (let i = 0; i < chaptersToUpload.length; i++) {
        const chapter = chaptersToUpload[i];
        
        updateChapter(chapter.id, 'uploading', true);

        try {
          const videoContentType = chapter.videoFile ? getVideoContentType(chapter.videoFile) : 'mp4';
          const thumbnailContentType = chapter.thumbnailFile ? (chapter.thumbnailFile.type.split('/')[1] || 'png') : 'png';

          const uploadUrls = await communityService.generateUploadUrls(
            videoContentType,
            thumbnailContentType
          );

          if (chapter.videoFile && uploadUrls.videoUploadUrl) {
            const videoMimeType = chapter.videoFile.type || (videoContentType === 'mov' ? 'video/quicktime' : 'video/mp4');
            await communityService.uploadToS3(
              uploadUrls.videoUploadUrl,
              chapter.videoFile,
              videoMimeType
            );
          }

          if (chapter.thumbnailFile && uploadUrls.thumbnailUploadUrl) {
            await communityService.uploadToS3(
              uploadUrls.thumbnailUploadUrl,
              chapter.thumbnailFile,
              chapter.thumbnailFile.type
            );
          }

          const tags = selectedTags.map(tag => tag.value);
          const payload: any = {
            videoId: uploadUrls.videoId,
            title: chapter.title.trim(),
            description: chapter.description.trim(),
            category: 'level_up',
            contentType: 'long_form',
            mediaType: 'video',
            videoType: 'chapter',
            tags: tags,
            uploadedBy: user?._id || user?.id || '',
            uploadedByRole: user?.role || 'admin',
            isPortrait: false,
            seriesId: seriesId,
            chapterNumber: baseChapterNumber + i
          };

          if (selectedEntity && selectedEntityType) {
            const baseMetadata = {
              id: selectedEntityType === EntityType.SFL_USER ? 'sfl' : selectedEntity.entityId,
              type: selectedEntityType,
              userProfilePic: selectedEntityType === EntityType.SFL_USER ? 'shared/sfl-logo.png' : (selectedEntity.profilePic || undefined)
            };

            if (selectedEntityType === EntityType.COACH) {
              // Start with basicInfo from selectedEntity (this should contain all coach data)
              let basicInfo = selectedEntity.basicInfo || {};
              
              // Check if basicInfo is empty or only has minimal data
              const hasMinimalBasicInfo = !basicInfo || Object.keys(basicInfo).length === 0 || 
                (Object.keys(basicInfo).length === 1 && basicInfo.sports);
              
              // If basicInfo is empty, try to fetch coach data on-the-fly
              if (hasMinimalBasicInfo && selectedEntity.entityId) {
                try {
                  const coaches = await communityService.getAllCoachesForAdmin(selectedEntity.entityName || '');
                  const matchingCoach = coaches.find((coach: any) => 
                    (coach._id || coach.coachId || coach.id) === selectedEntity.entityId
                  );
                  
                  if (matchingCoach) {
                    if (matchingCoach.basicInfo) {
                      basicInfo = matchingCoach.basicInfo;
                    } else {
                      // Construct basicInfo from coach object
                      const sportsArray = Array.isArray(matchingCoach.basicInfo?.sports) 
                        ? matchingCoach.basicInfo.sports 
                        : Array.isArray(matchingCoach.sports) 
                        ? matchingCoach.sports 
                        : [];
                      
                      basicInfo = {
                        ...(matchingCoach.fullName ? { fullName: matchingCoach.fullName } : {}),
                        ...(matchingCoach.email ? { email: matchingCoach.email } : {}),
                        ...(matchingCoach.dob ? { dob: matchingCoach.dob } : {}),
                        ...(matchingCoach.ageCategory ? { ageCategory: matchingCoach.ageCategory } : {}),
                        ...(matchingCoach.certification ? { certification: matchingCoach.certification } : {}),
                        ...(matchingCoach.experience ? { experience: matchingCoach.experience } : {}),
                        ...(matchingCoach.location ? { location: matchingCoach.location } : {}),
                        ...(sportsArray.length > 0 ? { sports: sportsArray } : {}),
                        _id: matchingCoach._id || matchingCoach.coachId || matchingCoach.id || selectedEntity.entityId
                      };
                    }
                  } else {
                    console.warn('Coach not found in fetched list');
                  }
                } catch (error) {
                  console.error('Error fetching coach details on-the-fly:', error);
                  // Continue with existing basicInfo if fetch fails
                  // But ensure we at least have minimum fields from selectedEntity
                  if (Object.keys(basicInfo).length === 0) {
                    basicInfo = {
                      _id: selectedEntity.entityId || selectedEntity.value || '',
                      fullName: selectedEntity.entityName || '',
                      profilePic: selectedEntity.profilePic || null
                    };
                  }
                }
              }
              
              // Final check: if basicInfo is still empty after all attempts, use selectedEntity data
              if (!basicInfo || Object.keys(basicInfo).length === 0) {
                console.warn('basicInfo is still empty after fetch attempt, using selectedEntity data');
                basicInfo = {
                  _id: selectedEntity.entityId || selectedEntity.value || '',
                  fullName: selectedEntity.entityName || '',
                  profilePic: selectedEntity.profilePic || null,
                  sports: Array.isArray(selectedEntity.sports) ? selectedEntity.sports : []
                };
              }
              
              // Get values, prioritizing basicInfo but falling back to selectedEntity properties
              const sports = Array.isArray(basicInfo.sports) && basicInfo.sports.length > 0 
                ? basicInfo.sports 
                : (Array.isArray(selectedEntity.sports) && selectedEntity.sports.length > 0 
                  ? selectedEntity.sports 
                  : []);
              
              const profilePic = basicInfo.profilePic || selectedEntity.profilePic || null;
              const fullName = basicInfo.fullName || selectedEntity.entityName || '';
              
              // Construct complete basicInfo ensuring all expected fields are present
              // IMPORTANT: Always explicitly set all fields, don't rely on spreading empty objects
              const basicInfoComplete: any = {
                // Always set these fields explicitly (don't rely on spread of empty object)
                fullName: fullName || basicInfo.fullName || selectedEntity.entityName || '',
                profilePic: profilePic || basicInfo.profilePic || selectedEntity.profilePic || null,
                sports: sports.length > 0 ? sports : (basicInfo.sports || selectedEntity.sports || []),
                // Explicitly ensure all expected fields are included (preserve null/empty if they exist in basicInfo, otherwise set defaults)
                dob: basicInfo.dob !== undefined ? basicInfo.dob : null,
                email: basicInfo.email !== undefined ? basicInfo.email : null,
                ageCategory: Array.isArray(basicInfo.ageCategory) ? basicInfo.ageCategory : [],
                certification: Array.isArray(basicInfo.certification) ? basicInfo.certification : [],
                experience: basicInfo.experience !== undefined ? basicInfo.experience : null,
                _id: basicInfo._id || selectedEntity.entityId || selectedEntity.value || ''
              };
              
              // Only add location if it exists (optional field)
              if (basicInfo.location !== undefined) {
                basicInfoComplete.location = basicInfo.location;
              }
              
              // Preserve any other fields from basicInfo that we haven't explicitly set
              Object.keys(basicInfo).forEach(key => {
                if (!basicInfoComplete.hasOwnProperty(key)) {
                  basicInfoComplete[key] = basicInfo[key];
                }
              });
              
              payload.metadata = {
                ...baseMetadata,
                basicInfo: basicInfoComplete
              };
            } else {
              payload.metadata = baseMetadata;
            }
          }

          if (uploadUrls.videoKey) {
            payload.videoKey = uploadUrls.videoKey;
          }
          if (uploadUrls.thumbnailKey) {
            payload.thumbnailKey = uploadUrls.thumbnailKey;
          }

          const response = await communityService.completeUpload(payload);
          
          updateChapter(chapter.id, 'uploaded', true);
          updateChapter(chapter.id, '_id', response?.data?._id || response?._id);
        } catch (error: any) {
          console.error(`Error uploading chapter ${i + 1}:`, error);
          const errorMsg = error.message || `Failed to upload chapter ${i + 1}`;
          setUploadError(errorMsg);
          throw error;
        } finally {
          updateChapter(chapter.id, 'uploading', false);
        }
      }

      toast.success(`Successfully uploaded ${chaptersToUpload.length} chapter(s)!`);
      setHasUploadedChapters(true);
      
      // Refresh parent to show all uploaded chapters
      if (initialTab === 'chapters') {
        onChapterAdded?.();
      } else {
      onSuccess?.();
      }
      setSeriesData({
        title: '',
        description: '',
        subCategory: '',
        sport: '',
        difficultyLevel: DifficultyLevel.BEGINNER,
        thumbnailUrl: '',
                coverImage: '',
                featured: false
      });
      setSeriesThumbnailFile(null);
      setSeriesThumbnailPreview(null);
      setSeriesCoverFile(null);
      setSeriesCoverPreview(null);
    } catch (error: any) {
      console.error('Error uploading chapters:', error);
      const errorMsg = error.message || 'Failed to upload chapters';
      setUploadError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setUploadingChapters(false);
    }
  };

  const getCategoryLabel = () => {
    switch (category) {
      case VideoCategory.ACTION:
        return 'Action';
      case VideoCategory.LEVEL_UP:
        return 'Level Up';
      case VideoCategory.SOCIAL:
        return 'Social';
      default:
        return 'Community';
    }
  };

  const mapCategoryToApiCategory = (category: VideoCategory): string => {
    switch (category) {
      case VideoCategory.ACTION:
        return 'action';
      case VideoCategory.LEVEL_UP:
        return 'level_up';
      case VideoCategory.SOCIAL:
        return 'social';
      default:
        return 'action';
    }
  };

  // Check if all required fields are filled
  const isFormValid = () => {
    // For Level Up category with chapters, check chapters instead
    if (category === VideoCategory.LEVEL_UP && !isEditMode && activeTab === 'chapters') {
      if (chapters.length === 0) {
        return false;
      }
      // Check if all chapters have required fields
      return chapters.every(ch => 
        ch.title.trim() && 
        ch.videoFile && 
        ch.thumbnailFile
      );
    }

    // Title is always required (for non-chapter uploads)
    if (!formData.title.trim()) {
      return false;
    }
    
    // For create mode, video/image and thumbnail files are required
    if (!isEditMode) {
      if (category === VideoCategory.SOCIAL) {
        // For Social, check based on post type
        if (postType === 'video') {
          // Video post: video and thumbnail are both required
      if (!videoFile || !thumbnailFile) {
        return false;
          }
        } else {
          // Image post: image is required
          if (!imageFile) {
            return false;
          }
        }
      } else if (category !== VideoCategory.LEVEL_UP) {
        // For Action (not Level Up), video and thumbnail are required
      if (!videoFile || !thumbnailFile) {
        return false;
        }
      }
      // For Level Up series, if not in chapters tab, series should be created first
      // For standalone Level Up, skip this check
      if (category === VideoCategory.LEVEL_UP && !isStandaloneLevelUp && activeTab !== 'chapters' && !createdSeriesId) {
        return false;
      }
    }
    
    // Entity selection is mandatory for all categories (except when uploading chapters or editing a chapter)
    // For Level Up chapters in edit mode, entity comes from parent series, so it's not required here
    // For standalone Level Up, coach or SFL User must be selected
    const isEditingChapter = isEditMode && category === VideoCategory.LEVEL_UP && (video?.videoType === 'chapter' || (video?.seriesId && video?.videoType !== 'series'));
    if (isStandaloneLevelUp) {
      // For standalone Level Up, coach or SFL User (entity) must be selected
      if (!selectedEntity || (selectedEntityType !== EntityType.COACH && selectedEntityType !== EntityType.SFL_USER)) {
        return false;
      }
    } else if ((category !== VideoCategory.LEVEL_UP || activeTab !== 'chapters') && !isEditingChapter) {
      // Entity type and entity must both be selected for Action/Social
      if (!selectedEntityType || !selectedEntity) {
        return false;
      }
    }
    
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    setUploadError(null);

    if (category === VideoCategory.LEVEL_UP && !isEditMode && activeTab === 'series-details') {
      await handleCreateSeries();
      return;
    }

    if (category === VideoCategory.LEVEL_UP && !isEditMode && activeTab === 'chapters' && createdSeriesId) {
      await handleBulkChaptersUpload();
      return;
    }

    if (!formData.title.trim()) {
      const errorMsg = 'Title is required';
      setUploadError(errorMsg);
      return;
    }

    if (!isEditMode) {
      if (category === VideoCategory.SOCIAL) {
        if (postType === 'video') {
          if (!videoFile) {
            const errorMsg = 'Please select a video file';
            setUploadError(errorMsg);
            return;
          }
          if (!thumbnailFile) {
            const errorMsg = 'Please select a thumbnail image';
            setUploadError(errorMsg);
            return;
          }
        } else {
          if (!imageFile) {
            const errorMsg = 'Please select an image file';
            setUploadError(errorMsg);
            return;
          }
        }
      } else if (category === VideoCategory.ACTION) {
        if (!videoFile) {
          const errorMsg = 'Please select a video file';
          setUploadError(errorMsg);
          return;
        }

        if (!thumbnailFile) {
          const errorMsg = 'Please select a thumbnail image';
          setUploadError(errorMsg);
          return;
        }
      }
    }
    // Entity selection is not required when:
    // 1. Level Up chapters tab (creating new chapters)
    // 2. Editing a chapter (chapters inherit entity from parent series)
    const isEditingChapter = isEditMode && category === VideoCategory.LEVEL_UP && (video?.videoType === 'chapter' || (video?.seriesId && video?.videoType !== 'series'));
    if ((category !== VideoCategory.LEVEL_UP || activeTab !== 'chapters') && !isEditingChapter) {
      if (!selectedEntityType) {
        const errorMsg = 'Please select who is adding this video (Player, Coach, Academy, or SFL User)';
        console.error('Validation failed:', errorMsg);
        setUploadError(errorMsg);
        return;
      }
      if (!selectedEntity) {
        const errorMsg = `Please select a ${selectedEntityType === EntityType.SFL_USER ? 'SFL User' : selectedEntityType}`;
        console.error('Validation failed:', errorMsg);
        setUploadError(errorMsg);
        return;
      }
    }

    setUploading(true);

    try {
      // Check if editing a Level Up series - use handleUpdateSeries instead
      if (isEditMode && video && category === VideoCategory.LEVEL_UP && video.videoType === 'series') {
        await handleUpdateSeries();
        return;
      }

      if (isEditMode && video) {
        // Check if files are being updated
        // For Social: include imageFile for image posts, videoFile/thumbnailFile for video posts
        const hasFileUpdates = category === VideoCategory.SOCIAL && postType === 'image'
          ? !!imageFile
          : videoFile || thumbnailFile;
        
        // Step 1: Handle file updates if files are selected
        if (hasFileUpdates) {
          // Handle Social image post updates differently
          if (category === VideoCategory.SOCIAL && postType === 'image' && imageFile) {
            console.log('Edit mode: Image file selected for update (Social image post)');
            
            const imageContentType = imageFile.type.split('/')[1] || 'png';
            
            // For Social image posts, use generateUpdateUrls with updateThumbnail flag
            // (image is stored as thumbnailUrl/contentUrl)
            const updateUrls = await communityService.generateUpdateUrls(
              video._id,
              {
                updateVideo: false,
                updateThumbnail: true,
                thumbnailContentType: imageContentType
              }
            );
            
            console.log('Edit mode: Update URLs received for image', updateUrls);
            
            // Upload image to S3
            if (updateUrls.thumbnailUploadUrl) {
              console.log('Edit mode: Uploading image to S3');
              await communityService.uploadToS3(
                updateUrls.thumbnailUploadUrl,
                imageFile,
                imageFile.type || 'image/png'
              );
              console.log('Edit mode: Image uploaded to S3 successfully');
            }
            
            // Complete the update
            const completeUpdatePayload: any = {
              updatedVideo: false,
              updatedThumbnail: true
            };
            
            if (updateUrls.thumbnailKey) {
              completeUpdatePayload.thumbnailKey = updateUrls.thumbnailKey;
            }
            
            console.log('Edit mode: Calling complete-update for image with payload:', completeUpdatePayload);
            
            const updateResponse = await communityService.completeUpdate(
              video._id,
              completeUpdatePayload
            );
            
            console.log('Edit mode: Image update completed', {
              response: updateResponse,
              thumbnailUrl: updateResponse?.thumbnailUrl
            });
          } else {
            // Handle video/thumbnail updates (for Actions, Level Up, and Social video posts)
            console.log('Edit mode: Files selected for update', {
              hasVideo: !!videoFile,
              hasThumbnail: !!thumbnailFile,
              category: category,
              postType: category === VideoCategory.SOCIAL ? postType : 'video'
            });
            
            // Generate update URLs
            const videoContentType = videoFile ? getVideoContentType(videoFile) : undefined;
            const thumbnailContentType = thumbnailFile ? (thumbnailFile.type.split('/')[1] || 'png') : undefined;
            
            const updateUrls = await communityService.generateUpdateUrls(
              video._id,
              {
                updateVideo: !!videoFile,
                updateThumbnail: !!thumbnailFile,
                videoContentType: videoContentType,
                thumbnailContentType: thumbnailContentType
              }
            );
            
            console.log('Edit mode: Update URLs received', updateUrls);
            
            // Upload files to S3
            if (videoFile && updateUrls.videoUploadUrl) {
              const videoMimeType = videoFile.type || (videoContentType === 'mov' ? 'video/quicktime' : 'video/mp4');
              console.log('Edit mode: Uploading video to S3');
              await communityService.uploadToS3(
                updateUrls.videoUploadUrl,
                videoFile,
                videoMimeType
              );
              console.log('Edit mode: Video uploaded to S3 successfully');
            }
            
            if (thumbnailFile && updateUrls.thumbnailUploadUrl) {
              console.log('Edit mode: Uploading thumbnail to S3');
              await communityService.uploadToS3(
                updateUrls.thumbnailUploadUrl,
                thumbnailFile,
                thumbnailFile.type || 'image/png'
              );
              console.log('Edit mode: Thumbnail uploaded to S3 successfully');
            }
            
            // Complete the update - include videoKey and thumbnailKey like in complete-upload
            const completeUpdatePayload: any = {
              updatedVideo: !!videoFile,
              updatedThumbnail: !!thumbnailFile
            };
            
            // Add S3 keys if provided by the generate-update-urls API (similar to complete-upload)
            if (videoFile && updateUrls.videoKey) {
              completeUpdatePayload.videoKey = updateUrls.videoKey;
            }
            if (thumbnailFile && updateUrls.thumbnailKey) {
              completeUpdatePayload.thumbnailKey = updateUrls.thumbnailKey;
            }
            
            console.log('Edit mode: Calling complete-update with payload:', completeUpdatePayload);
            
            const updateResponse = await communityService.completeUpdate(
              video._id,
              completeUpdatePayload
            );
            
            console.log('Edit mode: File update completed', {
              response: updateResponse,
              contentUrl: updateResponse?.contentUrl,
              thumbnailUrl: updateResponse?.thumbnailUrl,
              hasVideoUpdate: !!videoFile,
              hasThumbnailUpdate: !!thumbnailFile
            });
            
            // Verify that the update was successful
            if (updateResponse) {
              // If video was updated, verify contentUrl is present and updated
              if (videoFile) {
                if (!updateResponse.contentUrl) {
                  console.warn('Video update completed but contentUrl is missing in response');
                } else {
                  console.log('Video update successful - new contentUrl:', updateResponse.contentUrl);
                }
              }
              // If thumbnail was updated, verify thumbnailUrl is present and updated
              if (thumbnailFile) {
                if (!updateResponse.thumbnailUrl) {
                  console.warn('Thumbnail update completed but thumbnailUrl is missing in response');
                } else {
                  console.log('Thumbnail update successful - new thumbnailUrl:', updateResponse.thumbnailUrl);
                }
              }
            } else {
              console.warn('completeUpdate returned empty response');
            }
          }
        }
        
        // Step 2: Update text fields (always, even if files were updated)
        const updatePayload: any = {
          title: formData.title.trim(),
          description: formData.description.trim(),
          tags: selectedTags.map(tag => tag.value),
          ...((category === VideoCategory.ACTION || category === VideoCategory.SOCIAL || (category === VideoCategory.LEVEL_UP && isStandaloneLevelUp)) && { featured: isFeatured }) // Featured field for Action, Social, and standalone Level Up categories
        };

        // Add seriesId for Level Up chapters only
        if (category === VideoCategory.LEVEL_UP && (video.seriesId || video.videoType === 'chapter')) {
          // For chapters, seriesId is required in the update payload
          if (video.seriesId) {
            updatePayload.seriesId = video.seriesId;
          } else if (video.videoType === 'chapter') {
            // If seriesId is missing but it's a chapter, we need to find it
            // This should have been set during data loading, but as a fallback:
            console.warn('Chapter update: seriesId is missing, update may fail');
          }
        }

        if (selectedEntity && selectedEntityType) {
          // For Level Up standalone videos, handle metadata based on entity type
          if (category === VideoCategory.LEVEL_UP && isStandaloneLevelUp) {
            if (selectedEntityType === EntityType.SFL_USER) {
              // For SFL User in Level Up, use the exact structure requested
              updatePayload.metadata = {
                id: 'sfl',
                type: 'sfl_user',
                userName: 'sfl',
                userProfilePic: 'shared/sfl-logo.png'
              };
            } else if (selectedEntityType === EntityType.COACH) {
              // For Coach in Level Up, include basicInfo
              updatePayload.metadata = {
                id: selectedEntity.entityId,
            type: selectedEntityType,
                userName: selectedEntity.entityName,
                userProfilePic: selectedEntity.profilePic || undefined,
                basicInfo: selectedEntity.basicInfo || {}
          };
            } else {
              // For other entity types in Level Up
          updatePayload.metadata = {
                id: selectedEntity.entityId,
                type: selectedEntityType,
                userName: selectedEntity.entityName,
                userProfilePic: selectedEntity.profilePic || undefined
              };
            }
          } else {
            // For Action and Social categories, use base metadata without basicInfo
            const baseMetadata = {
              id: selectedEntityType === EntityType.SFL_USER ? 'sfl' : selectedEntity.entityId,
              type: selectedEntityType === EntityType.SFL_USER ? 'sfl_user' : selectedEntityType,
              userName: selectedEntityType === EntityType.SFL_USER ? 'sfl' : selectedEntity.entityName,
              userProfilePic: selectedEntityType === EntityType.SFL_USER ? 'shared/sfl-logo.png' : (selectedEntity.profilePic || undefined)
            };
            updatePayload.metadata = baseMetadata;
          }
        }

        await communityService.updateVideo(video._id, updatePayload);
        toast.success('Video updated successfully');
        
        // Close form first
        onHide();
        
        // Trigger success callback after a short delay to ensure form is closed
        setTimeout(() => {
          onSuccess?.();
        }, 100);
        
        setUploading(false);
        return;
      }

      // Handle create mode - check if it's an image post for Social
      if (category === VideoCategory.SOCIAL && postType === 'image') {
        // Handle image post upload
        if (!imageFile) {
          throw new Error('Please select an image file');
        }

        const imageContentType = imageFile.type.split('/')[1] || 'png';
        
        const imageUploadUrls = await communityService.generateImagePostUploadUrl(imageContentType);
        
        if (!imageUploadUrls.imageId || !imageUploadUrls.imageUploadUrl) {
          throw new Error('Failed to generate upload URL - missing required data');
        }
        
        try {
          await communityService.uploadToS3(
            imageUploadUrls.imageUploadUrl,
            imageFile,
            imageFile.type
          );
        } catch (s3Error: any) {
          throw new Error(`Failed to upload image to S3: ${s3Error.message || 'Unknown error'}`);
        }

        // Step 3: Parse tags from selected options
        const tags = selectedTags.map(tag => tag.value);

        // Step 4: Prepare payload for complete-upload API
        // API uses postId (from generate-upload-url response) for image posts
        const payload: any = {
          postId: imageUploadUrls.imageId, // imageId contains postId from API response
          title: formData.title,
          description: formData.description || '',
          category: mapCategoryToApiCategory(category),
          tags: tags,
          uploadedBy: user?._id || user?.id || '',
          uploadedByRole: user?.role || 'admin'
        };

        if (selectedEntity && selectedEntityType) {
          payload.metadata = {
            id: selectedEntityType === EntityType.SFL_USER ? 'sfl' : selectedEntity.entityId,
            type: selectedEntityType,
            userName: selectedEntityType === EntityType.SFL_USER ? 'sfl' : selectedEntity.entityName,
            userProfilePic: selectedEntityType === EntityType.SFL_USER ? 'shared/sfl-logo.png' : (selectedEntity.profilePic || undefined)
          };
        }

        if (formData.schedulePublish && formData.publishDate && formData.publishTime) {
          const publishDateTime = new Date(`${formData.publishDate}T${formData.publishTime}`);
          payload.scheduledPublishAt = publishDateTime.toISOString();
        }
        console.log('Step 5: Calling complete-upload API for image post:', {
          endpoint: '/api/v1/community/image-post/complete-upload',
          payload: payload
        });
        
        try {
          await communityService.completeImagePostUpload(payload);
          console.log('Step 5: Image post upload completed successfully!');
        } catch (completeError: any) {
          console.error('Step 5: Failed to complete image post upload:', completeError);
          throw new Error(`Failed to complete image post upload: ${completeError.message || 'Unknown error'}`);
        }

        // Show success toast
        toast.success('Social post uploaded successfully!', {
          position: 'top-right',
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true
        });

        // Close form first
        onHide();
        
        // Trigger success callback immediately to refresh listing
        onSuccess?.();
        
        // Reset form
        setFormData({
          title: '',
          description: '',
          schedulePublish: false,
          publishDate: '',
          publishTime: ''
        });
        setSelectedTags([]);
        setImageFile(null);
        if (imagePreview) {
          URL.revokeObjectURL(imagePreview);
        }
        setImagePreview(null);
        setActiveTab('video-details');
        setSelectedEntityType(null);
        setSelectedEntity(null);
        setPostType('video');
        setUploading(false);
        return;
      }

      // Handle video post upload (for all categories including Social video posts)
      // For Social category video posts, video is optional
      if (category === VideoCategory.SOCIAL && postType === 'video') {
        // For Social video posts, we can have just thumbnail without video
        if (videoFile && !thumbnailFile) {
          throw new Error('Please select a thumbnail image');
        }
      } else if (category !== VideoCategory.SOCIAL) {
        // For Action and Level Up, both video and thumbnail are required
      if (!videoFile || !thumbnailFile) {
        throw new Error('Video and thumbnail files are required');
        }
      }

      // Step 1: Generate presigned URLs
      // THIS IS WHERE WE HIT THE PRESIGNED URL API: /api/v1/community/video/generate-upload-urls
      // This happens when the user clicks "Upload Video +" and submits the form
      // We send: { videoContentType: "mp4" or "mov", thumbnailContentType: "png" }
      // We receive: { videoId, videoUploadUrl (presigned), thumbnailUploadUrl (presigned) }
      
      // Detect video format (mp4 or mov) and get appropriate content type
      // For Social category video posts, video is optional
      const videoContentType = videoFile ? getVideoContentType(videoFile) : 'mp4';
      const thumbnailContentType = thumbnailFile ? (thumbnailFile.type.split('/')[1] || 'png') : 'png';
      
      console.log('Step 1: Generating presigned URLs - Calling API:', {
        endpoint: '/api/v1/community/video/generate-upload-urls',
        payload: { videoContentType, thumbnailContentType },
        videoFileName: videoFile?.name,
        videoFileType: videoFile?.type,
        hasVideo: !!videoFile,
        hasThumbnail: !!thumbnailFile
      });
      
      const generatedUrls = await communityService.generateUploadUrls(
        videoContentType,
        thumbnailContentType
      );
      
      console.log('Step 1: Presigned URLs received:', {
        videoId: generatedUrls.videoId,
        hasVideoUrl: !!generatedUrls.videoUploadUrl,
        hasThumbnailUrl: !!generatedUrls.thumbnailUploadUrl,
        videoKey: generatedUrls.videoKey,
        thumbnailKey: generatedUrls.thumbnailKey,
        videoUploadUrl: generatedUrls.videoUploadUrl?.substring(0, 100) + '...',
        thumbnailUploadUrl: generatedUrls.thumbnailUploadUrl?.substring(0, 100) + '...'
      });
      
      if (!generatedUrls.videoId || !generatedUrls.thumbnailUploadUrl) {
        console.error('Missing required URLs from presigned URL response:', generatedUrls);
        throw new Error('Failed to generate upload URLs - missing required data');
      }

      // Step 2: Upload video to S3 using the presigned URL (only if video file exists)
      if (videoFile && generatedUrls.videoUploadUrl) {
      const videoMimeType = videoFile.type || (videoContentType === 'mov' ? 'video/quicktime' : 'video/mp4');
      
      console.log('Step 2: Uploading video to S3 using presigned URL:', {
        presignedUrl: generatedUrls.videoUploadUrl.substring(0, 50) + '...',
        videoMimeType,
        videoFileName: videoFile.name,
        videoFileSize: `${(videoFile.size / (1024 * 1024)).toFixed(2)} MB`
      });
      
      await communityService.uploadToS3(
        generatedUrls.videoUploadUrl,
        videoFile,
        videoMimeType
      );
      
      console.log('Step 2: Video uploaded to S3 successfully');
      } else {
        console.log('Step 2: Skipping video upload (no video file provided)');
      }

      // Step 3: Upload thumbnail to S3 using the presigned URL (only if thumbnail file exists)
      if (thumbnailFile && generatedUrls.thumbnailUploadUrl) {
      console.log('Step 3: Uploading thumbnail to S3 using presigned URL');
      await communityService.uploadToS3(
        generatedUrls.thumbnailUploadUrl,
        thumbnailFile,
          thumbnailFile?.type || 'image/png'
      );
      
      console.log('Step 3: Thumbnail uploaded to S3 successfully');
      } else {
        console.log('Step 3: Skipping thumbnail upload (no thumbnail file provided)');
      }

      const uploadUrls = {
        videoId: generatedUrls.videoId,
        videoKey: generatedUrls.videoKey,
        thumbnailKey: generatedUrls.thumbnailKey
      };

      // Step 4: Parse tags from selected options
      const tags = selectedTags.map(tag => tag.value);

      // Step 5: Prepare payload for complete-upload API
      // This payload includes the videoId and keys from the presigned URL response
      const payload: any = {
        videoId: uploadUrls.videoId,
        title: formData.title,
        description: formData.description || '',
        category: mapCategoryToApiCategory(category),
        contentType: isStandaloneLevelUp ? 'long_form' : 'short_form',
        mediaType: 'video',
        videoType: isStandaloneLevelUp ? 'single' : 'single',
        tags: tags,
        uploadedBy: user?._id || user?.id || '',
        uploadedByRole: user?.role || 'admin',
        isPortrait: videoFile ? isPortrait : false, // User-selected orientation (defaults to false if no video)
        ...((category === VideoCategory.ACTION || category === VideoCategory.SOCIAL) && { featured: isFeatured }) // Featured field for Action and Social categories
      };

      // Add S3 keys if provided by the presigned URL API
      // Only add videoKey if video was uploaded
      if (videoFile && uploadUrls.videoKey) {
        payload.videoKey = uploadUrls.videoKey;
      }
      if (uploadUrls.thumbnailKey) {
        payload.thumbnailKey = uploadUrls.thumbnailKey;
      }
      
      // For Social category without video, update mediaType
      if (!videoFile && category === VideoCategory.SOCIAL) {
        payload.mediaType = 'image'; // or 'post' depending on your backend
      }

      console.log('Step 5: Preparing complete-upload payload:', {
        videoId: payload.videoId,
        hasVideoKey: !!payload.videoKey,
        hasThumbnailKey: !!payload.thumbnailKey,
        title: payload.title,
        category: payload.category
      });

      // Add metadata for all categories if entity is selected
      // Match backend interfaces: IPlayerStoriesMetadata, ICoachCornerMetadata, ISocialMetadata
      if (selectedEntity && selectedEntityType) {
        // For SFL_USER, use fixed values with correct type string
        const baseMetadata: any = {
          id: selectedEntityType === EntityType.SFL_USER ? 'sfl' : selectedEntity.entityId,
          type: selectedEntityType === EntityType.SFL_USER ? 'sfl_user' : selectedEntityType,
          userName: selectedEntityType === EntityType.SFL_USER ? 'sfl' : selectedEntity.entityName,
          userProfilePic: selectedEntityType === EntityType.SFL_USER ? 'shared/sfl-logo.png' : (selectedEntity.profilePic || undefined)
        };

        // For Level Up standalone videos and series, include basicInfo for Coach
        if (category === VideoCategory.LEVEL_UP) {
          if (selectedEntityType === EntityType.SFL_USER) {
            // For SFL User in Level Up, use the exact structure requested
            payload.metadata = {
              id: 'sfl',
              type: 'sfl_user',
              userName: 'sfl',
              userProfilePic: 'shared/sfl-logo.png'
            };
          } else if (selectedEntityType === EntityType.COACH) {
            // For Coach in Level Up, include basicInfo
        payload.metadata = {
            ...baseMetadata,
            basicInfo: selectedEntity.basicInfo || {}
        };
        } else {
            payload.metadata = baseMetadata;
          }
        } else {
          // For Action and Social categories, use baseMetadata without basicInfo
          payload.metadata = baseMetadata;
        }
      }

      // Add scheduled publish if enabled
      if (formData.schedulePublish && formData.publishDate && formData.publishTime) {
        const publishDateTime = new Date(`${formData.publishDate}T${formData.publishTime}`);
        payload.scheduledPublishAt = publishDateTime.toISOString();
      }

      // Step 6: Complete upload - This is the "add video" API call
      // It uses the videoId and keys from the presigned URL response
      console.log('Step 6: Calling complete-upload API:', {
        endpoint: '/api/v1/community/video/complete-upload',
        payload: payload
      });
      
      await communityService.completeUpload(payload);
      
      console.log('Step 6: Video upload completed successfully!');

      // Show success toast
      const categoryLabel = category === VideoCategory.ACTION ? 'Action' : category === VideoCategory.LEVEL_UP ? 'Level Up' : 'Social';
      toast.success(`${categoryLabel} video uploaded successfully!`, {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });

      // Close form first
      onHide();
      
      // Trigger success callback immediately to refresh listing
      // The parent component handles the refresh logic with proper timing
      onSuccess?.();
      
      // Reset form
      setFormData({
        title: '',
        description: '',
        schedulePublish: false,
        publishDate: '',
        publishTime: ''
      });
      setSelectedTags([]);
      setVideoFile(null);
      if (videoPreview) {
        URL.revokeObjectURL(videoPreview);
      }
      setVideoPreview(null);
      setThumbnailFile(null);
      if (thumbnailPreview) {
        URL.revokeObjectURL(thumbnailPreview);
      }
      setThumbnailPreview(null);
      setImageFile(null);
      if (imagePreview) {
        URL.revokeObjectURL(imagePreview);
      }
      setImagePreview(null);
      setActiveTab('video-details');
      setSelectedEntityType(null);
      setSelectedEntity(null);
      setPostType('video');
      
      // Reset upload state for Actions category
      setVideoUploadStatus('idle');
      setVideoUploadProgress(0);
      setVideoUploadError(null);
      setUploadedVideoData(null);
      setThumbnailUploadStatus('idle');
      setThumbnailUploadProgress(0);
      setThumbnailUploadError(null);
      setUploadedThumbnailKey(null);
    } catch (error: any) {
      console.error('Upload error:', error);
      
      // Extract error message
      // The completeUpload service throws an Error with the correct message from API response
      // So error.message should contain the API error message
      let errorMessage = 'Failed to upload video. Please try again.';
      
      if (error?.message) {
        // Service throws Error with message from API: "Failed to complete video upload: Invalid metadata for action category"
        errorMessage = error.message;
      } else if (error?.response?.data?.error) {
        // Fallback: check response data directly if service didn't throw properly
        errorMessage = error.response.data.error;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      setUploadError(errorMessage);
      
      // Show error toast
      toast.error(errorMessage, {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });
    } finally {
      setUploading(false);
    }
  };

  if (!show && !inline) return null;

  // If inline mode, render form directly without sidebar/backdrop
  if (inline && show) {
  return (
      <div className="add-new-form-inline">
        <div className="add-new-form-body">
          <Form onSubmit={handleSubmit} className="add-new-form-form">
            {uploadError && (
              <div className="alert alert-danger" role="alert">
                {uploadError}
              </div>
            )}

            {/* Level Up Content Type Selection - Show only when creating Level Up and no selection made yet */}
            {category === VideoCategory.LEVEL_UP && !isEditMode && !levelUpContentType && (
              <Form.Group className="mb-4">
                <Form.Label>
                  Level Up Type
                  <span style={{ color: 'red', marginLeft: '4px' }}>*</span>
                </Form.Label>
                <Select
                  value={null}
                  onChange={(option: any) => {
                    const selectedType = option?.value as 'standalone' | 'series';
                    if (selectedType) {
                      setLevelUpContentType(selectedType);
                      if (selectedType === 'standalone') {
                        setActiveTab('video-details');
                      } else {
                        setActiveTab('series-details');
                      }
                    }
                  }}
                  options={[
                    { value: 'standalone', label: 'Standalone Video' },
                    { value: 'series', label: 'Series' }
                  ]}
                  placeholder="Select Level Up Type"
                  isClearable={false}
                  isDisabled={uploading}
                  classNamePrefix="react-select"
                  styles={{
                    control: (base) => ({
                      ...base,
                      minHeight: '38px',
                      border: '1px solid #dee2e6',
                      borderRadius: '6px'
                    }),
                    menu: (base) => ({
                      ...base,
                      zIndex: 9999
                    }),
                    menuPortal: (base) => ({
                      ...base,
                      zIndex: 9999
                    })
                  }}
                  menuPortalTarget={document.body}
                />
                <Form.Text className="text-muted">
                  Choose whether to create a single video or a series with multiple chapters
                </Form.Text>
              </Form.Group>
            )}

            {/* Tabs - Show Series Details/Chapters for Level Up, regular tabs for others */}
            {!(category === VideoCategory.LEVEL_UP && !isEditMode && !levelUpContentType) && (
              <Tab.Container 
                activeKey={activeTab || (isStandaloneLevelUp ? 'video-details' : category === VideoCategory.LEVEL_UP && isEditMode && (video?.videoType === 'chapter' || (video?.seriesId && video?.videoType !== 'series')) ? 'video-details' : category === VideoCategory.LEVEL_UP && (isEditMode && video?.videoType === 'series' || levelUpContentType === 'series') ? 'series-details' : 'video-details')} 
                onSelect={(k) => setActiveTab(k || 'video-details')}
                defaultActiveKey={isStandaloneLevelUp ? 'video-details' : category === VideoCategory.LEVEL_UP && isEditMode && (video?.videoType === 'chapter' || (video?.seriesId && video?.videoType !== 'series')) ? 'video-details' : category === VideoCategory.LEVEL_UP && (isEditMode && video?.videoType === 'series' || levelUpContentType === 'series') ? 'series-details' : 'video-details'}
              >
                <Nav variant="tabs" className="mb-4 form-tabs">
                  {category === VideoCategory.LEVEL_UP && !isStandaloneLevelUp && (!isEditMode || !inline) ? (
                    <>
                      <Nav.Item>
                        <Nav.Link 
                          eventKey="series-details"
                          onClick={() => {
                            console.log('Series Details tab clicked, setting activeTab to series-details');
                            setActiveTab('series-details');
                          }}
                        >
                          <i className="ph-list me-2"></i>
                          Series Details
                        </Nav.Link>
                      </Nav.Item>
                        <Nav.Item>
                          <Nav.Link 
                            eventKey="chapters" 
                            disabled={!createdSeriesId}
                            onClick={() => {
                              // Reset chapter form state when switching to chapters tab to show listing first
                              setShowAddChapterForm(false);
                              setSelectedChapterForEdit(null);
                              setOriginalChapterData(null);
                              setHasChapterChanges(false);
                              setActiveTab('chapters');
                            }}
                          >
                            <i className="ph-play me-2"></i>
                            Chapters
                          </Nav.Link>
                        </Nav.Item>
                    </>
                  ) : category === VideoCategory.LEVEL_UP && isEditMode && inline ? (
                    // Hide tabs in edit mode for Level Up (series or chapter) when in inline mode (detail page) since user is already in that detail section
                    null
                  ) : (
                    <>
                      <Nav.Item>
                        <Nav.Link eventKey="video-details">
                          <i className="ph-camera me-2"></i>
                          {category === VideoCategory.ACTION ? 'Action Details' : 
                           category === VideoCategory.LEVEL_UP && isStandaloneLevelUp ? 'Level Up Details' :
                           'Social Details'}
                        </Nav.Link>
                      </Nav.Item>
                      {(category === VideoCategory.ACTION || category === VideoCategory.SOCIAL) && (
                        <Nav.Item>
                          <Nav.Link eventKey="player-details">
                            <i className="ph-user me-2"></i>
                            Entity Details
                          </Nav.Link>
                        </Nav.Item>
                      )}
                    </>
                  )}
                </Nav>

                <Tab.Content>
                  {/* Video Details Tab - Show for non-Level Up, standalone Level Up, or Level Up chapters when editing */}
                  {(category !== VideoCategory.LEVEL_UP || isStandaloneLevelUp || (isEditMode && (video?.videoType === 'chapter' || (video?.seriesId && video?.videoType !== 'series'))) || (!isEditMode && category === VideoCategory.LEVEL_UP && levelUpContentType === 'standalone')) ? (
                  <Tab.Pane eventKey="video-details">
                    {/* Post Type Selection - Only for Social category */}
                    {category === VideoCategory.SOCIAL && (
                      <Form.Group className="mb-4">
                        <Form.Label>
                          Post Type
                          <span style={{ color: 'red', marginLeft: '4px' }}>*</span>
                        </Form.Label>
                        <div className="d-flex gap-3">
                          <FormCheck
                            type="radio"
                            id="post-type-video"
                            name="postType"
                            label="Video"
                            checked={postType === 'video'}
                            onChange={() => {
                              setPostType('video');
                              setImageFile(null);
                              setImagePreview(null);
                            }}
                            disabled={uploading}
                          />
                          <FormCheck
                            type="radio"
                            id="post-type-image"
                            name="postType"
                            label="Image"
                            checked={postType === 'image'}
                            onChange={() => {
                              setPostType('image');
                              setVideoFile(null);
                              setVideoPreview(null);
                              setThumbnailFile(null);
                              setThumbnailPreview(null);
                              setIsPortrait(false);
                            }}
                            disabled={uploading}
                          />
                        </div>
                      </Form.Group>
                    )}
                    
                    {/* Video and Thumbnail Upload in Same Row - Show for video posts or non-Social categories */}
                    {(category !== VideoCategory.SOCIAL || postType === 'video') && (
            <Row>
              <Col md={6}>
                <div className="form-section">
                  <label className="form-section-label">
                    Video {isEditMode ? 'Upload' : 'Upload'}
                    {!isEditMode && <span style={{ color: 'red', marginLeft: '4px' }}>*</span>}
                  </label>
                  <div className="video-upload-box">
                    {videoPreview ? (
                      <div className="video-preview-container">
                        <video 
                          src={videoPreview} 
                          controls 
                          className="video-preview"
                          style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '4px' }}
                        />
                        <div className="selected-file-info">
                          <i className="ph-file-video"></i>
                          <span>{videoFile?.name || (isEditMode ? 'Current video (click to change)' : videoFile?.name)}</span>
                          {videoFile && <span className="file-size">({videoFile ? (videoFile.size / (1024 * 1024)).toFixed(2) : 0} MB)</span>}
                        </div>
                        <button 
                          type="button" 
                          className="select-file-btn" 
                          onClick={() => document.getElementById('video-file-input')?.click()}
                          disabled={uploading}
                          style={{ marginTop: '8px' }}
                        >
                          <i className="ph-upload"></i>
                          {isEditMode ? 'Change Video' : 'Change Video'}
                        </button>
                      </div>
                    ) : (
                      !isEditMode && (
                      <>
                        <div className="video-upload-placeholder">
                          <i className="ph-cloud-arrow-up"></i>
                          <p>Select Video to Upload</p>
                          <p className="video-upload-hint">Supported Format: MP4, MOV (Max: 2GB)</p>
                        </div>
                        <button 
                          type="button" 
                          className="select-file-btn" 
                          onClick={() => document.getElementById('video-file-input')?.click()}
                          disabled={uploading}
                        >
                          <i className="ph-upload"></i>
                          Select File
                        </button>
                      </>
                      )
                    )}
                    <input
                      id="video-file-input"
                      type="file"
                      accept="video/mp4,video/quicktime,.mp4,.mov"
                      onChange={handleVideoFileChange}
                      style={{ display: 'none' }}
                      disabled={uploading}
                    />
                  </div>
                  
                  {/* Portrait/Landscape Selection - Show only when video is selected and not in edit mode */}
                  {videoFile && !isEditMode && (
                    <Form.Group className="mb-3 mt-3">
                      <Form.Label>
                        Video Orientation
                        <span style={{ color: 'red', marginLeft: '4px' }}>*</span>
                      </Form.Label>
                      <div className="d-flex align-items-center gap-3">
                        <span className={isPortrait ? 'text-muted' : 'fw-bold'}>Landscape</span>
                        <FormCheck
                          type="switch"
                          id="portrait-toggle"
                          label={isPortrait ? 'Portrait' : 'Landscape'}
                          checked={isPortrait}
                          onChange={(e) => setIsPortrait(e.target.checked)}
                          disabled={uploading}
                          style={{ margin: 0 }}
                        />
                        <span className={isPortrait ? 'fw-bold' : 'text-muted'}>Portrait</span>
                      </div>
                    </Form.Group>
                  )}
                  
                </div>
              </Col>
              <Col md={6}>
                <div className="form-section">
                  <label className="form-section-label">
                    Video Thumbnail
                    {!isEditMode && <span style={{ color: 'red', marginLeft: '4px' }}>*</span>}
                  </label>
                  <div className="video-upload-box">
                    {thumbnailPreview ? (
                      <div className="video-preview-container">
                        <img 
                          src={thumbnailPreview} 
                          alt="Thumbnail preview"
                          style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '4px', objectFit: 'contain' }}
                        />
                        <div className="selected-file-info">
                          <i className="ph-image"></i>
                          <span>{thumbnailFile?.name || (isEditMode ? 'Current thumbnail (click to change)' : thumbnailFile?.name)}</span>
                          {thumbnailFile && <span className="file-size">({thumbnailFile ? (thumbnailFile.size / (1024 * 1024)).toFixed(2) : 0} MB)</span>}
                        </div>
                        <button 
                          type="button" 
                          className="select-file-btn" 
                          onClick={() => document.getElementById('thumbnail-input')?.click()}
                          disabled={uploading}
                          style={{ marginTop: '8px' }}
                        >
                          <i className="ph-upload"></i>
                          {isEditMode ? 'Change Thumbnail' : 'Change Thumbnail'}
                        </button>
                      </div>
                    ) : (
                      <>
                        <div className="video-upload-placeholder">
                          <i className="ph-cloud-arrow-up"></i>
                          <p>{isEditMode ? 'Select New Thumbnail' : 'Select Thumbnail to Upload'}</p>
                          <p className="video-upload-hint">Supported Format: JPG, PNG, GIF (Max: 50MB)</p>
                      </div>
                        <button 
                          type="button" 
                          className="select-file-btn" 
                          onClick={() => document.getElementById('thumbnail-input')?.click()}
                          disabled={uploading}
                        >
                          <i className="ph-upload"></i>
                          Select File
                        </button>
                      </>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      id="thumbnail-input"
                      onChange={handleThumbnailFileChange}
                      disabled={uploading}
                    />
                  </div>
                </div>
              </Col>
            </Row>
                    )}
                    
                    {/* Image Upload - Show only for Social image posts - Using same size as thumbnail */}
                    {category === VideoCategory.SOCIAL && postType === 'image' && (
                      <div className="form-section">
                        <label className="form-section-label">
                          Image Upload
                          <span style={{ color: 'red', marginLeft: '4px' }}>*</span>
                        </label>
                        <div className="video-upload-box">
                          {imagePreview ? (
                            <div className="video-preview-container">
                              <img 
                                src={imagePreview} 
                                alt="Image preview"
                                style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '4px', objectFit: 'contain' }}
                              />
                              <div className="selected-file-info">
                                <i className="ph-image"></i>
                                <span>{imageFile?.name}</span>
                                <span className="file-size">({imageFile ? (imageFile.size / (1024 * 1024)).toFixed(2) : 0} MB)</span>
                              </div>
                              <button 
                                type="button" 
                                className="select-file-btn" 
                                onClick={() => document.getElementById('image-file-input')?.click()}
                                disabled={uploading}
                                style={{ marginTop: '8px' }}
                              >
                                <i className="ph-upload"></i>
                                Change Image
                              </button>
                            </div>
                          ) : (
                            <>
                              <div className="video-upload-placeholder">
                                <i className="ph-cloud-arrow-up"></i>
                                <p>Select Image to Upload</p>
                                <p className="video-upload-hint">Supported Format: JPG, PNG, GIF (Max: 50MB)</p>
                              </div>
                              <button 
                                type="button" 
                                className="select-file-btn" 
                                onClick={() => document.getElementById('image-file-input')?.click()}
                                disabled={uploading}
                              >
                                <i className="ph-upload"></i>
                                Select File
                              </button>
                            </>
                          )}
                          <input
                            id="image-file-input"
                            type="file"
                            accept="image/*"
                            onChange={handleImageFileChange}
                            style={{ display: 'none' }}
                            disabled={uploading}
                          />
                        </div>
                      </div>
                    )}

            {/* Title */}
            <Form.Group className="mb-3">
              <Form.Label>
                Title
                <span style={{ color: 'red', marginLeft: '4px' }}>*</span>
              </Form.Label>
              <Form.Control
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="Enter Title"
                required
                disabled={uploading}
              />
            </Form.Group>

            {/* Tags */}
            <Form.Group className="mb-3">
              <Form.Label>Tags (#)</Form.Label>
              <CreatableSelect
                isMulti
                isClearable
                isDisabled={uploading || loadingTags}
                isLoading={loadingTags}
                options={existingTags}
                value={selectedTags}
                onChange={(newValue) => {
                  if (isEditMode) {
                    setHasFormChanges(true);
                  }
                  setSelectedTags(newValue);
                }}
                placeholder="Enter tags separated by commas or select from existing"
                classNamePrefix="react-select"
                styles={{
                  control: (base) => ({ 
                    ...base, 
                    minHeight: '38px',
                    border: '1px solid #dee2e6',
                    borderRadius: '6px'
                  }),
                  menu: (base) => ({ 
                    ...base, 
                    zIndex: 9999 
                  }),
                  menuPortal: (base) => ({ 
                    ...base, 
                    zIndex: 9999 
                  })
                }}
                menuPortalTarget={document.body}
                formatCreateLabel={(inputValue) => `Add "${inputValue}"`}
              />
            </Form.Group>

            {/* Entity Selection - Show for standalone Level Up videos in video-details tab */}
            {category === VideoCategory.LEVEL_UP && (isStandaloneLevelUp || (!isEditMode && levelUpContentType === 'standalone')) && (
              <div className="form-section">
                <label className="form-section-label">Entity Details</label>
                
                <Form.Group className="mb-3">
                  <Form.Label>
                    Who is adding this video?
                    <span style={{ color: 'red', marginLeft: '4px' }}>*</span>
                  </Form.Label>
                  <Select
                    options={[
                      { value: EntityType.COACH, label: 'Coach' },
                      { value: EntityType.SFL_USER, label: 'SFL User' }
                    ]}
                    value={selectedEntityType ? { 
                      value: selectedEntityType, 
                      label: selectedEntityType === EntityType.SFL_USER ? 'SFL User' : selectedEntityType.charAt(0).toUpperCase() + selectedEntityType.slice(1)
                    } : null}
                    onChange={(option: any) => {
                      if (isEditMode) {
                        setHasFormChanges(true);
                      }
                      const newEntityType = option?.value || null;
                      setSelectedEntityType(newEntityType);
                      
                      // If SFL_USER is selected, automatically set entity
                      if (newEntityType === EntityType.SFL_USER) {
                        setSelectedEntity({
                          value: 'sfl',
                          label: 'SFL',
                          entityId: 'sfl',
                          entityName: 'sfl',
                          profilePic: 'shared/sfl-logo.png',
                          entityType: EntityType.SFL_USER
                        });
                      } else {
                        setSelectedEntity(null); // Reset entity when type changes
                      }
                    }}
                    placeholder="Select entity type"
                    isClearable
                    isDisabled={uploading}
                    classNamePrefix="react-select"
                    styles={{
                      control: (base) => ({ 
                        ...base, 
                        minHeight: '38px',
                        border: '1px solid #dee2e6',
                        borderRadius: '6px'
                      }),
                      menu: (base) => ({ 
                        ...base, 
                        zIndex: 99999 
                      }),
                      menuPortal: (base) => ({ 
                        ...base, 
                        zIndex: 99999 
                      })
                    }}
                    menuPortalTarget={document.body}
                  />
                </Form.Group>

                {selectedEntityType && selectedEntityType !== EntityType.SFL_USER && (
                  <Form.Group className="mb-3">
                    <Form.Label>
                      {selectedEntityType === EntityType.COACH ? 'Coach' : 'Entity'}
                      <span style={{ color: 'red', marginLeft: '4px' }}>*</span>
                    </Form.Label>
                    <AsyncSelect
                      key={`entity-select-standalone-${selectedEntityType}-${selectedEntity?.entityId || 'none'}`}
                      cacheOptions
                      defaultOptions={selectedEntity ? [selectedEntity] : true}
                      loadOptions={(inputValue: string) => loadEntities(inputValue, selectedEntityType)}
                      value={selectedEntity}
                      onChange={(option: EntityOption | null) => {
                        if (isEditMode) {
                          setHasFormChanges(true);
                        }
                        if (option) {
                          const sportsArray = Array.isArray(option.sports) 
                            ? option.sports 
                            : Array.isArray(option.basicInfo?.sports)
                            ? option.basicInfo.sports
                            : [];
                          
                          const basicInfo = option.basicInfo || {};
                          const basicInfoComplete = {
                            ...basicInfo,
                            ...(sportsArray.length > 0 ? { sports: sportsArray } : {}),
                            ...(option.profilePic ? { profilePic: option.profilePic } : {}),
                            ...(option.entityName ? { fullName: option.entityName } : {}),
                            ...(basicInfo.email ? { email: basicInfo.email } : {}),
                            ...(basicInfo.dob ? { dob: basicInfo.dob } : {}),
                            ...(basicInfo.ageCategory ? { ageCategory: basicInfo.ageCategory } : {}),
                            ...(basicInfo.certification ? { certification: basicInfo.certification } : {}),
                            ...(basicInfo.experience ? { experience: basicInfo.experience } : {}),
                            ...(basicInfo.location ? { location: basicInfo.location } : {})
                          };
                          
                          setSelectedEntity({
                            ...option,
                            sports: sportsArray,
                            basicInfo: basicInfoComplete
                          });
                        } else {
                          setSelectedEntity(null);
                        }
                      }}
                      placeholder={`Search and select a ${selectedEntityType === EntityType.COACH ? 'coach' : selectedEntityType}`}
                      isClearable
                      isDisabled={uploading}
                      getOptionValue={(option: EntityOption) => option.entityId}
                      getOptionLabel={(option: EntityOption) => option.entityName}
                      classNamePrefix="react-select"
                      styles={{
                        control: (base) => ({ 
                          ...base, 
                          minHeight: '38px',
                          border: '1px solid #dee2e6',
                          borderRadius: '6px'
                        }),
                        menu: (base) => ({ 
                          ...base, 
                          zIndex: 99999 
                        }),
                        menuPortal: (base) => ({ 
                          ...base, 
                          zIndex: 99999 
                        })
                      }}
                      menuPortalTarget={document.body}
                      noOptionsMessage={({ inputValue }) => 
                        inputValue ? `No ${selectedEntityType === EntityType.COACH ? 'coaches' : selectedEntityType} found for "${inputValue}"` : `Start typing to search ${selectedEntityType === EntityType.COACH ? 'coaches' : selectedEntityType}`
                      }
                    />
                  </Form.Group>
                )}
              </div>
            )}

            {/* Description */}
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Enter Description"
                disabled={uploading}
              />
            </Form.Group>

            {/* Featured Toggle - Show for Action, Social, and standalone Level Up categories in both create and edit modes */}
            {((category === VideoCategory.ACTION || category === VideoCategory.SOCIAL) || (category === VideoCategory.LEVEL_UP && (isStandaloneLevelUp || (!isEditMode && levelUpContentType === 'standalone')))) && (
              <Form.Group className="mb-3">
                <Form.Label>
                  Featured
                  <span style={{ color: 'red', marginLeft: '4px' }}>*</span>
                </Form.Label>
                <div className="d-flex align-items-center gap-3">
                  <span className={isFeatured ? 'text-muted' : 'fw-bold'}>No</span>
                  <FormCheck
                    type="switch"
                    id="featured-toggle"
                    label={isFeatured ? 'Yes' : 'No'}
                    checked={isFeatured}
                    onChange={(e) => {
                      setIsFeatured(e.target.checked);
                      if (isEditMode) {
                        setHasFormChanges(true);
                      }
                    }}
                    disabled={uploading}
                    style={{ margin: 0 }}
                  />
                  <span className={isFeatured ? 'fw-bold' : 'text-muted'}>Yes</span>
                </div>
              </Form.Group>
            )}

            {/* Schedule Publish - Hide for standalone Level Up videos and published/live Action/Social videos in edit mode */}
            {(() => {
              const isStandaloneLevelUpVideo = category === VideoCategory.LEVEL_UP && isStandaloneLevelUp;
              const isPublishedActionOrSocial = (category === VideoCategory.ACTION || category === VideoCategory.SOCIAL) && 
                isEditMode && 
                (video?.status === 'published' || video?.status === 'live' || video?.publishedAt);
              return !(isStandaloneLevelUpVideo || isPublishedActionOrSocial);
            })() && (
              <>
            <Form.Group className="mb-3">
              <FormCheck
                type="checkbox"
                id="schedule-publish"
                label="Schedule Publish"
                name="schedulePublish"
                checked={formData.schedulePublish}
                onChange={handleInputChange}
                disabled={uploading}
              />
              <Form.Text className="text-muted">
                Pick when you want your video to go live. We'll publish it automatically at that time.
              </Form.Text>
            </Form.Group>

            {formData.schedulePublish && (
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Publish Date</Form.Label>
                    <Form.Control
                      type="date"
                      name="publishDate"
                      value={formData.publishDate}
                      onChange={handleInputChange}
                      disabled={uploading}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Publish Time</Form.Label>
                    <Form.Control
                      type="time"
                      name="publishTime"
                      value={formData.publishTime}
                      onChange={handleInputChange}
                      disabled={uploading}
                    />
                  </Form.Group>
                </Col>
              </Row>
                )}
              </>
            )}
                  </Tab.Pane>
                  ) : null}

                  {/* Series Details Tab - Level Up only - Show when creating or editing a series, NOT for standalone or when editing a chapter */}
                  {category === VideoCategory.LEVEL_UP && !isStandaloneLevelUp && !(isEditMode && (video?.videoType === 'chapter' || (video?.seriesId && video?.videoType !== 'series'))) && (
                    <Tab.Pane eventKey="series-details" key={`series-details-${createdSeriesId || 'new'}`}>
                      {loadingChapters && isEditMode ? (
                        <div className="text-center py-5">
                          <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                          <span>Loading series details...</span>
                        </div>
                      ) : (
                        <div className="form-section">
                          <label className="form-section-label">Series Details</label>
                          
                          {/* Title */}
                          <Form.Group className="mb-3">
                            <Form.Label>
                              Title
                              <span style={{ color: 'red', marginLeft: '4px' }}>*</span>
                            </Form.Label>
                            <Form.Control
                              type="text"
                              name="title"
                              value={seriesData.title || ''}
                              onChange={handleSeriesInputChange}
                              placeholder="Enter Series Title"
                              disabled={isCreatingSeries || uploading}
                            />
                          </Form.Group>

                        {/* Description */}
                        <Form.Group className="mb-3">
                          <Form.Label>Description</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={4}
                            name="description"
                            value={seriesData.description || ''}
                            onChange={handleSeriesInputChange}
                            placeholder="Enter Series Description"
                            disabled={isCreatingSeries || uploading}
                          />
                        </Form.Group>

                        {/* Coach Selection */}
                        <Form.Group className="mb-3">
                          <Form.Label>
                            Coach Name
                            <span style={{ color: 'red', marginLeft: '4px' }}>*</span>
                          </Form.Label>
                          <AsyncSelect
                            cacheOptions
                            defaultOptions={true}
                            loadOptions={(inputValue: string) => loadEntities(inputValue, EntityType.COACH)}
                            value={selectedEntity && selectedEntityType === EntityType.COACH ? selectedEntity : null}
                            onChange={(option: EntityOption | null) => {
                              if (option) {
                                // Automatically set entity type to COACH for series
                                setSelectedEntityType(EntityType.COACH);
                                // Ensure sports is always an array - check both option.sports and option.basicInfo?.sports
                                const sportsArray = Array.isArray(option.sports) 
                                  ? option.sports 
                                  : Array.isArray(option.basicInfo?.sports)
                                  ? option.basicInfo.sports
                                  : [];
                                setSelectedEntity({
                                  ...option,
                                  sports: sportsArray
                                });
                                // Clear coach profile pic file when selecting a new coach
                                setCoachProfilePicFile(null);
                                setCoachProfilePicPreview(null);
                              } else {
                                setSelectedEntity(null);
                                setSelectedEntityType(null);
                                setCoachProfilePicFile(null);
                                setCoachProfilePicPreview(null);
                              }
                            }}
                            onFocus={() => {
                              // Load initial options when dropdown opens
                              if (!selectedEntity || selectedEntityType !== EntityType.COACH) {
                                loadEntities('', EntityType.COACH);
                              }
                            }}
                            placeholder="Search and select a coach"
                            isClearable
                            isDisabled={isCreatingSeries || uploading}
                            classNamePrefix="react-select"
                            menuPosition="fixed"
                            styles={{
                              control: (base) => ({ 
                                ...base, 
                                minHeight: '38px',
                                border: '1px solid #dee2e6',
                                borderRadius: '6px'
                              }),
                              menu: (base) => ({ 
                                ...base, 
                                zIndex: 9999 
                              }),
                              menuPortal: (base) => ({ 
                                ...base, 
                                zIndex: 9999 
                              })
                            }}
                            menuPortalTarget={document.body}
                            getOptionValue={(option: EntityOption) => option.entityId}
                            getOptionLabel={(option: EntityOption) => option.entityName}
                            noOptionsMessage={({ inputValue }) => 
                              inputValue ? `No coaches found for "${inputValue}"` : 'Start typing to search coaches'
                            }
                          />
                        </Form.Group>

                        {/* Sub Category */}
                        <Form.Group className="mb-3">
                          <Form.Label>
                            Sub Category
                            <span style={{ color: 'red', marginLeft: '4px' }}>*</span>
                          </Form.Label>
                          <Form.Control
                            type="text"
                            name="subCategory"
                            value={seriesData.subCategory || ''}
                            onChange={handleSeriesInputChange}
                            placeholder="e.g., technical_skills"
                            disabled={isCreatingSeries || uploading}
                          />
                        </Form.Group>

                        {/* Difficulty Level - Using react-select */}
                        <Form.Group className="mb-3">
                          <Form.Label>
                            Difficulty Level
                            <span style={{ color: 'red', marginLeft: '4px' }}>*</span>
                          </Form.Label>
                          <Select
                            options={[
                              { value: DifficultyLevel.BEGINNER, label: 'Beginner' },
                              { value: DifficultyLevel.INTERMEDIATE, label: 'Intermediate' },
                              { value: DifficultyLevel.ADVANCED, label: 'Advanced' }
                            ]}
                            value={{
                              value: seriesData.difficultyLevel,
                              label: seriesData.difficultyLevel === DifficultyLevel.BEGINNER ? 'Beginner' 
                                : seriesData.difficultyLevel === DifficultyLevel.INTERMEDIATE ? 'Intermediate' 
                                : 'Advanced'
                            }}
                            onChange={(option: any) => {
                              if (option) {
                                setSeriesData(prev => ({ ...prev, difficultyLevel: option.value }));
                                // Mark form as changed in edit mode
                                if (isEditMode) {
                                  setHasFormChanges(true);
                                }
                              }
                            }}
                            isDisabled={isCreatingSeries || uploading}
                            classNamePrefix="react-select"
                            styles={{
                              control: (base) => ({ 
                                ...base, 
                                minHeight: '38px',
                                border: '1px solid #dee2e6',
                                borderRadius: '6px'
                              }),
                              menu: (base) => ({ 
                                ...base, 
                                zIndex: 9999 
                              }),
                              menuPortal: (base) => ({ 
                                ...base, 
                                zIndex: 9999 
                              })
                            }}
                            menuPortalTarget={document.body}
                            placeholder="Select difficulty level"
                          />
                        </Form.Group>

                        {/* Tags */}
                        <Form.Group className="mb-3">
                          <Form.Label>Tags (#)</Form.Label>
                          <CreatableSelect
                            isMulti
                            options={existingTags}
                            value={selectedTags}
                            onChange={(newValue) => {
                  if (isEditMode) {
                    setHasFormChanges(true);
                  }
                  setSelectedTags(newValue);
                }}
                            onCreateOption={(inputValue) => {
                              const newTag = { value: inputValue, label: inputValue };
                              setSelectedTags([...selectedTags, newTag]);
                            }}
                            placeholder="Add tags..."
                            isDisabled={isCreatingSeries || uploading || loadingTags}
                            classNamePrefix="react-select"
                            styles={{
                              control: (base) => ({ 
                                ...base, 
                                minHeight: '38px',
                                border: '1px solid #dee2e6',
                                borderRadius: '6px'
                              }),
                              menu: (base) => ({ 
                                ...base, 
                                zIndex: 9999 
                              }),
                              menuPortal: (base) => ({ 
                                ...base, 
                                zIndex: 9999 
                              })
                            }}
                            menuPortalTarget={document.body}
                            formatCreateLabel={(inputValue) => `Add "${inputValue}"`}
                          />
                        </Form.Group>

                        {/* Featured Toggle - Show for Level Up series */}
                        <Form.Group className="mb-3">
                          <Form.Label>
                            Featured
                            <span style={{ color: 'red', marginLeft: '4px' }}>*</span>
                          </Form.Label>
                          <div className="d-flex align-items-center gap-3">
                            <span className={isFeatured ? 'text-muted' : 'fw-bold'}>No</span>
                            <FormCheck
                              type="switch"
                              id="featured-toggle-series"
                              label={isFeatured ? 'Yes' : 'No'}
                              checked={isFeatured}
                              onChange={(e) => {
                                setIsFeatured(e.target.checked);
                                setSeriesData(prev => ({ ...prev, featured: e.target.checked }));
                                if (isEditMode) {
                                  setHasFormChanges(true);
                                }
                              }}
                              disabled={isCreatingSeries || uploading}
                              style={{ margin: 0 }}
                            />
                            <span className={isFeatured ? 'fw-bold' : 'text-muted'}>Yes</span>
                          </div>
                        </Form.Group>

                        {/* Coach Profile Picture Upload - Only show if Coach is selected and doesn't have profile pic */}
                        {selectedEntity && selectedEntityType === EntityType.COACH && 
                         (!selectedEntity.profilePic || selectedEntity.profilePic === null || selectedEntity.profilePic === 'null' || selectedEntity.profilePic === '') && (
                          <Form.Group className="mb-3">
                            <Form.Label>
                              Coach Profile Picture
                              <span style={{ color: 'red', marginLeft: '4px' }}>*</span>
                            </Form.Label>
                            <div className="video-upload-box">
                              {coachProfilePicPreview ? (
                                <div className="video-preview-container">
                                  <img 
                                    src={coachProfilePicPreview} 
                                    alt="Coach profile preview"
                                    style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '4px', objectFit: 'contain' }}
                                  />
                                  <button 
                                    type="button" 
                                    className="select-file-btn" 
                                    onClick={() => document.getElementById('coach-profile-pic-input-inline')?.click()}
                                    disabled={isCreatingSeries || uploading}
                                    style={{ marginTop: '8px' }}
                                  >
                                    <i className="ph-upload"></i>
                                    Change Image
                                  </button>
                                </div>
                              ) : (
                                <>
                                  <div className="video-upload-placeholder">
                                    <i className="ph-cloud-arrow-up"></i>
                                    <p>Select Coach Profile Picture</p>
                                  </div>
                                  <button 
                                    type="button" 
                                    className="select-file-btn" 
                                    onClick={() => document.getElementById('coach-profile-pic-input-inline')?.click()}
                                    disabled={isCreatingSeries || uploading}
                                  >
                                    <i className="ph-upload"></i>
                                    Select File
                                  </button>
                                </>
                              )}
                              <input
                                id="coach-profile-pic-input-inline"
                                type="file"
                                accept="image/*"
                                onChange={handleCoachProfilePicChange}
                                style={{ display: 'none' }}
                                disabled={isCreatingSeries || uploading}
                              />
                            </div>
                          </Form.Group>
                        )}

                        {/* Cover Image Upload */}
                        <Row>
                          <Col md={12}>
                            <Form.Group className="mb-3">
                              <Form.Label>
                                Cover Image
                                {!(isEditMode && video?.videoType === 'series') && <span style={{ color: 'red', marginLeft: '4px' }}>*</span>}
                              </Form.Label>
                              <div className="video-upload-box">
                                {seriesCoverPreview ? (
                                  <div className="video-preview-container">
                                    <img 
                                      src={seriesCoverPreview} 
                                      alt="Cover preview"
                                      style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '4px', objectFit: 'contain' }}
                                    />
                                    {/* Only show Change Cover button when creating new series or editing chapters, not when editing series */}
                                    {!(isEditMode && video?.videoType === 'series') && (
                                      <button 
                                        type="button" 
                                        className="select-file-btn" 
                                        onClick={() => document.getElementById('series-cover-input-inline')?.click()}
                                        disabled={isCreatingSeries || uploading}
                                        style={{ marginTop: '8px' }}
                                      >
                                        <i className="ph-upload"></i>
                                        Change Cover
                                      </button>
                                    )}
                                  </div>
                                ) : (
                                  <>
                                    {/* Only show upload UI when creating new series or editing chapters, not when editing series */}
                                    {!(isEditMode && video?.videoType === 'series') && (
                                      <>
                                        <div className="video-upload-placeholder">
                                          <i className="ph-cloud-arrow-up"></i>
                                          <p>Select Cover Image</p>
                                        </div>
                                        <button 
                                          type="button" 
                                          className="select-file-btn" 
                                          onClick={() => document.getElementById('series-cover-input-inline')?.click()}
                                          disabled={isCreatingSeries || uploading}
                                        >
                                          <i className="ph-upload"></i>
                                          Select File
                                        </button>
                                      </>
                                    )}
                                  </>
                                )}
                                {/* Only enable file input when creating new series or editing chapters, not when editing series */}
                                {!(isEditMode && video?.videoType === 'series') && (
                                  <input
                                    id="series-cover-input-inline"
                                    type="file"
                                    accept="image/*"
                                    onChange={handleSeriesCoverChange}
                                    style={{ display: 'none' }}
                                    disabled={isCreatingSeries || uploading}
                                  />
                                )}
                              </div>
                            </Form.Group>
                          </Col>
                        </Row>


                        {/* Create Series Button - Only show in create mode */}
                        {!isEditMode && (
                          <div className="d-flex justify-content-end mt-4">
                            <button
                              type="button"
                              className="btn btn-primary"
                              onClick={handleCreateSeries}
                              disabled={isCreatingSeries || !seriesData.title.trim() || !selectedEntity || selectedEntityType !== EntityType.COACH || !seriesData.subCategory.trim() || !(selectedEntity.sports && Array.isArray(selectedEntity.sports) && selectedEntity.sports.length > 0) || !seriesCoverFile}
                            >
                              {isCreatingSeries ? (
                                <>
                                  <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                  Creating...
                                </>
                              ) : (
                                <>
                                  <i className="ph-check me-2"></i>
                                  Create Series
                                </>
                              )}
                            </button>
                          </div>
                        )}

                        {/* Update Series Button - Only show when editing a series (not chapters) */}
                        {isEditMode && createdSeriesId && video?.videoType === 'series' && (
                          <div className="d-flex justify-content-end mt-4">
                            <button
                              type="button"
                              className="btn btn-primary"
                              onClick={handleUpdateSeries}
                              disabled={uploading || !hasFormChanges || !seriesData.title?.trim() || !selectedEntity || selectedEntityType !== EntityType.COACH || !seriesData.subCategory?.trim() || !(selectedEntity.sports && selectedEntity.sports.length > 0)}
                            >
                              {uploading ? (
                                <>
                                  <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                  Updating...
                                </>
                              ) : (
                                <>
                                  <i className="ph-check me-2"></i>
                                  Update Series
                                </>
                              )}
                            </button>
                          </div>
                        )}
                        </div>
                      )}
                    </Tab.Pane>
                  )}

                  {category === VideoCategory.LEVEL_UP && (!isEditMode || (isEditMode && (activeTab === 'chapters' || initialTab === 'chapters') && (createdSeriesId || (initialTab === 'chapters' && video?._id)))) && (
                    <Tab.Pane eventKey="chapters">
                      <div className="form-section">
                        {/* Show "Add New" button - always show it to allow adding multiple chapters */}
                        {!isEditMode || (isEditMode && initialTab !== 'chapters') ? (
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <label className="form-section-label mb-0">Upload Chapter</label>
                          <button
                            type="button"
                            className="btn btn-primary btn-sm"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                addNewChapter();
                              }}
                            disabled={uploadingChapters || !createdSeriesId}
                          >
                            <i className="ph-plus me-2"></i>
                            Add New
                          </button>
                        </div>
                        ) : null}

                        {chapters.length === 0 ? (
                          <div className="text-center py-5 text-muted">
                            <i className="ph-play-circle" style={{ fontSize: '48px' }}></i>
                            <p className="mt-3">
                              {isEditMode && initialTab === 'chapters'
                                ? 'No chapters added yet. Use the "Add New" button above to add chapters.' 
                                : 'No chapters added yet. Click "Add New +" to add a chapter.'}
                            </p>
                          </div>
                        ) : (
                          <div className="chapters-list">
                            {chapters.map((chapter, index) => (
                              <div key={chapter.id} className={`chapter-card mb-4 ${chapter.uploaded ? '' : ''}`} style={{ 
                                backgroundColor: chapter.uploaded ? '#f8fff9' : '#ffffff',
                                border: chapter.uploaded ? '1px solid #d1e7dd' : '1px solid #e0e0e0',
                                borderRadius: '8px',
                                padding: '20px',
                                transition: 'all 0.2s ease',
                                boxShadow: chapter.uploaded ? '0 2px 4px rgba(25, 135, 84, 0.08)' : '0 1px 3px rgba(0,0,0,0.1)'
                              }}>
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                  <div className="d-flex align-items-center gap-2">
                                    <h6 className="mb-0" style={{ fontWeight: 600, fontSize: '16px', color: '#1a1a1a', letterSpacing: '-0.01em' }}>
                                      Chapter {index + 1}
                                    </h6>
                                    {chapter.uploaded && (
                                      <span className="badge rounded-pill" style={{ 
                                        fontSize: '11px', 
                                        padding: '4px 10px',
                                        backgroundColor: '#198754',
                                        fontWeight: 500
                                      }}>
                                        <i className="ph-check me-1"></i>
                                        Uploaded
                                      </span>
                                    )}
                                    {chapter.uploading && (
                                      <span className="badge rounded-pill" style={{ 
                                        fontSize: '11px', 
                                        padding: '4px 10px',
                                        backgroundColor: '#ffc107',
                                        color: '#000',
                                        fontWeight: 500
                                      }}>
                                        <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" style={{ width: '0.7rem', height: '0.7rem' }}></span>
                                        Uploading
                                      </span>
                                    )}
                                  </div>
                                  {!chapter.uploaded && (
                                  <button
                                    type="button"
                                      className="btn btn-link text-danger p-0"
                                    onClick={() => removeChapter(chapter.id)}
                                    disabled={chapter.uploading || uploadingChapters}
                                      title="Remove chapter"
                                      style={{ 
                                        fontSize: '16px',
                                        width: '24px',
                                        height: '24px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        textDecoration: 'none'
                                      }}
                                      onMouseEnter={(e) => e.currentTarget.style.opacity = '0.7'}
                                      onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                                  >
                                    <i className="ph-trash"></i>
                                  </button>
                                  )}
                                </div>

                                {/* Chapter Title */}
                                <Form.Group className="mb-3">
                                  <Form.Label style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px', color: '#495057', letterSpacing: '0.01em' }}>Title</Form.Label>
                                  <Form.Control
                                    type="text"
                                    value={chapter.title}
                                    onChange={(e) => updateChapter(chapter.id, 'title', e.target.value)}
                                    placeholder="Enter chapter title"
                                    disabled={chapter.uploading || uploadingChapters || chapter.uploaded}
                                    style={{ 
                                      fontSize: '14px', 
                                      padding: '8px 14px', 
                                      height: '40px',
                                      border: '1px solid #ced4da',
                                      borderRadius: '6px',
                                      transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out'
                                    }}
                                    onFocus={(e) => {
                                      e.target.style.borderColor = '#86b7fe';
                                      e.target.style.boxShadow = '0 0 0 0.25rem rgba(13, 110, 253, 0.15)';
                                    }}
                                    onBlur={(e) => {
                                      e.target.style.borderColor = '#ced4da';
                                      e.target.style.boxShadow = 'none';
                                    }}
                                  />
                                </Form.Group>

                                {/* Chapter Description */}
                                <Form.Group className="mb-3">
                                  <Form.Label style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px', color: '#495057', letterSpacing: '0.01em' }}>Description</Form.Label>
                                  <Form.Control
                                    as="textarea"
                                    rows={3}
                                    value={chapter.description}
                                    onChange={(e) => updateChapter(chapter.id, 'description', e.target.value)}
                                    placeholder="Enter chapter description"
                                    disabled={chapter.uploading || uploadingChapters}
                                    style={{ 
                                      fontSize: '14px', 
                                      padding: '8px 14px', 
                                      resize: 'vertical',
                                      border: '1px solid #ced4da',
                                      borderRadius: '6px',
                                      transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
                                      lineHeight: '1.5'
                                    }}
                                    onFocus={(e) => {
                                      e.target.style.borderColor = '#86b7fe';
                                      e.target.style.boxShadow = '0 0 0 0.25rem rgba(13, 110, 253, 0.15)';
                                    }}
                                    onBlur={(e) => {
                                      e.target.style.borderColor = '#ced4da';
                                      e.target.style.boxShadow = 'none';
                                    }}
                                  />
                                </Form.Group>

                                {/* Chapter Video and Thumbnail */}
                                <Row className="g-3">
                                  <Col md={6}>
                                    <Form.Group className="mb-3">
                                      <Form.Label style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px', color: '#495057', letterSpacing: '0.01em' }}>Video</Form.Label>
                                      <div 
                                        className="video-upload-box" 
                                        style={{ 
                                          minHeight: '140px', 
                                          padding: '20px',
                                          border: '2px dashed #dee2e6',
                                          borderRadius: '8px',
                                          backgroundColor: '#f8f9fa',
                                          transition: 'all 0.2s ease',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center'
                                        }}
                                        onMouseEnter={(e) => {
                                          if (!chapter.videoPreview) {
                                            e.currentTarget.style.borderColor = '#86b7fe';
                                            e.currentTarget.style.backgroundColor = '#f0f7ff';
                                          }
                                        }}
                                        onMouseLeave={(e) => {
                                          if (!chapter.videoPreview) {
                                            e.currentTarget.style.borderColor = '#dee2e6';
                                            e.currentTarget.style.backgroundColor = '#f8f9fa';
                                          }
                                        }}
                                      >
                                        {chapter.videoPreview ? (
                                          <div className="video-preview-container w-100">
                                            <video 
                                              src={chapter.videoPreview} 
                                              controls 
                                              className="video-preview w-100"
                                              style={{ maxHeight: '140px', borderRadius: '6px', marginBottom: '10px' }}
                                            />
                                            <button 
                                              type="button" 
                                              className="btn btn-outline-primary btn-sm w-100" 
                                              onClick={() => document.getElementById(`chapter-video-inline-${chapter.id}`)?.click()}
                                              disabled={chapter.uploading || uploadingChapters || chapter.uploaded}
                                              style={{ fontSize: '13px', padding: '6px 12px' }}
                                            >
                                              <i className="ph-pencil-simple me-1"></i>
                                              Change Video
                                            </button>
                                          </div>
                                        ) : (
                                          <button 
                                            type="button" 
                                            className="btn btn-primary" 
                                            onClick={() => document.getElementById(`chapter-video-inline-${chapter.id}`)?.click()}
                                            disabled={chapter.uploading || uploadingChapters || chapter.uploaded}
                                            style={{ 
                                              padding: '10px 20px', 
                                              fontSize: '14px',
                                              fontWeight: 500,
                                              borderRadius: '6px',
                                              border: 'none'
                                            }}
                                          >
                                            <i className="ph-upload me-2"></i>
                                            Select Video
                                          </button>
                                        )}
                                        <input
                                          id={`chapter-video-inline-${chapter.id}`}
                                          type="file"
                                          accept="video/mp4,video/quicktime,.mp4,.mov"
                                          onChange={(e) => handleChapterVideoChange(chapter.id, e)}
                                          style={{ display: 'none' }}
                                          disabled={chapter.uploading || uploadingChapters || chapter.uploaded}
                                        />
                                      </div>
                                    </Form.Group>
                                  </Col>
                                  <Col md={6}>
                                    <Form.Group className="mb-3">
                                      <Form.Label style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px', color: '#495057', letterSpacing: '0.01em' }}>Thumbnail</Form.Label>
                                      <div 
                                        className="video-upload-box" 
                                        style={{ 
                                          minHeight: '140px', 
                                          padding: '20px',
                                          border: '2px dashed #dee2e6',
                                          borderRadius: '8px',
                                          backgroundColor: '#f8f9fa',
                                          transition: 'all 0.2s ease',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center'
                                        }}
                                        onMouseEnter={(e) => {
                                          if (!chapter.thumbnailPreview) {
                                            e.currentTarget.style.borderColor = '#86b7fe';
                                            e.currentTarget.style.backgroundColor = '#f0f7ff';
                                          }
                                        }}
                                        onMouseLeave={(e) => {
                                          if (!chapter.thumbnailPreview) {
                                            e.currentTarget.style.borderColor = '#dee2e6';
                                            e.currentTarget.style.backgroundColor = '#f8f9fa';
                                          }
                                        }}
                                      >
                                        {chapter.thumbnailPreview ? (
                                          <div className="video-preview-container w-100 text-center">
                                            <img 
                                              src={chapter.thumbnailPreview} 
                                              alt="Thumbnail"
                                              style={{ 
                                                maxWidth: '100%', 
                                                maxHeight: '140px', 
                                                borderRadius: '6px', 
                                                objectFit: 'contain',
                                                marginBottom: '10px',
                                                border: '1px solid #e0e0e0'
                                              }}
                                            />
                                            <button 
                                              type="button" 
                                              className="btn btn-outline-primary btn-sm w-100" 
                                              onClick={() => document.getElementById(`chapter-thumbnail-inline-${chapter.id}`)?.click()}
                                              disabled={chapter.uploading || uploadingChapters || chapter.uploaded}
                                              style={{ fontSize: '13px', padding: '6px 12px' }}
                                            >
                                              <i className="ph-pencil-simple me-1"></i>
                                              Change Thumbnail
                                            </button>
                                          </div>
                                        ) : (
                                          <button 
                                            type="button" 
                                            className="btn btn-primary" 
                                            onClick={() => document.getElementById(`chapter-thumbnail-inline-${chapter.id}`)?.click()}
                                            disabled={chapter.uploading || uploadingChapters || chapter.uploaded}
                                            style={{ 
                                              padding: '10px 20px', 
                                              fontSize: '14px',
                                              fontWeight: 500,
                                              borderRadius: '6px',
                                              border: 'none'
                                            }}
                                          >
                                            <i className="ph-upload me-2"></i>
                                            Select Thumbnail
                                          </button>
                                        )}
                                        <input
                                          id={`chapter-thumbnail-inline-${chapter.id}`}
                                          type="file"
                                          accept="image/*"
                                          onChange={(e) => handleChapterThumbnailChange(chapter.id, e)}
                                          style={{ display: 'none' }}
                                          disabled={chapter.uploading || uploadingChapters || chapter.uploaded}
                                        />
                                      </div>
                                    </Form.Group>
                                  </Col>
                                </Row>

                                {/* Add Chapter Button */}
                                {!chapter.uploaded && (
                                  <div className="d-flex justify-content-end mt-3 pt-3" style={{ borderTop: '1px solid #e9ecef' }}>
                                    <button
                                      type="button"
                                      className="btn btn-primary"
                                      onClick={() => handleSingleChapterUpload(chapter.id)}
                                      disabled={
                                        chapter.uploading || 
                                        uploadingChapters || 
                                        !chapter.title.trim() || 
                                        !chapter.videoFile || 
                                        !chapter.thumbnailFile ||
                                        (!createdSeriesId && !video?._id)
                                      }
                                      style={{ 
                                        padding: '8px 20px', 
                                        fontSize: '14px',
                                        fontWeight: 500,
                                        borderRadius: '6px',
                                        minWidth: '140px'
                                      }}
                                    >
                                      {chapter.uploading ? (
                                        <>
                                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" style={{ width: '1rem', height: '1rem' }}></span>
                                          Uploading...
                                        </>
                                      ) : (
                                        <>
                                          <i className="ph-upload me-2"></i>
                                          Upload Chapter
                                        </>
                                      )}
                                    </button>
                                  </div>
                                )}
                              </div>
                            ))}
                            {/* Action buttons: Add New, Submit All, Refresh List */}
                            <div className="mt-4 pt-4" style={{ borderTop: '2px solid #e9ecef' }}>
                              <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                                <div className="d-flex align-items-center gap-2">
                                  {hasUploadedChapters && initialTab === 'chapters' && (
                                    <button
                                      type="button"
                                      className="btn btn-outline-secondary"
                                      onClick={() => {
                                        onChapterAdded?.();
                                        toast.info('Refreshing chapters list...');
                                      }}
                                      disabled={uploadingChapters}
                                      style={{ 
                                        padding: '7px 16px', 
                                        fontSize: '14px',
                                        fontWeight: 500,
                                        borderRadius: '6px'
                                      }}
                                    >
                                      <i className="ph-arrow-clockwise me-2"></i>
                                      Refresh List
                                    </button>
                                  )}
                                  <button
                                    type="button"
                                    className="btn btn-outline-primary"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      addNewChapter();
                                    }}
                                    disabled={uploadingChapters || (!createdSeriesId && !video?._id)}
                                    style={{ 
                                      padding: '7px 16px', 
                                      fontSize: '14px',
                                      fontWeight: 500,
                                      borderRadius: '6px'
                                    }}
                                  >
                                    <i className="ph-plus me-2"></i>
                                    Add Another Chapter
                                  </button>
                                </div>
                                {chapters.filter(ch => !ch.uploaded).length > 0 && (
                                  <button
                                    type="button"
                                    className="btn btn-success"
                                    onClick={handleBulkChaptersUpload}
                                    disabled={uploadingChapters || (!createdSeriesId && !video?._id)}
                                    style={{ 
                                      padding: '8px 24px', 
                                      fontSize: '14px',
                                      fontWeight: 600,
                                      borderRadius: '6px',
                                      boxShadow: '0 2px 4px rgba(25, 135, 84, 0.2)'
                                    }}
                                  >
                                    {uploadingChapters ? (
                                      <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" style={{ width: '1rem', height: '1rem' }}></span>
                                        Uploading All...
                                      </>
                                    ) : (
                                      <>
                                        <i className="ph-upload me-2"></i>
                                        Upload All ({chapters.filter(ch => !ch.uploaded).length})
                                      </>
                                    )}
                                  </button>
                                )}
                              </div>
                              {chapters.filter(ch => !ch.uploaded).length > 0 && (
                                <p className="text-muted mb-0 mt-3" style={{ fontSize: '13px', fontStyle: 'italic' }}>
                                  <i className="ph-info me-1"></i>
                                  {chapters.filter(ch => !ch.uploaded).length} chapter{chapters.filter(ch => !ch.uploaded).length !== 1 ? 's' : ''} ready to upload
                                </p>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </Tab.Pane>
                  )}

                  {/* Chapters Tab - Level Up edit mode only - List View (only show when editing a series that was just created, not existing series) */}
                  {/* Don't show this when opened from detail page with initialTab='chapters' - use repeater field instead */}
                  {category === VideoCategory.LEVEL_UP && isEditMode && createdSeriesId && initialTab !== 'chapters' && (
                    <Tab.Pane eventKey="chapters">
                      <div className="form-section">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <label className="form-section-label mb-0">Upload Chapter</label>
                          <button
                            type="button"
                            className="btn btn-primary btn-sm"
                            onClick={() => {
                              setFormData({
                                title: '',
                                description: '',
                                schedulePublish: false,
                                publishDate: '',
                                publishTime: ''
                              });
                              setVideoFile(null);
                              setVideoPreview(null);
                              setThumbnailFile(null);
                              setThumbnailPreview(null);
                              setIsPortrait(false);
                              setSelectedChapterForEdit(null);
                              setOriginalChapterData(null);
                              setHasChapterChanges(false);
                              setShowAddChapterForm(true);
                            }}
                            disabled={uploadingChapters}
                          >
                            <i className="ph-plus me-2"></i>
                            Add New
                          </button>
                        </div>

                        {loadingChapters ? (
                          <div className="text-center py-4">
                            <div className="spinner-border spinner-border-sm" role="status">
                              <span className="visually-hidden">Loading chapters...</span>
                            </div>
                          </div>
                        ) : (
                          <>
                            {showAddChapterForm && (
                              <div className="mb-4 p-4 border rounded bg-light">
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                  <label className="form-section-label mb-0">
                                    {selectedChapterForEdit ? 'Edit Chapter' : 'Add New Chapter'}
                                  </label>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-link text-muted p-0"
                                    onClick={() => {
                                      setShowAddChapterForm(false);
                                      setSelectedChapterForEdit(null);
                                      setOriginalChapterData(null);
                                      setHasChapterChanges(false);
                                      setFormData({
                                        title: '',
                                        description: '',
                                        schedulePublish: false,
                                        publishDate: '',
                                        publishTime: ''
                                      });
                                      setVideoFile(null);
                                      setVideoPreview(null);
                                      setThumbnailFile(null);
                                      setThumbnailPreview(null);
                                      setIsPortrait(false);
                                    }}
                                  >
                                    <i className="ph-x" style={{ fontSize: '20px' }}></i>
                                  </button>
                                </div>

                        <Row>
                          <Col md={6}>
                            <div className="form-section">
                              <label className="form-section-label">
                                Video Upload
                                <span style={{ color: 'red', marginLeft: '4px' }}>*</span>
                              </label>
                              <div className="video-upload-box">
                                {videoPreview ? (
                                  <div className="video-preview-container">
                                    <video 
                                      src={videoPreview} 
                                      controls 
                                      className="video-preview"
                                      style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '4px' }}
                                    />
                                    <div className="selected-file-info">
                                      <i className="ph-file-video"></i>
                                      <span>{videoFile?.name || 'Existing video'}</span>
                                      {videoFile && <span className="file-size">({(videoFile.size / (1024 * 1024)).toFixed(2)} MB)</span>}
                                    </div>
                                    <button 
                                      type="button" 
                                      className="select-file-btn" 
                                              onClick={() => document.getElementById('chapter-video-file-input-inline')?.click()}
                                      disabled={uploading}
                                      style={{ marginTop: '8px' }}
                                    >
                                      <i className="ph-upload"></i>
                                      Change Video
                                    </button>
                                  </div>
                                ) : (
                                  <>
                                    <div className="video-upload-placeholder">
                                      <i className="ph-cloud-arrow-up"></i>
                                      <p>Select Video to Upload</p>
                                      <p className="video-upload-hint">Supported Format: MP4, MOV (Max: 2GB)</p>
                                    </div>
                                    <button 
                                      type="button" 
                                      className="select-file-btn" 
                                              onClick={() => document.getElementById('chapter-video-file-input-inline')?.click()}
                                      disabled={uploading}
                                    >
                                      <i className="ph-upload"></i>
                                      Select File
                                    </button>
                                  </>
                                )}
                                <input
                                          id="chapter-video-file-input-inline"
                                  type="file"
                                  accept="video/mp4,video/quicktime,.mp4,.mov"
                                  onChange={handleVideoFileChange}
                                  style={{ display: 'none' }}
                                  disabled={uploading}
                                />
                              </div>
                              
                              {(videoFile || videoPreview) && (
                                <Form.Group className="mb-3 mt-3">
                                  <Form.Label>
                                    Video Orientation
                                    <span style={{ color: 'red', marginLeft: '4px' }}>*</span>
                                  </Form.Label>
                                  <div className="d-flex align-items-center gap-3">
                                    <span className={isPortrait ? 'text-muted' : 'fw-bold'}>Landscape</span>
                                    <FormCheck
                                      type="switch"
                                              id="chapter-portrait-toggle-inline"
                                      label={isPortrait ? 'Portrait' : 'Landscape'}
                                      checked={isPortrait}
                                      onChange={(e) => setIsPortrait(e.target.checked)}
                                      disabled={uploading}
                                      style={{ margin: 0 }}
                                    />
                                    <span className={isPortrait ? 'fw-bold' : 'text-muted'}>Portrait</span>
                                  </div>
                                </Form.Group>
                              )}
                            </div>
                          </Col>
                          <Col md={6}>
                            <div className="form-section">
                              <label className="form-section-label">
                                Video Thumbnail
                                <span style={{ color: 'red', marginLeft: '4px' }}>*</span>
                              </label>
                              <div className="video-upload-box">
                                {thumbnailPreview ? (
                                  <div className="video-preview-container">
                                    <img 
                                      src={thumbnailPreview} 
                                      alt="Thumbnail preview"
                                      style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '4px', objectFit: 'contain' }}
                                    />
                                    <div className="selected-file-info">
                                      <i className="ph-image"></i>
                                      <span>{thumbnailFile?.name || 'Existing thumbnail'}</span>
                                      {thumbnailFile && <span className="file-size">({(thumbnailFile.size / (1024 * 1024)).toFixed(2)} MB)</span>}
                                    </div>
                                    <button 
                                      type="button" 
                                      className="select-file-btn" 
                                              onClick={() => document.getElementById('chapter-thumbnail-input-inline')?.click()}
                                      disabled={uploading}
                                      style={{ marginTop: '8px' }}
                                    >
                                      <i className="ph-upload"></i>
                                      Change Thumbnail
                                    </button>
                                  </div>
                                ) : (
                                  <>
                                    <div className="video-upload-placeholder">
                                      <i className="ph-cloud-arrow-up"></i>
                                      <p>Select Thumbnail to Upload</p>
                                      <p className="video-upload-hint">Supported Format: JPG, PNG, GIF (Max: 50MB)</p>
                                    </div>
                                    <button 
                                      type="button" 
                                      className="select-file-btn" 
                                              onClick={() => document.getElementById('chapter-thumbnail-input-inline')?.click()}
                                      disabled={uploading}
                                    >
                                      <i className="ph-upload"></i>
                                      Select File
                                    </button>
                                  </>
                                )}
                                <input
                                  type="file"
                                  accept="image/*"
                                  style={{ display: 'none' }}
                                          id="chapter-thumbnail-input-inline"
                                  onChange={handleThumbnailFileChange}
                                  disabled={uploading}
                                />
                              </div>
                            </div>
                          </Col>
                        </Row>

                        <Form.Group className="mb-3">
                          <Form.Label>
                            Chapter Title
                            <span style={{ color: 'red', marginLeft: '4px' }}>*</span>
                          </Form.Label>
                          <Form.Control
                            type="text"
                            name="title"
                            value={formData.title}
                            onChange={handleInputChange}
                            placeholder="Enter Chapter Title"
                            required
                            disabled={uploading}
                          />
                        </Form.Group>

                        <Form.Group className="mb-3">
                          <Form.Label>
                            Description
                            <span style={{ color: 'red', marginLeft: '4px' }}>*</span>
                          </Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={4}
                            name="description"
                            value={formData.description}
                            onChange={handleInputChange}
                            placeholder="Enter Chapter Description"
                            required
                            disabled={uploading}
                          />
                        </Form.Group>

                                <div className="d-flex justify-content-end gap-2">
                                  <button
                                    type="button"
                                    className="btn btn-secondary btn-sm"
                                    onClick={() => {
                                      setShowAddChapterForm(false);
                                      setSelectedChapterForEdit(null);
                                      setOriginalChapterData(null);
                                      setHasChapterChanges(false);
                                      setFormData({
                                        title: '',
                                        description: '',
                                        schedulePublish: false,
                                        publishDate: '',
                                        publishTime: ''
                                      });
                                      setVideoFile(null);
                                      setVideoPreview(null);
                                      setThumbnailFile(null);
                                      setThumbnailPreview(null);
                                      setIsPortrait(false);
                                    }}
                                    disabled={uploading}
                                  >
                                    Cancel
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-primary btn-sm"
                                    onClick={async () => {
                                      if (!formData.title.trim()) {
                                        toast.error('Chapter title is required');
                                        return;
                                      }
                                      
                                      // For new chapters, video and thumbnail are required
                                      if (!selectedChapterForEdit) {
                                        if (!videoFile && !videoPreview) {
                                          toast.error('Video is required');
                                          return;
                                        }
                                        if (!thumbnailFile && !thumbnailPreview) {
                                          toast.error('Thumbnail is required');
                                          return;
                                        }
                                      }
                                      
                                      // Use video._id if createdSeriesId is not set (for series editing)
                                      const seriesIdToUse = createdSeriesId || (video?.videoType === 'series' ? video._id : null);
                                      if (!seriesIdToUse) {
                                        toast.error('Series ID is missing');
                                        return;
                                      }

                                      setUploading(true);
                                      try {
                                        if (selectedChapterForEdit) {
                                          // Update existing chapter
                                          const updatePayload: any = {
                                            title: formData.title.trim(),
                                            description: formData.description.trim(),
                                            isPortrait: isPortrait
                                          };

                                          // If new video file is selected, upload it
                                          if (videoFile) {
                                            const videoContentType = getVideoContentType(videoFile);
                                            const thumbnailContentType = thumbnailFile 
                                              ? (thumbnailFile.type.split('/')[1] || 'png') 
                                              : 'png';
                                            
                                            const uploadUrls = await communityService.generateUploadUrls(
                                              videoContentType,
                                              thumbnailContentType
                                            );

                                            const videoMimeType = videoFile.type || (videoContentType === 'mov' ? 'video/quicktime' : 'video/mp4');
                                            await communityService.uploadToS3(uploadUrls.videoUploadUrl, videoFile, videoMimeType);
                                            updatePayload.videoId = uploadUrls.videoId;
                                            if (uploadUrls.videoKey) {
                                              updatePayload.videoKey = uploadUrls.videoKey;
                                            }
                                          }

                                          // If new thumbnail file is selected, upload it
                                          if (thumbnailFile) {
                                            const thumbnailContentType = thumbnailFile.type.split('/')[1] || 'png';
                                            const videoContentType = videoFile 
                                              ? getVideoContentType(videoFile) 
                                              : 'mp4';
                                            
                                            const uploadUrls = await communityService.generateUploadUrls(
                                              videoContentType,
                                              thumbnailContentType
                                            );

                                            await communityService.uploadToS3(uploadUrls.thumbnailUploadUrl, thumbnailFile, thumbnailFile.type);
                                            if (uploadUrls.thumbnailKey) {
                                              updatePayload.thumbnailKey = uploadUrls.thumbnailKey;
                                            }
                                          }

                                          // Call update API
                                          await communityService.updateVideo(selectedChapterForEdit._id, updatePayload);
                                          toast.success('Chapter updated successfully');
                                          
                                          // Reload chapters after update
                                          if (seriesIdToUse) {
                                            setTimeout(async () => {
                                              try {
                                                await reloadSeriesData(seriesIdToUse);
                                              } catch (error) {
                                                console.error('Error reloading chapters after update:', error);
                                              }
                                            }, 500);
                                          }
                                        } else {
                                          // Create new chapter
                                          const videoContentType = videoFile ? getVideoContentType(videoFile) : 'mp4';
                                          const thumbnailContentType = thumbnailFile ? (thumbnailFile.type.split('/')[1] || 'png') : 'png';

                                          // Step 1: Generate presigned URLs
                                          const uploadUrls = await communityService.generateUploadUrls(
                                            videoContentType,
                                            thumbnailContentType
                                          );

                                          // Step 2: Upload video to S3
                                          if (videoFile && uploadUrls.videoUploadUrl) {
                                            const videoMimeType = videoFile.type || (videoContentType === 'mov' ? 'video/quicktime' : 'video/mp4');
                                            await communityService.uploadToS3(
                                              uploadUrls.videoUploadUrl,
                                              videoFile,
                                              videoMimeType
                                            );
                                          }

                                          // Step 3: Upload thumbnail to S3
                                          if (thumbnailFile && uploadUrls.thumbnailUploadUrl) {
                                            await communityService.uploadToS3(
                                              uploadUrls.thumbnailUploadUrl,
                                              thumbnailFile,
                                              thumbnailFile.type
                                            );
                                          }

                                          // Step 4: Get next chapter number
                                          const nextChapterNumber = chapters.length > 0 
                                            ? Math.max(...chapters.map((ch: any) => ch.chapterNumber || 0)) + 1
                                            : 1;

                                          // Step 5: Complete upload with series ID
                                          const tags = selectedTags.map(tag => tag.value);
                                          const payload: any = {
                                            videoId: uploadUrls.videoId,
                                            title: formData.title.trim(),
                                            description: formData.description.trim(),
                                            category: 'level_up',
                                            contentType: 'long_form',
                                            mediaType: 'video',
                                            videoType: 'chapter',
                                            tags: tags,
                                            uploadedBy: user?._id || user?.id || '',
                                            uploadedByRole: user?.role || 'admin',
                                            isPortrait: isPortrait,
                                            seriesId: seriesIdToUse,
                                            chapterNumber: nextChapterNumber
                                          };

                                          // Add metadata if entity is selected
                                          if (selectedEntity && selectedEntityType) {
                                            const baseMetadata = {
                                              id: selectedEntityType === EntityType.SFL_USER ? 'sfl' : selectedEntity.entityId,
                                              type: selectedEntityType,
                                              userName: selectedEntityType === EntityType.SFL_USER ? 'sfl' : selectedEntity.entityName,
                                              userProfilePic: selectedEntityType === EntityType.SFL_USER ? 'shared/sfl-logo.png' : (selectedEntity.profilePic || undefined)
                                            };

                                            if (selectedEntityType === EntityType.COACH) {
                                              // CRITICAL: Use the same logic as handleSingleChapterUpload to construct basicInfo
                                              let basicInfo = selectedEntity.basicInfo || {};
                                              
                                              // If basicInfo is empty, construct from selectedEntity properties
                                              if (!basicInfo || (typeof basicInfo === 'object' && Object.keys(basicInfo).length === 0)) {
                                                basicInfo = {
                                                  fullName: selectedEntity.entityName || '',
                                                  profilePic: selectedEntity.profilePic || null,
                                                  sports: Array.isArray(selectedEntity.sports) ? selectedEntity.sports : [],
                                                  dob: null,
                                                  email: null,
                                                  ageCategory: [],
                                                  certification: [],
                                                  experience: null,
                                                  _id: selectedEntity.entityId || selectedEntity.value || ''
                                                };
                                              }
                                              
                                              // Get values, prioritizing basicInfo but falling back to selectedEntity properties
                                              const sports = (basicInfo && Array.isArray(basicInfo.sports) && basicInfo.sports.length > 0)
                                                ? basicInfo.sports 
                                                : (Array.isArray(selectedEntity.sports) && selectedEntity.sports.length > 0 
                                                  ? selectedEntity.sports 
                                                  : []);
                                              
                                              const profilePic = (basicInfo && basicInfo.profilePic) || selectedEntity.profilePic || null;
                                              const fullName = (basicInfo && basicInfo.fullName) || selectedEntity.entityName || '';
                                              const coachId = (basicInfo && basicInfo._id) || selectedEntity.entityId || selectedEntity.value || '';
                                              
                                              // Construct complete basicInfo ensuring all expected fields are present
                                              const basicInfoComplete: any = {
                                                fullName: fullName || selectedEntity.entityName || '',
                                                profilePic: profilePic || selectedEntity.profilePic || null,
                                                sports: sports.length > 0 ? sports : (Array.isArray(selectedEntity.sports) ? selectedEntity.sports : []),
                                                dob: (basicInfo && basicInfo.dob !== undefined) ? basicInfo.dob : null,
                                                email: (basicInfo && basicInfo.email !== undefined) ? basicInfo.email : null,
                                                ageCategory: (basicInfo && Array.isArray(basicInfo.ageCategory)) ? basicInfo.ageCategory : [],
                                                certification: (basicInfo && Array.isArray(basicInfo.certification)) ? basicInfo.certification : [],
                                                experience: (basicInfo && basicInfo.experience !== undefined) ? basicInfo.experience : null,
                                                _id: coachId
                                              };
                                              
                                              // Only add location if it exists (optional field)
                                              if (basicInfo && basicInfo.location !== undefined) {
                                                basicInfoComplete.location = basicInfo.location;
                                              }
                                              
                                              // Preserve any other fields from basicInfo
                                              if (basicInfo && typeof basicInfo === 'object' && !Array.isArray(basicInfo)) {
                                                Object.keys(basicInfo).forEach(key => {
                                                  if (!basicInfoComplete.hasOwnProperty(key) && basicInfo[key] !== undefined) {
                                                    basicInfoComplete[key] = basicInfo[key];
                                                  }
                                                });
                                              }
                                              
                                              // Final validation: Ensure basicInfoComplete is never empty
                                              const requiredFields = ['fullName', 'profilePic', 'sports', 'dob', 'email', 'ageCategory', 'certification', 'experience', '_id'];
                                              const missingFields = requiredFields.filter(field => !basicInfoComplete.hasOwnProperty(field));
                                              
                                              if (missingFields.length > 0 || Object.keys(basicInfoComplete).length === 0) {
                                                console.error('ERROR: basicInfoComplete is missing fields! Missing:', missingFields);
                                                missingFields.forEach(field => {
                                                  if (field === 'fullName') basicInfoComplete.fullName = selectedEntity.entityName || '';
                                                  else if (field === 'profilePic') basicInfoComplete.profilePic = selectedEntity.profilePic || null;
                                                  else if (field === 'sports') basicInfoComplete.sports = Array.isArray(selectedEntity.sports) ? selectedEntity.sports : [];
                                                  else if (field === 'dob') basicInfoComplete.dob = null;
                                                  else if (field === 'email') basicInfoComplete.email = null;
                                                  else if (field === 'ageCategory') basicInfoComplete.ageCategory = [];
                                                  else if (field === 'certification') basicInfoComplete.certification = [];
                                                  else if (field === 'experience') basicInfoComplete.experience = null;
                                                  else if (field === '_id') basicInfoComplete._id = selectedEntity.entityId || selectedEntity.value || '';
                                                });
                                              }
                                              
                                              // Verify the object is not empty before using it
                                              if (Object.keys(basicInfoComplete).length === 0) {
                                                console.error('CRITICAL ERROR: basicInfoComplete is still empty! Using emergency fallback');
                                                basicInfoComplete.fullName = selectedEntity.entityName || 'Unknown';
                                                basicInfoComplete.profilePic = selectedEntity.profilePic || null;
                                                basicInfoComplete.sports = Array.isArray(selectedEntity.sports) ? selectedEntity.sports : [];
                                                basicInfoComplete.dob = null;
                                                basicInfoComplete.email = null;
                                                basicInfoComplete.ageCategory = [];
                                                basicInfoComplete.certification = [];
                                                basicInfoComplete.experience = null;
                                                basicInfoComplete._id = selectedEntity.entityId || selectedEntity.value || '';
                                              }
                                              
                                              payload.metadata = {
                                                ...baseMetadata,
                                                basicInfo: basicInfoComplete
                                              };
                                            } else {
                                              payload.metadata = baseMetadata;
                                            }
                                          }

                                          // Add S3 keys
                                          if (uploadUrls.videoKey) {
                                            payload.videoKey = uploadUrls.videoKey;
                                          }
                                          if (uploadUrls.thumbnailKey) {
                                            payload.thumbnailKey = uploadUrls.thumbnailKey;
                                          }

                                          const chapterResponse = await communityService.completeUpload(payload);
                                          toast.success('Chapter added successfully');
                                          
                                          // Optimistically add the chapter to the list if we have the response data
                                          if (chapterResponse && chapterResponse._id) {
                                            const newChapter = {
                                              _id: chapterResponse._id,
                                              title: formData.title.trim(),
                                              description: formData.description.trim(),
                                              chapterNumber: nextChapterNumber,
                                              thumbnailUrl: uploadUrls.thumbnailKey || chapterResponse.thumbnailUrl,
                                              contentUrl: uploadUrls.videoKey || chapterResponse.contentUrl,
                                              isPortrait: isPortrait,
                                              status: chapterResponse.status || 'draft'
                                            };
                                            setChapters(prev => [...prev, newChapter]);
                                          }
                                        }

                                        // Reset form
                                        setShowAddChapterForm(false);
                                        setSelectedChapterForEdit(null);
                                        setOriginalChapterData(null);
                                        setHasChapterChanges(false);
                                        setFormData({
                                          title: '',
                                          description: '',
                                          schedulePublish: false,
                                          publishDate: '',
                                          publishTime: ''
                                        });
                                        setVideoFile(null);
                                        setVideoPreview(null);
                                        setThumbnailFile(null);
                                        setThumbnailPreview(null);
                                        setIsPortrait(false);

                                        // Reload series data to get updated chapters list
                                        if (seriesIdToUse) {
                                          setTimeout(async () => {
                                            await reloadSeriesData(seriesIdToUse);
                                          }, 500);
                                        }
                                        
                                        // Trigger success callback to refresh the listing (form stays open for adding more chapters)
                                        // Use setTimeout to ensure state updates are complete before triggering refresh
                                        setTimeout(() => {
                                          onSuccess?.();
                                        }, 100);
                                      } catch (error: any) {
                                        console.error('Error saving chapter:', error);
                                        toast.error(error?.message || 'Failed to save chapter');
                                      } finally {
                                        setUploading(false);
                                      }
                                    }}
                                    disabled={uploading || !formData.title.trim() || !formData.description.trim() || 
                                      ((selectedChapterForEdit || (isEditMode && category === VideoCategory.LEVEL_UP && (video?.videoType === 'chapter' || (video?.seriesId && video?.videoType !== 'series')))) && !hasChapterChanges)}
                                  >
                                    {uploading ? 'Saving...' : (selectedChapterForEdit || (isEditMode && category === VideoCategory.LEVEL_UP && (video?.videoType === 'chapter' || (video?.seriesId && video?.videoType !== 'series')))) ? 'Update Chapter' : 'Add Chapter'}
                                  </button>
                                </div>
                              </div>
                            )}
                            <div className="chapters-list">
                              {chapters.map((chapter: any, index: number) => (
                              <div 
                                key={chapter._id || chapter.id} 
                                className="mb-3 p-3 border rounded"
                                style={{
                                  cursor: 'pointer',
                                  transition: 'all 0.3s ease',
                                  backgroundColor: selectedChapterForEdit?._id === chapter._id ? '#f0f8ff' : 'white'
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                                  e.currentTarget.style.transform = 'translateY(-2px)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.boxShadow = 'none';
                                  e.currentTarget.style.transform = 'translateY(0)';
                                }}
                                onClick={() => handleEditChapter(chapter)}
                              >
                                <div className="d-flex align-items-start gap-3">
                                  {/* Thumbnail */}
                                  {chapter.thumbnailUrl && (
                                    <div style={{ 
                                      width: '120px', 
                                      height: '80px', 
                                      flexShrink: 0,
                                      borderRadius: '4px',
                                      overflow: 'hidden',
                                      backgroundColor: '#f0f0f0'
                                    }}>
                                      <img
                                        src={chapter.thumbnailUrl.startsWith('http') 
                                          ? chapter.thumbnailUrl 
                                          : `https://cdn-dev.sportsforlife.in/${chapter.thumbnailUrl}`}
                                        alt={chapter.title || chapter.chapterTitle}
                                        style={{
                                          width: '100%',
                                          height: '100%',
                                          objectFit: 'cover'
                                        }}
                                        onError={(e) => {
                                          const target = e.target as HTMLImageElement;
                                          // Only set placeholder if we haven't already tried it
                                          if (target.src !== window.location.origin + '/placeholder-video.png') {
                                            target.src = '/placeholder-video.png';
                                          } else {
                                            // If placeholder also fails, hide the image to prevent infinite loop
                                            target.style.display = 'none';
                                          }
                                        }}
                                      />
                                    </div>
                                  )}
                                  <div className="flex-grow-1">
                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                      <div>
                                        <h6 className="mb-1" style={{ fontSize: '16px', fontWeight: 600 }}>
                                          {chapter.chapterTitle || chapter.title || `Chapter ${chapter.chapterNumber || index + 1}`}
                                        </h6>
                                        {chapter.chapterNumber && (
                                          <p className="mb-1 text-muted" style={{ fontSize: '14px' }}>
                                            Chapter {chapter.chapterNumber}
                                          </p>
                                        )}
                                      </div>
                                      <div className="chapter-menu-wrapper" style={{ position: 'relative' }}>
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-link text-muted p-0 chapter-menu-button"
                                          style={{ fontSize: '18px' }}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setOpenChapterMenuId(openChapterMenuId === chapter._id ? null : chapter._id);
                                          }}
                                        >
                                          <i className="ph-dots-three-vertical"></i>
                                        </button>
                                        {openChapterMenuId === chapter._id && isEditMode && (
                                          <div 
                                            className="dropdown-menu show"
                                            style={{
                                              position: 'absolute',
                                              right: 0,
                                              top: '100%',
                                              zIndex: 1000,
                                              minWidth: '150px',
                                              marginTop: '4px',
                                              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                                              borderRadius: '8px',
                                              padding: '4px',
                                              backgroundColor: 'white'
                                            }}
                                            onClick={(e) => e.stopPropagation()}
                                          >
                                            <button
                                              className="dropdown-item"
                                              style={{ 
                                                padding: '8px 12px',
                                                border: 'none',
                                                background: 'none',
                                                width: '100%',
                                                textAlign: 'left',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                                fontSize: '14px'
                                              }}
                                              onClick={() => {
                                                setOpenChapterMenuId(null);
                                                handleEditChapter(chapter);
                                              }}
                                            >
                                              <i className="ph-pencil"></i>
                                              <span>Edit</span>
                                            </button>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    {chapter.description && (
                                      <p className="mb-0 text-muted" style={{ fontSize: '14px', lineHeight: '1.5' }}>
                                        {chapter.description.length > 100 
                                          ? `${chapter.description.substring(0, 100)}...` 
                                          : chapter.description}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                            </div>
                          </>
                        )}
                      </div>
                    </Tab.Pane>
                  )}

                  {(category === VideoCategory.ACTION || category === VideoCategory.SOCIAL) && (
                  <Tab.Pane eventKey="player-details">
                    <div className="form-section">
                      <label className="form-section-label">Entity Details</label>
                      
                      <Form.Group className="mb-3">
                        <Form.Label>
                          Who is adding this video?
                          <span style={{ color: 'red', marginLeft: '4px' }}>*</span>
                        </Form.Label>
                        <Select
                          options={[
                            { value: EntityType.PLAYER, label: 'Player' },
                            { value: EntityType.COACH, label: 'Coach' },
                            { value: EntityType.ACADEMY, label: 'Academy' },
                            { value: EntityType.SFL_USER, label: 'SFL User' }
                          ]}
                          value={selectedEntityType ? { 
                            value: selectedEntityType, 
                            label: selectedEntityType === EntityType.SFL_USER ? 'SFL User' : selectedEntityType.charAt(0).toUpperCase() + selectedEntityType.slice(1)
                          } : null}
                          onChange={(option: any) => {
                            if (isEditMode) {
                              setHasFormChanges(true);
                            }
                            const newEntityType = option?.value || null;
                            setSelectedEntityType(newEntityType);
                            
                            // If SFL_USER is selected, automatically set entity
                            if (newEntityType === EntityType.SFL_USER) {
                              setSelectedEntity({
                                value: 'sfl',
                                label: 'SFL',
                                entityId: 'sfl',
                                entityName: 'sfl',
                                profilePic: 'shared/sfl-logo.png', // Update this path if needed
                                entityType: EntityType.SFL_USER
                              });
                            } else {
                              setSelectedEntity(null); // Reset entity when type changes
                            }
                          }}
                          placeholder="Select entity type"
                          isClearable
                          isDisabled={uploading}
                          classNamePrefix="react-select"
                          styles={{
                            control: (base) => ({ 
                              ...base, 
                              minHeight: '38px',
                              border: '1px solid #dee2e6',
                              borderRadius: '6px'
                            }),
                            menu: (base) => ({ 
                              ...base, 
                              zIndex: 9999 
                            }),
                            menuPortal: (base) => ({ 
                              ...base, 
                              zIndex: 9999 
                            })
                          }}
                          menuPortalTarget={document.body}
                        />
                      </Form.Group>

                      {selectedEntityType && selectedEntityType !== EntityType.SFL_USER && (
                <Form.Group className="mb-3">
                          <Form.Label>
                            {selectedEntityType === EntityType.PLAYER ? 'Player' : 
                             selectedEntityType === EntityType.COACH ? 'Coach' : 
                             'Academy'}
                            <span style={{ color: 'red', marginLeft: '4px' }}>*</span>
                          </Form.Label>
                        <AsyncSelect
                            key={`entity-select-${selectedEntityType}-${selectedEntity?.entityId || 'none'}`}
                          cacheOptions
                          defaultOptions={selectedEntity ? [selectedEntity] : true}
                            loadOptions={(inputValue: string) => loadEntities(inputValue, selectedEntityType)}
                            value={selectedEntity}
                            onChange={(option: EntityOption | null) => {
                              if (isEditMode) {
                                setHasFormChanges(true);
                              }
                              setSelectedEntity(option);
                            }}
                            placeholder={`Search and select a ${selectedEntityType}`}
                          isClearable
                          isDisabled={uploading}
                          getOptionValue={(option: EntityOption) => option.entityId}
                          getOptionLabel={(option: EntityOption) => option.entityName}
                          classNamePrefix="react-select"
                          styles={{
                            control: (base) => ({ 
                              ...base, 
                              minHeight: '38px',
                              border: '1px solid #dee2e6',
                              borderRadius: '6px'
                            }),
                            menu: (base) => ({ 
                              ...base, 
                              zIndex: 9999 
                            }),
                            menuPortal: (base) => ({ 
                              ...base, 
                              zIndex: 9999 
                            })
                          }}
                          menuPortalTarget={document.body}
                          noOptionsMessage={({ inputValue }) => 
                              inputValue ? `No ${selectedEntityType}s found for "${inputValue}"` : `Start typing to search ${selectedEntityType}s`
                          }
                        />
                      </Form.Group>
                      )}
                    </div>
                  </Tab.Pane>
                  )}
                </Tab.Content>

              </Tab.Container>
            )}

          </Form>
        </div>
        
        {/* Footer - For inline mode */}
        {/* Hide footer when inline and initialTab is 'chapters' (used in modal from detail page) */}
        {!(initialTab === 'chapters') && (
        <div className="add-new-form-footer">
          <button 
            type="button" 
            className="cancel-btn" 
              onClick={() => {
                hasAutoAddedFirstChapter.current = false;
                setChapters([]);
                onHide();
              }}
            disabled={uploading}
          >
            Cancel
          </button>
          {/* Hide Update Video button when editing a series - Update Series button is shown in series-details tab instead */}
          {!(isEditMode && category === VideoCategory.LEVEL_UP && video?.videoType === 'series') && (
            <button 
              type="button" 
              className="upload-btn"
              onClick={(e) => {
                e.preventDefault();
                const form = document.querySelector('.add-new-form-form') as HTMLFormElement;
                if (form) {
                  form.requestSubmit();
                }
              }}
              disabled={uploading || uploadingChapters || !isFormValid() || (isEditMode && !hasFormChanges)}
            >
              {uploading || uploadingChapters ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  {category === VideoCategory.LEVEL_UP && activeTab === 'chapters' ? 'Uploading Chapters...' : 'Uploading...'}
                </>
              ) : (
                <>
                  <i className={isEditMode ? "ph-check" : "ph-upload"}></i>
                  {category === VideoCategory.LEVEL_UP && activeTab === 'chapters' && !isEditMode 
                    ? 'Upload Video +' 
                    : isEditMode 
                      ? (() => {
                          // Determine button text based on what we're editing
                          const isEditingChapter = category === VideoCategory.LEVEL_UP && (video?.videoType === 'chapter' || (video?.seriesId && video?.videoType !== 'series'));
                          
                          if (isEditingChapter) {
                            return 'Update Chapter';
                          } else if (category === VideoCategory.SOCIAL && postType === 'image') {
                            return 'Update Image';
                          } else {
                            return 'Update Video';
                          }
                        })()
                      : 'Upload Video +'}
                </>
              )}
            </button>
          )}
        </div>
        )}
      </div>
    );
  }

  // Sidebar mode (default)
  return (
    <>
      {/* Backdrop - removed onClick to prevent closing on outside click */}
      <div className={`add-new-form-backdrop ${show ? 'show' : ''}`} />
      
      {/* Right Side Panel */}
      <div className={`add-new-form-panel ${show ? 'show' : ''}`}>
        <div className="add-new-form-header">
          <h4 className="add-new-form-title">{isEditMode ? 'Edit' : 'Add New'} {getCategoryLabel()}</h4>
          <button className="add-new-form-close" onClick={() => {
            hasAutoAddedFirstChapter.current = false;
            setChapters([]);
            onHide();
          }}>
            <i className="ph-x"></i>
          </button>
        </div>

        <div className="add-new-form-body">
          <Form onSubmit={handleSubmit} className="add-new-form-form">
            {uploadError && (
              <div className="alert alert-danger" role="alert">
                {uploadError}
              </div>
            )}

            {/* Level Up Content Type Selection - Show only when creating Level Up and no selection made yet */}
            {category === VideoCategory.LEVEL_UP && !isEditMode && !levelUpContentType && (
              <Form.Group className="mb-4">
                <Form.Label>
                  Level Up Type
                  <span style={{ color: 'red', marginLeft: '4px' }}>*</span>
                </Form.Label>
                <Select
                  value={null}
                  onChange={(option: any) => {
                    const selectedType = option?.value as 'standalone' | 'series';
                    if (selectedType) {
                      setLevelUpContentType(selectedType);
                      if (selectedType === 'standalone') {
                        setActiveTab('video-details');
                      } else {
                        setActiveTab('series-details');
                      }
                    }
                  }}
                  options={[
                    { value: 'standalone', label: 'Standalone Video' },
                    { value: 'series', label: 'Series' }
                  ]}
                  placeholder="Select Level Up Type"
                  isClearable={false}
                  isDisabled={uploading}
                  classNamePrefix="react-select"
                  styles={{
                    control: (base) => ({
                      ...base,
                      minHeight: '38px',
                      border: '1px solid #dee2e6',
                      borderRadius: '6px'
                    }),
                    menu: (base) => ({
                      ...base,
                      zIndex: 9999
                    }),
                    menuPortal: (base) => ({
                      ...base,
                      zIndex: 9999
                    })
                  }}
                  menuPortalTarget={document.body}
                />
                <Form.Text className="text-muted">
                  Choose whether to create a single video or a series with multiple chapters
                </Form.Text>
              </Form.Group>
            )}

            {/* Tabs - Show Series Details/Chapters for Level Up, regular tabs for others */}
            {!(category === VideoCategory.LEVEL_UP && !isEditMode && !levelUpContentType) && (
              <Tab.Container 
                activeKey={activeTab || (isStandaloneLevelUp ? 'video-details' : category === VideoCategory.LEVEL_UP && isEditMode && (video?.videoType === 'chapter' || (video?.seriesId && video?.videoType !== 'series')) ? 'video-details' : category === VideoCategory.LEVEL_UP && (isEditMode && video?.videoType === 'series' || levelUpContentType === 'series') ? 'series-details' : 'video-details')} 
                onSelect={(k) => setActiveTab(k || 'video-details')}
                defaultActiveKey={isStandaloneLevelUp ? 'video-details' : category === VideoCategory.LEVEL_UP && isEditMode && (video?.videoType === 'chapter' || (video?.seriesId && video?.videoType !== 'series')) ? 'video-details' : category === VideoCategory.LEVEL_UP && (isEditMode && video?.videoType === 'series' || levelUpContentType === 'series') ? 'series-details' : 'video-details'}
              >
                <Nav variant="tabs" className="mb-4 form-tabs">
                  {category === VideoCategory.LEVEL_UP && !isStandaloneLevelUp && (!isEditMode || !inline) ? (
                    <>
                      <Nav.Item>
                        <Nav.Link 
                          eventKey="series-details"
                          onClick={() => {
                            console.log('Series Details tab clicked, setting activeTab to series-details');
                            setActiveTab('series-details');
                          }}
                        >
                          <i className="ph-list me-2"></i>
                          Series Details
                        </Nav.Link>
                      </Nav.Item>
                        <Nav.Item>
                          <Nav.Link 
                            eventKey="chapters" 
                            disabled={!createdSeriesId}
                            onClick={() => {
                              // Reset chapter form state when switching to chapters tab to show listing first
                              setShowAddChapterForm(false);
                              setSelectedChapterForEdit(null);
                              setOriginalChapterData(null);
                              setHasChapterChanges(false);
                              setActiveTab('chapters');
                            }}
                          >
                            <i className="ph-play me-2"></i>
                            Chapters
                          </Nav.Link>
                        </Nav.Item>
                    </>
                  ) : category === VideoCategory.LEVEL_UP && isEditMode && inline ? (
                    // Hide tabs in edit mode for Level Up (series or chapter) when in inline mode (detail page) since user is already in that detail section
                    null
                  ) : (
                    <>
                      <Nav.Item>
                        <Nav.Link eventKey="video-details">
                          <i className="ph-camera me-2"></i>
                          {category === VideoCategory.ACTION ? 'Action Details' : 
                           category === VideoCategory.LEVEL_UP && isStandaloneLevelUp ? 'Level Up Details' :
                           'Social Details'}
                        </Nav.Link>
                      </Nav.Item>
                      {(category === VideoCategory.ACTION || category === VideoCategory.SOCIAL) && (
                        <Nav.Item>
                          <Nav.Link eventKey="player-details">
                            <i className="ph-user me-2"></i>
                            Entity Details
                          </Nav.Link>
                        </Nav.Item>
                      )}
                    </>
                  )}
                </Nav>

                <Tab.Content>
                  {/* Video Details Tab - Show for non-Level Up, standalone Level Up, or Level Up chapters when editing */}
                  {(category !== VideoCategory.LEVEL_UP || isStandaloneLevelUp || (isEditMode && (video?.videoType === 'chapter' || (video?.seriesId && video?.videoType !== 'series'))) || (!isEditMode && category === VideoCategory.LEVEL_UP && levelUpContentType === 'standalone')) ? (
                  <Tab.Pane eventKey="video-details">
                    {/* Post Type Selection - Only for Social category */}
                    {category === VideoCategory.SOCIAL && (
                      <Form.Group className="mb-4">
                        <Form.Label>
                          Post Type
                          <span style={{ color: 'red', marginLeft: '4px' }}>*</span>
                        </Form.Label>
                        <div className="d-flex gap-3">
                          <FormCheck
                            type="radio"
                            id="post-type-video-sidebar"
                            name="postType"
                            label="Video"
                            checked={postType === 'video'}
                            onChange={() => {
                              setPostType('video');
                              setImageFile(null);
                              setImagePreview(null);
                            }}
                            disabled={uploading}
                          />
                          <FormCheck
                            type="radio"
                            id="post-type-image-sidebar"
                            name="postType"
                            label="Image"
                            checked={postType === 'image'}
                            onChange={() => {
                              setPostType('image');
                              setVideoFile(null);
                              setVideoPreview(null);
                              setThumbnailFile(null);
                              setThumbnailPreview(null);
                              setIsPortrait(false);
                            }}
                            disabled={uploading}
                          />
                        </div>
                      </Form.Group>
                    )}
                    
                    {/* Video and Thumbnail Upload in Same Row - Show for video posts or non-Social categories */}
                    {(category !== VideoCategory.SOCIAL || postType === 'video') && (
                <Row>
                  <Col md={6}>
                    <div className="form-section">
                  <label className="form-section-label">
                    Video Upload
                    {!isEditMode && <span style={{ color: 'red', marginLeft: '4px' }}>*</span>}
                  </label>
                      <div className="video-upload-box">
                        {videoPreview ? (
                          <div className="video-preview-container">
                            <video 
                              src={videoPreview} 
                              controls 
                              className="video-preview"
                              style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '4px' }}
                            />
                            <div className="selected-file-info">
                              <i className="ph-file-video"></i>
                              <span>{videoFile?.name || (isEditMode ? 'Current video (click to change)' : videoFile?.name)}</span>
                              {videoFile && <span className="file-size">({videoFile ? (videoFile.size / (1024 * 1024)).toFixed(2) : 0} MB)</span>}
                            </div>
                            <button 
                              type="button" 
                              className="select-file-btn" 
                              onClick={() => document.getElementById('video-file-input')?.click()}
                              disabled={uploading}
                              style={{ marginTop: '8px' }}
                            >
                              <i className="ph-upload"></i>
                              {isEditMode ? 'Change Video' : 'Change Video'}
                            </button>
                          </div>
                        ) : (
                          <>
                            <div className="video-upload-placeholder">
                              <i className="ph-cloud-arrow-up"></i>
                              <p>{isEditMode ? 'Select New Video' : 'Select Video to Upload'}</p>
                              <p className="video-upload-hint">Supported Format: MP4, MOV (Max: 2GB)</p>
                            </div>
                            <button 
                              type="button" 
                              className="select-file-btn" 
                              onClick={() => document.getElementById('video-file-input')?.click()}
                              disabled={uploading}
                            >
                              <i className="ph-upload"></i>
                              Select File
                            </button>
                          </>
                        )}
                        <input
                          id="video-file-input"
                          type="file"
                          accept="video/mp4,video/quicktime,.mp4,.mov"
                          onChange={handleVideoFileChange}
                          style={{ display: 'none' }}
                          disabled={uploading}
                        />
                      </div>
                  
                  {/* Portrait/Landscape Selection - Show only when video is selected and not in edit mode */}
                  {videoFile && !isEditMode && (
                    <Form.Group className="mb-3 mt-3">
                      <Form.Label>
                        Video Orientation
                        <span style={{ color: 'red', marginLeft: '4px' }}>*</span>
                      </Form.Label>
                      <div className="d-flex align-items-center gap-3">
                        <span className={isPortrait ? 'text-muted' : 'fw-bold'}>Landscape</span>
                        <FormCheck
                          type="switch"
                          id="portrait-toggle-sidebar"
                          label={isPortrait ? 'Portrait' : 'Landscape'}
                          checked={isPortrait}
                          onChange={(e) => setIsPortrait(e.target.checked)}
                          disabled={uploading}
                          style={{ margin: 0 }}
                        />
                        <span className={isPortrait ? 'fw-bold' : 'text-muted'}>Portrait</span>
                      </div>
                    </Form.Group>
                  )}
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="form-section">
                  <label className="form-section-label">
                    Video Thumbnail
                    {!isEditMode && <span style={{ color: 'red', marginLeft: '4px' }}>*</span>}
                  </label>
                  <div className="video-upload-box">
                        {thumbnailPreview ? (
                      <div className="video-preview-container">
                        <img 
                          src={thumbnailPreview} 
                          alt="Thumbnail preview"
                          style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '4px', objectFit: 'contain' }}
                        />
                        <div className="selected-file-info">
                          <i className="ph-image"></i>
                          <span>{thumbnailFile?.name || (isEditMode ? 'Current thumbnail (click to change)' : thumbnailFile?.name)}</span>
                          {thumbnailFile && <span className="file-size">({thumbnailFile ? (thumbnailFile.size / (1024 * 1024)).toFixed(2) : 0} MB)</span>}
                        </div>
                        <button 
                          type="button" 
                          className="select-file-btn" 
                          onClick={() => document.getElementById('thumbnail-input')?.click()}
                          disabled={uploading}
                          style={{ marginTop: '8px' }}
                        >
                          <i className="ph-upload"></i>
                          {isEditMode ? 'Change Thumbnail' : 'Change Thumbnail'}
                        </button>
                          </div>
                        ) : (
                          <>
                            <div className="video-upload-placeholder">
                              <i className="ph-cloud-arrow-up"></i>
                              <p>{isEditMode ? 'Select New Thumbnail' : 'Select Thumbnail to Upload'}</p>
                              <p className="video-upload-hint">Supported Format: JPG, PNG, GIF (Max: 50MB)</p>
                            </div>
                            <button 
                              type="button" 
                              className="select-file-btn" 
                              onClick={() => document.getElementById('thumbnail-input')?.click()}
                              disabled={uploading}
                            >
                              <i className="ph-upload"></i>
                              Select File
                            </button>
                          </>
                        )}
                        <input
                          type="file"
                          accept="image/*"
                          style={{ display: 'none' }}
                          id="thumbnail-input"
                          onChange={handleThumbnailFileChange}
                          disabled={uploading}
                        />
                      </div>
                    </div>
                  </Col>
                </Row>
                    )}
                    
                    {/* Image Upload - Show only for Social image posts - Using same size as thumbnail */}
                    {category === VideoCategory.SOCIAL && postType === 'image' && (
                      <div className="form-section">
                        <label className="form-section-label">
                          Image Upload
                          <span style={{ color: 'red', marginLeft: '4px' }}>*</span>
                        </label>
                        <div className="video-upload-box">
                          {imagePreview ? (
                            <div className="video-preview-container">
                              <img 
                                src={imagePreview} 
                                alt="Image preview"
                                style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '4px', objectFit: 'contain' }}
                              />
                              <div className="selected-file-info">
                                <i className="ph-image"></i>
                                <span>{imageFile?.name}</span>
                                <span className="file-size">({imageFile ? (imageFile.size / (1024 * 1024)).toFixed(2) : 0} MB)</span>
                              </div>
                              <button 
                                type="button" 
                                className="select-file-btn" 
                                onClick={() => document.getElementById('image-file-input')?.click()}
                                disabled={uploading}
                                style={{ marginTop: '8px' }}
                              >
                                <i className="ph-upload"></i>
                                Change Image
                              </button>
                            </div>
                          ) : (
                            <>
                              <div className="video-upload-placeholder">
                                <i className="ph-cloud-arrow-up"></i>
                                <p>Select Image to Upload</p>
                                <p className="video-upload-hint">Supported Format: JPG, PNG, GIF (Max: 50MB)</p>
                              </div>
                              <button 
                                type="button" 
                                className="select-file-btn" 
                                onClick={() => document.getElementById('image-file-input')?.click()}
                                disabled={uploading}
                              >
                                <i className="ph-upload"></i>
                                Select File
                              </button>
                            </>
                          )}
                          <input
                            id="image-file-input"
                            type="file"
                            accept="image/*"
                            onChange={handleImageFileChange}
                            style={{ display: 'none' }}
                            disabled={uploading}
                          />
                        </div>
                      </div>
                    )}

                {/* Title */}
                <Form.Group className="mb-3">
              <Form.Label>
                Title
                <span style={{ color: 'red', marginLeft: '4px' }}>*</span>
              </Form.Label>
                  <Form.Control
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    placeholder="Enter Title"
                    required
                    disabled={uploading}
                  />
                </Form.Group>

                {/* Tags */}
                <Form.Group className="mb-3">
                  <Form.Label>Tags (#)</Form.Label>
                  <CreatableSelect
                    isMulti
                    isClearable
                    isDisabled={uploading || loadingTags}
                    isLoading={loadingTags}
                    options={existingTags}
                    value={selectedTags}
                    onChange={(newValue) => {
                  if (isEditMode) {
                    setHasFormChanges(true);
                  }
                  setSelectedTags(newValue);
                }}
                    placeholder="Enter tags separated by commas or select from existing"
                    classNamePrefix="react-select"
                    styles={{
                      control: (base) => ({ 
                        ...base, 
                        minHeight: '38px',
                        border: '1px solid #dee2e6',
                        borderRadius: '6px'
                      }),
                      menu: (base) => ({ 
                        ...base, 
                        zIndex: 9999 
                      }),
                      menuPortal: (base) => ({ 
                        ...base, 
                        zIndex: 9999 
                      })
                    }}
                    menuPortalTarget={document.body}
                    formatCreateLabel={(inputValue) => `Add "${inputValue}"`}
                  />
                </Form.Group>

                {/* Entity Selection - Show for standalone Level Up videos in video-details tab */}
                {category === VideoCategory.LEVEL_UP && (isStandaloneLevelUp || (!isEditMode && levelUpContentType === 'standalone')) && (
                  <div className="form-section">
                    <label className="form-section-label">Entity Details</label>
                    
                    <Form.Group className="mb-3">
                      <Form.Label>
                        Who is adding this video?
                        <span style={{ color: 'red', marginLeft: '4px' }}>*</span>
                      </Form.Label>
                      <Select
                        options={[
                          { value: EntityType.COACH, label: 'Coach' },
                          { value: EntityType.SFL_USER, label: 'SFL User' }
                        ]}
                        value={selectedEntityType ? { 
                          value: selectedEntityType, 
                          label: selectedEntityType === EntityType.SFL_USER ? 'SFL User' : selectedEntityType.charAt(0).toUpperCase() + selectedEntityType.slice(1)
                        } : null}
                        onChange={(option: any) => {
                          if (isEditMode) {
                            setHasFormChanges(true);
                          }
                          const newEntityType = option?.value || null;
                          setSelectedEntityType(newEntityType);
                          
                          // If SFL_USER is selected, automatically set entity
                          if (newEntityType === EntityType.SFL_USER) {
                            setSelectedEntity({
                              value: 'sfl',
                              label: 'SFL',
                              entityId: 'sfl',
                              entityName: 'sfl',
                              profilePic: 'shared/sfl-logo.png',
                              entityType: EntityType.SFL_USER
                            });
                          } else {
                            setSelectedEntity(null); // Reset entity when type changes
                          }
                        }}
                        placeholder="Select entity type"
                        isClearable
                        isDisabled={uploading}
                        classNamePrefix="react-select"
                        styles={{
                          control: (base) => ({ 
                            ...base, 
                            minHeight: '38px',
                            border: '1px solid #dee2e6',
                            borderRadius: '6px'
                          }),
                          menu: (base) => ({ 
                            ...base, 
                            zIndex: 99999 
                          }),
                          menuPortal: (base) => ({ 
                            ...base, 
                            zIndex: 99999 
                          })
                        }}
                        menuPortalTarget={document.body}
                      />
                    </Form.Group>

                    {selectedEntityType && selectedEntityType !== EntityType.SFL_USER && (
                      <Form.Group className="mb-3">
                        <Form.Label>
                          {selectedEntityType === EntityType.COACH ? 'Coach' : 'Entity'}
                          <span style={{ color: 'red', marginLeft: '4px' }}>*</span>
                        </Form.Label>
                        <AsyncSelect
                          key={`entity-select-standalone-sidebar-${selectedEntityType}-${selectedEntity?.entityId || 'none'}`}
                          cacheOptions
                          defaultOptions={selectedEntity ? [selectedEntity] : true}
                          loadOptions={(inputValue: string) => loadEntities(inputValue, selectedEntityType)}
                          value={selectedEntity}
                          onChange={(option: EntityOption | null) => {
                            if (isEditMode) {
                              setHasFormChanges(true);
                            }
                            if (option) {
                              // Ensure sports is always an array - check both option.sports and option.basicInfo?.sports
                              const sportsArray = Array.isArray(option.sports) 
                                ? option.sports 
                                : Array.isArray(option.basicInfo?.sports)
                                ? option.basicInfo.sports
                                : [];
                              setSelectedEntity({
                                ...option,
                                sports: sportsArray
                              });
                            } else {
                              setSelectedEntity(null);
                            }
                          }}
                          placeholder={`Search and select a ${selectedEntityType === EntityType.COACH ? 'coach' : selectedEntityType}`}
                          isClearable
                          isDisabled={uploading}
                          getOptionValue={(option: EntityOption) => option.entityId}
                          getOptionLabel={(option: EntityOption) => option.entityName}
                          classNamePrefix="react-select"
                          styles={{
                            control: (base) => ({ 
                              ...base, 
                              minHeight: '38px',
                              border: '1px solid #dee2e6',
                              borderRadius: '6px'
                            }),
                            menu: (base) => ({ 
                              ...base, 
                              zIndex: 99999 
                            }),
                            menuPortal: (base) => ({ 
                              ...base, 
                              zIndex: 99999 
                            })
                          }}
                          menuPortalTarget={document.body}
                          noOptionsMessage={({ inputValue }) => 
                            inputValue ? `No ${selectedEntityType === EntityType.COACH ? 'coaches' : selectedEntityType} found for "${inputValue}"` : `Start typing to search ${selectedEntityType === EntityType.COACH ? 'coaches' : selectedEntityType}`
                          }
                        />
                      </Form.Group>
                    )}
                  </div>
                )}

                {/* Description */}
                <Form.Group className="mb-3">
                  <Form.Label>Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Enter Description"
                    disabled={uploading}
                  />
                </Form.Group>

                {/* Featured Toggle - Show for Action, Social, and standalone Level Up categories in both create and edit modes */}
                {((category === VideoCategory.ACTION || category === VideoCategory.SOCIAL) || (category === VideoCategory.LEVEL_UP && (isStandaloneLevelUp || (!isEditMode && levelUpContentType === 'standalone')))) && (
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Featured
                      <span style={{ color: 'red', marginLeft: '4px' }}>*</span>
                    </Form.Label>
                    <div className="d-flex align-items-center gap-3">
                      <span className={isFeatured ? 'text-muted' : 'fw-bold'}>No</span>
                      <FormCheck
                        type="switch"
                        id="featured-toggle-sidebar"
                        label={isFeatured ? 'Yes' : 'No'}
                        checked={isFeatured}
                        onChange={(e) => {
                          setIsFeatured(e.target.checked);
                          if (isEditMode) {
                            setHasFormChanges(true);
                          }
                        }}
                        disabled={uploading}
                        style={{ margin: 0 }}
                      />
                      <span className={isFeatured ? 'fw-bold' : 'text-muted'}>Yes</span>
                    </div>
                  </Form.Group>
                )}

                {/* Schedule Publish - Hide for standalone Level Up videos and published/live Action/Social videos in edit mode */}
                {(() => {
                  const isStandaloneLevelUpVideo = category === VideoCategory.LEVEL_UP && (isStandaloneLevelUp || (!isEditMode && levelUpContentType === 'standalone'));
                  const isPublishedActionOrSocial = (category === VideoCategory.ACTION || category === VideoCategory.SOCIAL) && 
                    isEditMode && 
                    (video?.status === 'published' || video?.status === 'live' || video?.publishedAt);
                  return !(isStandaloneLevelUpVideo || isPublishedActionOrSocial);
                })() && (
                  <>
                <Form.Group className="mb-3">
                  <FormCheck
                    type="checkbox"
                    id="schedule-publish-sidebar"
                    label="Schedule Publish"
                    name="schedulePublish"
                    checked={formData.schedulePublish}
                    onChange={handleInputChange}
                    disabled={uploading}
                  />
                  <Form.Text className="text-muted">
                    Pick when you want your video to go live. We'll publish it automatically at that time.
                  </Form.Text>
                </Form.Group>

                {formData.schedulePublish && (
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Publish Date</Form.Label>
                        <Form.Control
                          type="date"
                          name="publishDate"
                          value={formData.publishDate}
                          onChange={handleInputChange}
                          disabled={uploading}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Publish Time</Form.Label>
                        <Form.Control
                          type="time"
                          name="publishTime"
                          value={formData.publishTime}
                          onChange={handleInputChange}
                          disabled={uploading}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                    )}
                  </>
                )}
                  </Tab.Pane>
                  ) : null}

                  {/* Series Details Tab - Level Up only (Sidebar) - Show when creating or editing a series, NOT when editing a chapter */}
                  {category === VideoCategory.LEVEL_UP && !(isEditMode && (video?.videoType === 'chapter' || (video?.seriesId && video?.videoType !== 'series'))) && (
                    <Tab.Pane eventKey="series-details" key={`series-details-sidebar-${createdSeriesId || 'new'}`}>
                      {loadingChapters && isEditMode ? (
                        <div className="text-center py-5">
                          <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                          <span>Loading series details...</span>
                        </div>
                      ) : (
                      <div className="form-section">
                        <label className="form-section-label">Series Details</label>
                        
                        {/* Title */}
                        <Form.Group className="mb-3">
                          <Form.Label>
                            Title
                            <span style={{ color: 'red', marginLeft: '4px' }}>*</span>
                          </Form.Label>
                          <Form.Control
                            type="text"
                            name="title"
                              value={seriesData.title || ''}
                            onChange={handleSeriesInputChange}
                            placeholder="Enter Series Title"
                              disabled={isCreatingSeries || uploading}
                          />
                        </Form.Group>

                        {/* Description */}
                        <Form.Group className="mb-3">
                          <Form.Label>Description</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={4}
                            name="description"
                            value={seriesData.description || ''}
                            onChange={handleSeriesInputChange}
                            placeholder="Enter Series Description"
                            disabled={isCreatingSeries || uploading}
                          />
                        </Form.Group>

                        {/* Coach Selection */}
                        <Form.Group className="mb-3">
                          <Form.Label>
                            Coach Name
                            <span style={{ color: 'red', marginLeft: '4px' }}>*</span>
                          </Form.Label>
                          <AsyncSelect
                            cacheOptions
                            defaultOptions={true}
                            loadOptions={(inputValue: string) => loadEntities(inputValue, EntityType.COACH)}
                            value={selectedEntity && selectedEntityType === EntityType.COACH ? selectedEntity : null}
                            onChange={(option: EntityOption | null) => {
                              if (option) {
                                // Automatically set entity type to COACH for series
                                setSelectedEntityType(EntityType.COACH);
                                // Ensure sports is always an array - check both option.sports and option.basicInfo?.sports
                                const sportsArray = Array.isArray(option.sports) 
                                  ? option.sports 
                                  : Array.isArray(option.basicInfo?.sports)
                                  ? option.basicInfo.sports
                                  : [];
                                setSelectedEntity({
                                  ...option,
                                  sports: sportsArray
                                });
                                // Clear coach profile pic file when selecting a new coach
                                setCoachProfilePicFile(null);
                                setCoachProfilePicPreview(null);
                              } else {
                                setSelectedEntity(null);
                                setSelectedEntityType(null);
                                setCoachProfilePicFile(null);
                                setCoachProfilePicPreview(null);
                              }
                            }}
                            onFocus={() => {
                              // Load initial options when dropdown opens
                              if (!selectedEntity || selectedEntityType !== EntityType.COACH) {
                                loadEntities('', EntityType.COACH);
                              }
                            }}
                            placeholder="Search and select a coach"
                            isClearable
                            isDisabled={isCreatingSeries || uploading}
                            classNamePrefix="react-select"
                            menuPosition="fixed"
                            styles={{
                              control: (base) => ({ 
                                ...base, 
                                minHeight: '38px',
                                border: '1px solid #dee2e6',
                                borderRadius: '6px'
                              }),
                              menu: (base) => ({ 
                                ...base, 
                                zIndex: 9999 
                              }),
                              menuPortal: (base) => ({ 
                                ...base, 
                                zIndex: 9999 
                              })
                            }}
                            menuPortalTarget={document.body}
                            getOptionValue={(option: EntityOption) => option.entityId}
                            getOptionLabel={(option: EntityOption) => option.entityName}
                            noOptionsMessage={({ inputValue }) => 
                              inputValue ? `No coaches found for "${inputValue}"` : 'Start typing to search coaches'
                            }
                          />
                        </Form.Group>

                        {/* Sub Category */}
                        <Form.Group className="mb-3">
                          <Form.Label>
                            Sub Category
                            <span style={{ color: 'red', marginLeft: '4px' }}>*</span>
                          </Form.Label>
                          <Form.Control
                            type="text"
                            name="subCategory"
                            value={seriesData.subCategory || ''}
                            onChange={handleSeriesInputChange}
                            placeholder="e.g., technical_skills"
                            disabled={isCreatingSeries || uploading}
                          />
                        </Form.Group>

                        {/* Difficulty Level - Using react-select */}
                        <Form.Group className="mb-3">
                          <Form.Label>
                            Difficulty Level
                            <span style={{ color: 'red', marginLeft: '4px' }}>*</span>
                          </Form.Label>
                          <Select
                            options={[
                              { value: DifficultyLevel.BEGINNER, label: 'Beginner' },
                              { value: DifficultyLevel.INTERMEDIATE, label: 'Intermediate' },
                              { value: DifficultyLevel.ADVANCED, label: 'Advanced' }
                            ]}
                            value={{
                              value: seriesData.difficultyLevel,
                              label: seriesData.difficultyLevel === DifficultyLevel.BEGINNER ? 'Beginner' 
                                : seriesData.difficultyLevel === DifficultyLevel.INTERMEDIATE ? 'Intermediate' 
                                : 'Advanced'
                            }}
                            onChange={(option: any) => {
                              if (option) {
                                setSeriesData(prev => ({ ...prev, difficultyLevel: option.value }));
                                // Mark form as changed in edit mode
                                if (isEditMode) {
                                  setHasFormChanges(true);
                                }
                              }
                            }}
                            isDisabled={isCreatingSeries || uploading}
                            classNamePrefix="react-select"
                            styles={{
                              control: (base) => ({ 
                                ...base, 
                                minHeight: '38px',
                                border: '1px solid #dee2e6',
                                borderRadius: '6px'
                              }),
                              menu: (base) => ({ 
                                ...base, 
                                zIndex: 9999 
                              }),
                              menuPortal: (base) => ({ 
                                ...base, 
                                zIndex: 9999 
                              })
                            }}
                            menuPortalTarget={document.body}
                            placeholder="Select difficulty level"
                          />
                        </Form.Group>

                        {/* Tags */}
                        <Form.Group className="mb-3">
                          <Form.Label>Tags (#)</Form.Label>
                          <CreatableSelect
                            isMulti
                            options={existingTags}
                            value={selectedTags}
                            onChange={(newValue) => {
                  if (isEditMode) {
                    setHasFormChanges(true);
                  }
                  setSelectedTags(newValue);
                }}
                            onCreateOption={(inputValue) => {
                              const newTag = { value: inputValue, label: inputValue };
                              setSelectedTags([...selectedTags, newTag]);
                            }}
                            placeholder="Add tags..."
                            isDisabled={isCreatingSeries || uploading || loadingTags}
                            classNamePrefix="react-select"
                            styles={{
                              control: (base) => ({ 
                                ...base, 
                                minHeight: '38px',
                                border: '1px solid #dee2e6',
                                borderRadius: '6px'
                              }),
                              menu: (base) => ({ 
                                ...base, 
                                zIndex: 9999 
                              }),
                              menuPortal: (base) => ({ 
                                ...base, 
                                zIndex: 9999 
                              })
                            }}
                            menuPortalTarget={document.body}
                            formatCreateLabel={(inputValue) => `Add "${inputValue}"`}
                          />
                        </Form.Group>

                        {/* Featured Toggle - Show for Level Up series */}
                        <Form.Group className="mb-3">
                          <Form.Label>
                            Featured
                            <span style={{ color: 'red', marginLeft: '4px' }}>*</span>
                          </Form.Label>
                          <div className="d-flex align-items-center gap-3">
                            <span className={isFeatured ? 'text-muted' : 'fw-bold'}>No</span>
                            <FormCheck
                              type="switch"
                              id="featured-toggle-series-sidebar"
                              label={isFeatured ? 'Yes' : 'No'}
                              checked={isFeatured}
                              onChange={(e) => {
                                setIsFeatured(e.target.checked);
                                setSeriesData(prev => ({ ...prev, featured: e.target.checked }));
                                if (isEditMode) {
                                  setHasFormChanges(true);
                                }
                              }}
                              disabled={isCreatingSeries || uploading}
                              style={{ margin: 0 }}
                            />
                            <span className={isFeatured ? 'fw-bold' : 'text-muted'}>Yes</span>
                          </div>
                        </Form.Group>

                        {/* Coach Profile Picture Upload - Only show if Coach is selected and doesn't have profile pic */}
                        {selectedEntity && selectedEntityType === EntityType.COACH && 
                         (!selectedEntity.profilePic || selectedEntity.profilePic === null || selectedEntity.profilePic === 'null' || selectedEntity.profilePic === '') && (
                          <Form.Group className="mb-3">
                            <Form.Label>
                              Coach Profile Picture
                              <span style={{ color: 'red', marginLeft: '4px' }}>*</span>
                            </Form.Label>
                            <div className="video-upload-box">
                              {coachProfilePicPreview ? (
                                <div className="video-preview-container">
                                  <img 
                                    src={coachProfilePicPreview} 
                                    alt="Coach profile preview"
                                    style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '4px', objectFit: 'contain' }}
                                  />
                                  <button 
                                    type="button" 
                                    className="select-file-btn" 
                                    onClick={() => document.getElementById('coach-profile-pic-input-sidebar')?.click()}
                                    disabled={isCreatingSeries}
                                    style={{ marginTop: '8px' }}
                                  >
                                    <i className="ph-upload"></i>
                                    Change Image
                                  </button>
                                </div>
                              ) : (
                                <>
                                  <div className="video-upload-placeholder">
                                    <i className="ph-cloud-arrow-up"></i>
                                    <p>Select Coach Profile Picture</p>
                                  </div>
                                  <button 
                                    type="button" 
                                    className="select-file-btn" 
                                    onClick={() => document.getElementById('coach-profile-pic-input-sidebar')?.click()}
                                    disabled={isCreatingSeries}
                                  >
                                    <i className="ph-upload"></i>
                                    Select File
                                  </button>
                                </>
                              )}
                              <input
                                id="coach-profile-pic-input-sidebar"
                                type="file"
                                accept="image/*"
                                onChange={handleCoachProfilePicChange}
                                style={{ display: 'none' }}
                                disabled={isCreatingSeries}
                              />
                            </div>
                          </Form.Group>
                        )}

                        {/* Cover Image Upload */}
                        <Row>
                          <Col md={12}>
                            <Form.Group className="mb-3">
                              <Form.Label>
                                Cover Image
                                {!(isEditMode && video?.videoType === 'series') && <span style={{ color: 'red', marginLeft: '4px' }}>*</span>}
                              </Form.Label>
                              <div className="video-upload-box">
                                {seriesCoverPreview ? (
                                  <div className="video-preview-container">
                                    <img 
                                      src={seriesCoverPreview} 
                                      alt="Cover preview"
                                      style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '4px', objectFit: 'contain' }}
                                    />
                                    {/* Only show Change Cover button when creating new series or editing chapters, not when editing series */}
                                    {!(isEditMode && video?.videoType === 'series') && (
                                      <button 
                                        type="button" 
                                        className="select-file-btn" 
                                        onClick={() => document.getElementById('series-cover-input-sidebar')?.click()}
                                        disabled={isCreatingSeries}
                                        style={{ marginTop: '8px' }}
                                      >
                                        <i className="ph-upload"></i>
                                        Change Cover
                                      </button>
                                    )}
                                  </div>
                                ) : (
                                  <>
                                    {/* Only show upload UI when creating new series or editing chapters, not when editing series */}
                                    {!(isEditMode && video?.videoType === 'series') && (
                                      <>
                                        <div className="video-upload-placeholder">
                                          <i className="ph-cloud-arrow-up"></i>
                                          <p>Select Cover Image</p>
                                        </div>
                                        <button 
                                          type="button" 
                                          className="select-file-btn" 
                                          onClick={() => document.getElementById('series-cover-input-sidebar')?.click()}
                                          disabled={isCreatingSeries}
                                        >
                                          <i className="ph-upload"></i>
                                          Select File
                                        </button>
                                      </>
                                    )}
                                  </>
                                )}
                                {/* Only enable file input when creating new series or editing chapters, not when editing series */}
                                {!(isEditMode && video?.videoType === 'series') && (
                                  <input
                                    id="series-cover-input-sidebar"
                                    type="file"
                                    accept="image/*"
                                    onChange={handleSeriesCoverChange}
                                    style={{ display: 'none' }}
                                    disabled={isCreatingSeries}
                                  />
                                )}
                              </div>
                            </Form.Group>
                          </Col>
                        </Row>


                        {/* Create Series Button - Only show in create mode */}
                        {!isEditMode && (
                          <div className="d-flex justify-content-end mt-4">
                            <button
                              type="button"
                              className="btn btn-primary"
                              onClick={handleCreateSeries}
                              disabled={isCreatingSeries || !seriesData.title.trim() || !selectedEntity || selectedEntityType !== EntityType.COACH || !seriesData.subCategory.trim() || !(selectedEntity.sports && Array.isArray(selectedEntity.sports) && selectedEntity.sports.length > 0) || !seriesCoverFile}
                            >
                              {isCreatingSeries ? (
                                <>
                                  <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                  Creating...
                                </>
                              ) : (
                                <>
                                  <i className="ph-check me-2"></i>
                                  Create Series
                                </>
                              )}
                            </button>
                          </div>
                        )}

                        {/* Update Series Button - Only show when editing a series (not chapters) */}
                        {isEditMode && createdSeriesId && video?.videoType === 'series' && (
                          <div className="d-flex justify-content-end mt-4">
                            <button
                              type="button"
                              className="btn btn-primary"
                              onClick={handleUpdateSeries}
                              disabled={uploading || !hasFormChanges || !seriesData.title?.trim() || !selectedEntity || selectedEntityType !== EntityType.COACH || !seriesData.subCategory?.trim() || !(selectedEntity.sports && selectedEntity.sports.length > 0)}
                            >
                              {uploading ? (
                                <>
                                  <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                  Updating...
                                </>
                              ) : (
                                <>
                                  <i className="ph-check me-2"></i>
                                  Update Series
                                </>
                              )}
                            </button>
                      </div>
                        )}
                      </div>
                      )}
                    </Tab.Pane>
                  )}

                  {/* Chapters Tab - Level Up only (Sidebar) - Create Mode */}
                  {category === VideoCategory.LEVEL_UP && !isEditMode && (
                    <Tab.Pane eventKey="chapters">
                      <div className="form-section">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <label className="form-section-label mb-0">Upload Chapter</label>
                          <button
                            type="button"
                            className="btn btn-primary btn-sm"
                            onClick={addNewChapter}
                            disabled={uploadingChapters || !createdSeriesId}
                          >
                            <i className="ph-plus me-2"></i>
                            Add New
                          </button>
                        </div>

                        {chapters.length === 0 ? (
                          <div className="text-center py-5 text-muted">
                            <i className="ph-play-circle" style={{ fontSize: '48px' }}></i>
                            <p className="mt-3">No chapters added yet. Click "Add New +" to add a chapter.</p>
                          </div>
                        ) : (
                          <div className="chapters-list">
                            {chapters.map((chapter, index) => (
                              <div key={chapter.id} className="chapter-card mb-4 p-3 border rounded">
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                  <h6 className="mb-0">Chapter {index + 1}</h6>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-danger"
                                    onClick={() => removeChapter(chapter.id)}
                                    disabled={chapter.uploading || uploadingChapters}
                                  >
                                    <i className="ph-trash"></i>
                                  </button>
                                </div>

                                {/* Chapter Title */}
                                <Form.Group className="mb-3">
                                  <Form.Label>Title</Form.Label>
                                  <Form.Control
                                    type="text"
                                    value={chapter.title}
                                    onChange={(e) => updateChapter(chapter.id, 'title', e.target.value)}
                                    placeholder="Enter chapter title"
                                    disabled={chapter.uploading || uploadingChapters || chapter.uploaded}
                                  />
                                </Form.Group>

                                {/* Chapter Description */}
                                <Form.Group className="mb-3">
                                  <Form.Label>Description</Form.Label>
                                  <Form.Control
                                    as="textarea"
                                    rows={3}
                                    value={chapter.description}
                                    onChange={(e) => updateChapter(chapter.id, 'description', e.target.value)}
                                    placeholder="Enter chapter description"
                                    disabled={chapter.uploading || uploadingChapters}
                                  />
                                </Form.Group>

                                {/* Chapter Video and Thumbnail */}
                                <Row>
                                  <Col md={6}>
                                    <Form.Group className="mb-3">
                                      <Form.Label>Video</Form.Label>
                                      <div className="video-upload-box">
                                        {chapter.videoPreview ? (
                                          <div className="video-preview-container">
                                            <video 
                                              src={chapter.videoPreview} 
                                              controls 
                                              className="video-preview"
                                              style={{ maxWidth: '100%', maxHeight: '150px', borderRadius: '4px' }}
                                            />
                                            <button 
                                              type="button" 
                                              className="select-file-btn btn-sm" 
                                              onClick={() => document.getElementById(`chapter-video-sidebar-${chapter.id}`)?.click()}
                                              disabled={chapter.uploading || uploadingChapters}
                                            >
                                              Change
                                            </button>
                                          </div>
                                        ) : (
                                          <button 
                                            type="button" 
                                            className="select-file-btn btn-sm" 
                                            onClick={() => document.getElementById(`chapter-video-sidebar-${chapter.id}`)?.click()}
                                            disabled={chapter.uploading || uploadingChapters}
                                          >
                                            <i className="ph-upload"></i>
                                            Select Video
                                          </button>
                                        )}
                                        <input
                                          id={`chapter-video-sidebar-${chapter.id}`}
                                          type="file"
                                          accept="video/mp4,video/quicktime,.mp4,.mov"
                                          onChange={(e) => handleChapterVideoChange(chapter.id, e)}
                                          style={{ display: 'none' }}
                                          disabled={chapter.uploading || uploadingChapters}
                                        />
                                      </div>
                                    </Form.Group>
                                  </Col>
                                  <Col md={6}>
                                    <Form.Group className="mb-3">
                                      <Form.Label>Thumbnail</Form.Label>
                                      <div className="video-upload-box">
                                        {chapter.thumbnailPreview ? (
                                          <div className="video-preview-container">
                                            <img 
                                              src={chapter.thumbnailPreview} 
                                              alt="Thumbnail"
                                              style={{ maxWidth: '100%', maxHeight: '150px', borderRadius: '4px', objectFit: 'contain' }}
                                            />
                                            <button 
                                              type="button" 
                                              className="select-file-btn btn-sm" 
                                              onClick={() => document.getElementById(`chapter-thumbnail-sidebar-${chapter.id}`)?.click()}
                                              disabled={chapter.uploading || uploadingChapters}
                                            >
                                              Change
                                            </button>
                                          </div>
                                        ) : (
                                          <button 
                                            type="button" 
                                            className="select-file-btn btn-sm" 
                                            onClick={() => document.getElementById(`chapter-thumbnail-sidebar-${chapter.id}`)?.click()}
                                            disabled={chapter.uploading || uploadingChapters}
                                          >
                                            <i className="ph-upload"></i>
                                            Select Thumbnail
                                          </button>
                                        )}
                                        <input
                                          id={`chapter-thumbnail-sidebar-${chapter.id}`}
                                          type="file"
                                          accept="image/*"
                                          onChange={(e) => handleChapterThumbnailChange(chapter.id, e)}
                                          style={{ display: 'none' }}
                                          disabled={chapter.uploading || uploadingChapters}
                                        />
                                      </div>
                                    </Form.Group>
                                  </Col>
                                </Row>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </Tab.Pane>
                  )}

                  {/* Chapters Tab - Level Up only (Sidebar) - Edit Mode - List View */}
                  {category === VideoCategory.LEVEL_UP && isEditMode && (createdSeriesId || (video?.videoType === 'series' && video?._id)) && (
                    <Tab.Pane eventKey="chapters">
                      <div className="form-section">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <label className="form-section-label mb-0">Upload Chapter</label>
                          <button
                            type="button"
                            className="btn btn-primary btn-sm"
                            onClick={() => {
                              setFormData({
                                title: '',
                                description: '',
                                schedulePublish: false,
                                publishDate: '',
                                publishTime: ''
                              });
                              setVideoFile(null);
                              setVideoPreview(null);
                              setThumbnailFile(null);
                              setThumbnailPreview(null);
                              setIsPortrait(false);
                              setSelectedChapterForEdit(null);
                              setOriginalChapterData(null);
                              setHasChapterChanges(false);
                              setShowAddChapterForm(true);
                            }}
                            disabled={uploadingChapters}
                          >
                            <i className="ph-plus me-2"></i>
                            Add New
                          </button>
                        </div>

                        {loadingChapters ? (
                          <div className="text-center py-4">
                            <div className="spinner-border spinner-border-sm" role="status">
                              <span className="visually-hidden">Loading chapters...</span>
                            </div>
                          </div>
                        ) : (
                          <>
                            {showAddChapterForm && (
                              <div className="mb-4 p-4 border rounded bg-light">
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                  <label className="form-section-label mb-0">
                                    {selectedChapterForEdit ? 'Edit Chapter' : 'Add New Chapter'}
                                  </label>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-link text-muted p-0"
                                    onClick={() => {
                                      setShowAddChapterForm(false);
                                      setSelectedChapterForEdit(null);
                                      setOriginalChapterData(null);
                                      setHasChapterChanges(false);
                                      setFormData({
                                        title: '',
                                        description: '',
                                        schedulePublish: false,
                                        publishDate: '',
                                        publishTime: ''
                                      });
                                      setVideoFile(null);
                                      setVideoPreview(null);
                                      setThumbnailFile(null);
                                      setThumbnailPreview(null);
                                      setIsPortrait(false);
                                    }}
                                  >
                                    <i className="ph-x" style={{ fontSize: '20px' }}></i>
                                  </button>
                                </div>

                                <Row>
                                  <Col md={6}>
                                    <div className="form-section">
                                      <label className="form-section-label">
                                        Video Upload
                                        <span style={{ color: 'red', marginLeft: '4px' }}>*</span>
                                      </label>
                                      <div className="video-upload-box">
                                        {videoPreview ? (
                                          <div className="video-preview-container">
                                            <video 
                                              src={videoPreview} 
                                              controls 
                                              className="video-preview"
                                              style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '4px' }}
                                            />
                                            <div className="selected-file-info">
                                              <i className="ph-file-video"></i>
                                              <span>{videoFile?.name || 'Existing video'}</span>
                                              {videoFile && <span className="file-size">({(videoFile.size / (1024 * 1024)).toFixed(2)} MB)</span>}
                                            </div>
                                            <button 
                                              type="button" 
                                              className="select-file-btn" 
                                              onClick={() => document.getElementById('chapter-video-file-input-inline')?.click()}
                                              disabled={uploading}
                                              style={{ marginTop: '8px' }}
                                            >
                                              <i className="ph-upload"></i>
                                              Change Video
                                            </button>
                                          </div>
                                        ) : (
                                          <>
                                            <div className="video-upload-placeholder">
                                              <i className="ph-cloud-arrow-up"></i>
                                              <p>Select Video to Upload</p>
                                              <p className="video-upload-hint">Supported Format: MP4, MOV (Max: 2GB)</p>
                                            </div>
                                            <button 
                                              type="button" 
                                              className="select-file-btn" 
                                              onClick={() => document.getElementById('chapter-video-file-input-inline')?.click()}
                                              disabled={uploading}
                                            >
                                              <i className="ph-upload"></i>
                                              Select File
                                            </button>
                                          </>
                                        )}
                                        <input
                                          id="chapter-video-file-input-inline"
                                          type="file"
                                          accept="video/mp4,video/quicktime,.mp4,.mov"
                                          onChange={handleVideoFileChange}
                                          style={{ display: 'none' }}
                                          disabled={uploading}
                                        />
                                      </div>
                                      
                                      {(videoFile || videoPreview) && (
                                        <Form.Group className="mb-3 mt-3">
                                          <Form.Label>
                                            Video Orientation
                                            <span style={{ color: 'red', marginLeft: '4px' }}>*</span>
                                          </Form.Label>
                                          <div className="d-flex align-items-center gap-3">
                                            <span className={isPortrait ? 'text-muted' : 'fw-bold'}>Landscape</span>
                                            <FormCheck
                                              type="switch"
                                              id="chapter-portrait-toggle-inline"
                                              label={isPortrait ? 'Portrait' : 'Landscape'}
                                              checked={isPortrait}
                                              onChange={(e) => setIsPortrait(e.target.checked)}
                                              disabled={uploading}
                                              style={{ margin: 0 }}
                                            />
                                            <span className={isPortrait ? 'fw-bold' : 'text-muted'}>Portrait</span>
                                          </div>
                                        </Form.Group>
                                      )}
                                    </div>
                                  </Col>
                                  <Col md={6}>
                                    <div className="form-section">
                                      <label className="form-section-label">
                                        Video Thumbnail
                                        <span style={{ color: 'red', marginLeft: '4px' }}>*</span>
                                      </label>
                                      <div className="video-upload-box">
                                        {thumbnailPreview ? (
                                          <div className="video-preview-container">
                                            <img 
                                              src={thumbnailPreview} 
                                              alt="Thumbnail preview"
                                              style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '4px', objectFit: 'contain' }}
                                            />
                                            <div className="selected-file-info">
                                              <i className="ph-image"></i>
                                              <span>{thumbnailFile?.name || 'Existing thumbnail'}</span>
                                              {thumbnailFile && <span className="file-size">({(thumbnailFile.size / (1024 * 1024)).toFixed(2)} MB)</span>}
                                            </div>
                                            <button 
                                              type="button" 
                                              className="select-file-btn" 
                                              onClick={() => document.getElementById('chapter-thumbnail-input-inline')?.click()}
                                              disabled={uploading}
                                              style={{ marginTop: '8px' }}
                                            >
                                              <i className="ph-upload"></i>
                                              Change Thumbnail
                                            </button>
                                          </div>
                                        ) : (
                                          <>
                                            <div className="video-upload-placeholder">
                                              <i className="ph-cloud-arrow-up"></i>
                                              <p>Select Thumbnail to Upload</p>
                                              <p className="video-upload-hint">Supported Format: JPG, PNG, GIF (Max: 50MB)</p>
                                            </div>
                                            <button 
                                              type="button" 
                                              className="select-file-btn" 
                                              onClick={() => document.getElementById('chapter-thumbnail-input-inline')?.click()}
                                              disabled={uploading}
                                            >
                                              <i className="ph-upload"></i>
                                              Select File
                                            </button>
                                          </>
                                        )}
                                        <input
                                          type="file"
                                          accept="image/*"
                                          style={{ display: 'none' }}
                                          id="chapter-thumbnail-input-inline"
                                          onChange={handleThumbnailFileChange}
                                          disabled={uploading}
                                        />
                                      </div>
                                    </div>
                                  </Col>
                                </Row>

                                <Form.Group className="mb-3">
                                  <Form.Label>
                                    Chapter Title
                                    <span style={{ color: 'red', marginLeft: '4px' }}>*</span>
                                  </Form.Label>
                                  <Form.Control
                                    type="text"
                                    name="title"
                                    value={formData.title}
                                    onChange={handleInputChange}
                                    placeholder="Enter Chapter Title"
                                    required
                                    disabled={uploading}
                                  />
                                </Form.Group>

                                <Form.Group className="mb-3">
                                  <Form.Label>Description</Form.Label>
                                  <Form.Control
                                    as="textarea"
                                    rows={4}
                                    name="description"
                                    value={formData.description}
                                    onChange={handleInputChange}
                                    placeholder="Enter Chapter Description"
                                    disabled={uploading}
                                  />
                                </Form.Group>

                                <div className="d-flex justify-content-end gap-2">
                                  <button
                                    type="button"
                                    className="btn btn-secondary btn-sm"
                                    onClick={() => {
                                      setShowAddChapterForm(false);
                                      setSelectedChapterForEdit(null);
                                      setOriginalChapterData(null);
                                      setHasChapterChanges(false);
                                      setFormData({
                                        title: '',
                                        description: '',
                                        schedulePublish: false,
                                        publishDate: '',
                                        publishTime: ''
                                      });
                                      setVideoFile(null);
                                      setVideoPreview(null);
                                      setThumbnailFile(null);
                                      setThumbnailPreview(null);
                                      setIsPortrait(false);
                                    }}
                                    disabled={uploading}
                                  >
                                    Cancel
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-primary btn-sm"
                                    onClick={async () => {
                                      if (!formData.title.trim()) {
                                        toast.error('Chapter title is required');
                                        return;
                                      }
                                      
                                      // For new chapters, video and thumbnail are required
                                      if (!selectedChapterForEdit) {
                                        if (!videoFile && !videoPreview) {
                                          toast.error('Video is required');
                                          return;
                                        }
                                        if (!thumbnailFile && !thumbnailPreview) {
                                          toast.error('Thumbnail is required');
                                          return;
                                        }
                                      }
                                      
                                      // Use video._id if createdSeriesId is not set (for series editing)
                                      const seriesIdToUse = createdSeriesId || (video?.videoType === 'series' ? video._id : null);
                                      if (!seriesIdToUse) {
                                        toast.error('Series ID is missing');
                                        return;
                                      }

                                      setUploading(true);
                                      try {
                                        if (selectedChapterForEdit) {
                                          // Update existing chapter
                                          const updatePayload: any = {
                                            title: formData.title.trim(),
                                            description: formData.description.trim(),
                                            isPortrait: isPortrait
                                          };

                                          // If new video file is selected, upload it
                                          if (videoFile) {
                                            const videoContentType = getVideoContentType(videoFile);
                                            const thumbnailContentType = thumbnailFile 
                                              ? (thumbnailFile.type.split('/')[1] || 'png') 
                                              : 'png';
                                            
                                            const uploadUrls = await communityService.generateUploadUrls(
                                              videoContentType,
                                              thumbnailContentType
                                            );

                                            const videoMimeType = videoFile.type || (videoContentType === 'mov' ? 'video/quicktime' : 'video/mp4');
                                            await communityService.uploadToS3(uploadUrls.videoUploadUrl, videoFile, videoMimeType);
                                            updatePayload.videoId = uploadUrls.videoId;
                                            if (uploadUrls.videoKey) {
                                              updatePayload.videoKey = uploadUrls.videoKey;
                                            }
                                          }

                                          // If new thumbnail file is selected, upload it
                                          if (thumbnailFile) {
                                            const thumbnailContentType = thumbnailFile.type.split('/')[1] || 'png';
                                            const videoContentType = videoFile 
                                              ? getVideoContentType(videoFile) 
                                              : 'mp4';
                                            
                                            const uploadUrls = await communityService.generateUploadUrls(
                                              videoContentType,
                                              thumbnailContentType
                                            );

                                            await communityService.uploadToS3(uploadUrls.thumbnailUploadUrl, thumbnailFile, thumbnailFile.type);
                                            if (uploadUrls.thumbnailKey) {
                                              updatePayload.thumbnailKey = uploadUrls.thumbnailKey;
                                            }
                                          }

                                          // Call update API
                                          await communityService.updateVideo(selectedChapterForEdit._id, updatePayload);
                                          toast.success('Chapter updated successfully');
                                          
                                          // Reload chapters after update
                                          if (seriesIdToUse) {
                                            setTimeout(async () => {
                                              try {
                                                await reloadSeriesData(seriesIdToUse);
                                              } catch (error) {
                                                console.error('Error reloading chapters after update:', error);
                                              }
                                            }, 500);
                                          }
                                        } else {
                                          // Create new chapter
                                          const videoContentType = videoFile ? getVideoContentType(videoFile) : 'mp4';
                                          const thumbnailContentType = thumbnailFile ? (thumbnailFile.type.split('/')[1] || 'png') : 'png';

                                          // Step 1: Generate presigned URLs
                                          const uploadUrls = await communityService.generateUploadUrls(
                                            videoContentType,
                                            thumbnailContentType
                                          );

                                          // Step 2: Upload video to S3
                                          if (videoFile && uploadUrls.videoUploadUrl) {
                                            const videoMimeType = videoFile.type || (videoContentType === 'mov' ? 'video/quicktime' : 'video/mp4');
                                            await communityService.uploadToS3(
                                              uploadUrls.videoUploadUrl,
                                              videoFile,
                                              videoMimeType
                                            );
                                          }

                                          // Step 3: Upload thumbnail to S3
                                          if (thumbnailFile && uploadUrls.thumbnailUploadUrl) {
                                            await communityService.uploadToS3(
                                              uploadUrls.thumbnailUploadUrl,
                                              thumbnailFile,
                                              thumbnailFile.type
                                            );
                                          }

                                          // Step 4: Get next chapter number
                                          const nextChapterNumber = chapters.length > 0 
                                            ? Math.max(...chapters.map((ch: any) => ch.chapterNumber || 0)) + 1
                                            : 1;

                                          // Step 5: Complete upload with series ID
                                          const tags = selectedTags.map(tag => tag.value);
                                          const payload: any = {
                                            videoId: uploadUrls.videoId,
                                            title: formData.title.trim(),
                                            description: formData.description.trim(),
                                            category: 'level_up',
                                            contentType: 'long_form',
                                            mediaType: 'video',
                                            videoType: 'chapter',
                                            tags: tags,
                                            uploadedBy: user?._id || user?.id || '',
                                            uploadedByRole: user?.role || 'admin',
                                            isPortrait: isPortrait,
                                            seriesId: seriesIdToUse,
                                            chapterNumber: nextChapterNumber
                                          };

                                          // Add metadata if entity is selected
                                          if (selectedEntity && selectedEntityType) {
                                            const baseMetadata = {
                                              id: selectedEntityType === EntityType.SFL_USER ? 'sfl' : selectedEntity.entityId,
                                              type: selectedEntityType,
                                              userName: selectedEntityType === EntityType.SFL_USER ? 'sfl' : selectedEntity.entityName,
                                              userProfilePic: selectedEntityType === EntityType.SFL_USER ? 'shared/sfl-logo.png' : (selectedEntity.profilePic || undefined)
                                            };

                                            if (selectedEntityType === EntityType.COACH) {
                                              // CRITICAL: Use the same logic as handleSingleChapterUpload to construct basicInfo
                                              let basicInfo = selectedEntity.basicInfo || {};
                                              
                                              // If basicInfo is empty, construct from selectedEntity properties
                                              if (!basicInfo || (typeof basicInfo === 'object' && Object.keys(basicInfo).length === 0)) {
                                                basicInfo = {
                                                  fullName: selectedEntity.entityName || '',
                                                  profilePic: selectedEntity.profilePic || null,
                                                  sports: Array.isArray(selectedEntity.sports) ? selectedEntity.sports : [],
                                                  dob: null,
                                                  email: null,
                                                  ageCategory: [],
                                                  certification: [],
                                                  experience: null,
                                                  _id: selectedEntity.entityId || selectedEntity.value || ''
                                                };
                                              }
                                              
                                              // Get values, prioritizing basicInfo but falling back to selectedEntity properties
                                              const sports = (basicInfo && Array.isArray(basicInfo.sports) && basicInfo.sports.length > 0)
                                                ? basicInfo.sports 
                                                : (Array.isArray(selectedEntity.sports) && selectedEntity.sports.length > 0 
                                                  ? selectedEntity.sports 
                                                  : []);
                                              
                                              const profilePic = (basicInfo && basicInfo.profilePic) || selectedEntity.profilePic || null;
                                              const fullName = (basicInfo && basicInfo.fullName) || selectedEntity.entityName || '';
                                              const coachId = (basicInfo && basicInfo._id) || selectedEntity.entityId || selectedEntity.value || '';
                                              
                                              // Construct complete basicInfo ensuring all expected fields are present
                                              const basicInfoComplete: any = {
                                                fullName: fullName || selectedEntity.entityName || '',
                                                profilePic: profilePic || selectedEntity.profilePic || null,
                                                sports: sports.length > 0 ? sports : (Array.isArray(selectedEntity.sports) ? selectedEntity.sports : []),
                                                dob: (basicInfo && basicInfo.dob !== undefined) ? basicInfo.dob : null,
                                                email: (basicInfo && basicInfo.email !== undefined) ? basicInfo.email : null,
                                                ageCategory: (basicInfo && Array.isArray(basicInfo.ageCategory)) ? basicInfo.ageCategory : [],
                                                certification: (basicInfo && Array.isArray(basicInfo.certification)) ? basicInfo.certification : [],
                                                experience: (basicInfo && basicInfo.experience !== undefined) ? basicInfo.experience : null,
                                                _id: coachId
                                              };
                                              
                                              // Only add location if it exists (optional field)
                                              if (basicInfo && basicInfo.location !== undefined) {
                                                basicInfoComplete.location = basicInfo.location;
                                              }
                                              
                                              // Preserve any other fields from basicInfo
                                              if (basicInfo && typeof basicInfo === 'object' && !Array.isArray(basicInfo)) {
                                                Object.keys(basicInfo).forEach(key => {
                                                  if (!basicInfoComplete.hasOwnProperty(key) && basicInfo[key] !== undefined) {
                                                    basicInfoComplete[key] = basicInfo[key];
                                                  }
                                                });
                                              }
                                              
                                              // Final validation: Ensure basicInfoComplete is never empty
                                              const requiredFields = ['fullName', 'profilePic', 'sports', 'dob', 'email', 'ageCategory', 'certification', 'experience', '_id'];
                                              const missingFields = requiredFields.filter(field => !basicInfoComplete.hasOwnProperty(field));
                                              
                                              if (missingFields.length > 0 || Object.keys(basicInfoComplete).length === 0) {
                                                console.error('ERROR: basicInfoComplete is missing fields! Missing:', missingFields);
                                                missingFields.forEach(field => {
                                                  if (field === 'fullName') basicInfoComplete.fullName = selectedEntity.entityName || '';
                                                  else if (field === 'profilePic') basicInfoComplete.profilePic = selectedEntity.profilePic || null;
                                                  else if (field === 'sports') basicInfoComplete.sports = Array.isArray(selectedEntity.sports) ? selectedEntity.sports : [];
                                                  else if (field === 'dob') basicInfoComplete.dob = null;
                                                  else if (field === 'email') basicInfoComplete.email = null;
                                                  else if (field === 'ageCategory') basicInfoComplete.ageCategory = [];
                                                  else if (field === 'certification') basicInfoComplete.certification = [];
                                                  else if (field === 'experience') basicInfoComplete.experience = null;
                                                  else if (field === '_id') basicInfoComplete._id = selectedEntity.entityId || selectedEntity.value || '';
                                                });
                                              }
                                              
                                              // Verify the object is not empty before using it
                                              if (Object.keys(basicInfoComplete).length === 0) {
                                                console.error('CRITICAL ERROR: basicInfoComplete is still empty! Using emergency fallback');
                                                basicInfoComplete.fullName = selectedEntity.entityName || 'Unknown';
                                                basicInfoComplete.profilePic = selectedEntity.profilePic || null;
                                                basicInfoComplete.sports = Array.isArray(selectedEntity.sports) ? selectedEntity.sports : [];
                                                basicInfoComplete.dob = null;
                                                basicInfoComplete.email = null;
                                                basicInfoComplete.ageCategory = [];
                                                basicInfoComplete.certification = [];
                                                basicInfoComplete.experience = null;
                                                basicInfoComplete._id = selectedEntity.entityId || selectedEntity.value || '';
                                              }
                                              
                                              payload.metadata = {
                                                ...baseMetadata,
                                                basicInfo: basicInfoComplete
                                              };
                                            } else {
                                              payload.metadata = baseMetadata;
                                            }
                                          }

                                          // Add S3 keys
                                          if (uploadUrls.videoKey) {
                                            payload.videoKey = uploadUrls.videoKey;
                                          }
                                          if (uploadUrls.thumbnailKey) {
                                            payload.thumbnailKey = uploadUrls.thumbnailKey;
                                          }

                                          const chapterResponse = await communityService.completeUpload(payload);
                                          toast.success('Chapter added successfully');
                                          
                                          // Optimistically add the chapter to the list if we have the response data
                                          if (chapterResponse && chapterResponse._id) {
                                            const newChapter = {
                                              _id: chapterResponse._id,
                                              title: formData.title.trim(),
                                              description: formData.description.trim(),
                                              chapterNumber: nextChapterNumber,
                                              thumbnailUrl: uploadUrls.thumbnailKey || chapterResponse.thumbnailUrl,
                                              contentUrl: uploadUrls.videoKey || chapterResponse.contentUrl,
                                              isPortrait: isPortrait,
                                              status: chapterResponse.status || 'draft'
                                            };
                                            setChapters(prev => [...prev, newChapter]);
                                          }
                                        }

                                        // Reset form
                                        setShowAddChapterForm(false);
                                        setSelectedChapterForEdit(null);
                                        setOriginalChapterData(null);
                                        setHasChapterChanges(false);
                                        setFormData({
                                          title: '',
                                          description: '',
                                          schedulePublish: false,
                                          publishDate: '',
                                          publishTime: ''
                                        });
                                        setVideoFile(null);
                                        setVideoPreview(null);
                                        setThumbnailFile(null);
                                        setThumbnailPreview(null);
                                        setIsPortrait(false);

                                        // Reload series data to get updated chapters list
                                        if (seriesIdToUse) {
                                          setTimeout(async () => {
                                            await reloadSeriesData(seriesIdToUse);
                                          }, 500);
                                        }
                                        
                                        // Trigger success callback to refresh the listing (form stays open for adding more chapters)
                                        // Use setTimeout to ensure state updates are complete before triggering refresh
                                        setTimeout(() => {
                                          onSuccess?.();
                                        }, 100);
                                      } catch (error: any) {
                                        console.error('Error saving chapter:', error);
                                        toast.error(error?.message || 'Failed to save chapter');
                                      } finally {
                                        setUploading(false);
                                      }
                                    }}
                                    disabled={uploading || !formData.title.trim() || !formData.description.trim() || 
                                      ((selectedChapterForEdit || (isEditMode && category === VideoCategory.LEVEL_UP && (video?.videoType === 'chapter' || (video?.seriesId && video?.videoType !== 'series')))) && !hasChapterChanges)}
                                  >
                                    {uploading ? 'Saving...' : (selectedChapterForEdit || (isEditMode && category === VideoCategory.LEVEL_UP && (video?.videoType === 'chapter' || (video?.seriesId && video?.videoType !== 'series')))) ? 'Update Chapter' : 'Add Chapter'}
                                  </button>
                                </div>
                              </div>
                            )}
                            <div className="chapters-list">
                              {chapters.map((chapter: any, index: number) => (
                              <div 
                                key={chapter._id || chapter.id} 
                                className="mb-3 p-3 border rounded"
                                style={{
                                  cursor: 'pointer',
                                  transition: 'all 0.3s ease',
                                  backgroundColor: selectedChapterForEdit?._id === chapter._id ? '#f0f8ff' : 'white'
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                                  e.currentTarget.style.transform = 'translateY(-2px)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.boxShadow = 'none';
                                  e.currentTarget.style.transform = 'translateY(0)';
                                }}
                                onClick={() => handleEditChapter(chapter)}
                              >
                                <div className="d-flex align-items-start gap-3">
                                  {/* Thumbnail */}
                                  {chapter.thumbnailUrl && (
                                    <div style={{ 
                                      width: '120px', 
                                      height: '80px', 
                                      flexShrink: 0,
                                      borderRadius: '4px',
                                      overflow: 'hidden',
                                      backgroundColor: '#f0f0f0'
                                    }}>
                                      <img
                                        src={chapter.thumbnailUrl.startsWith('http') 
                                          ? chapter.thumbnailUrl 
                                          : `https://cdn-dev.sportsforlife.in/${chapter.thumbnailUrl}`}
                                        alt={chapter.title || chapter.chapterTitle}
                                        style={{
                                          width: '100%',
                                          height: '100%',
                                          objectFit: 'cover'
                                        }}
                                        onError={(e) => {
                                          const target = e.target as HTMLImageElement;
                                          // Only set placeholder if we haven't already tried it
                                          if (target.src !== window.location.origin + '/placeholder-video.png') {
                                            target.src = '/placeholder-video.png';
                                          } else {
                                            // If placeholder also fails, hide the image to prevent infinite loop
                                            target.style.display = 'none';
                                          }
                                        }}
                                      />
                                    </div>
                                  )}
                                  <div className="flex-grow-1">
                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                      <div>
                                        <h6 className="mb-1" style={{ fontSize: '16px', fontWeight: 600 }}>
                                          {chapter.chapterTitle || chapter.title || `Chapter ${chapter.chapterNumber || index + 1}`}
                                        </h6>
                                        {chapter.chapterNumber && (
                                          <p className="mb-1 text-muted" style={{ fontSize: '14px' }}>
                                            Chapter {chapter.chapterNumber}
                                          </p>
                                        )}
                                      </div>
                                      <div className="chapter-menu-wrapper" style={{ position: 'relative' }}>
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-link text-muted p-0 chapter-menu-button"
                                          style={{ fontSize: '18px' }}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setOpenChapterMenuId(openChapterMenuId === chapter._id ? null : chapter._id);
                                          }}
                                        >
                                          <i className="ph-dots-three-vertical"></i>
                                        </button>
                                        {openChapterMenuId === chapter._id && isEditMode && (
                                          <div 
                                            className="dropdown-menu show"
                                            style={{
                                              position: 'absolute',
                                              right: 0,
                                              top: '100%',
                                              zIndex: 1000,
                                              minWidth: '150px',
                                              marginTop: '4px',
                                              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                                              borderRadius: '8px',
                                              padding: '4px',
                                              backgroundColor: 'white'
                                            }}
                                            onClick={(e) => e.stopPropagation()}
                                          >
                                            <button
                                              className="dropdown-item"
                                              style={{ 
                                                padding: '8px 12px',
                                                border: 'none',
                                                background: 'none',
                                                width: '100%',
                                                textAlign: 'left',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                                fontSize: '14px'
                                              }}
                                              onClick={() => {
                                                setOpenChapterMenuId(null);
                                                handleEditChapter(chapter);
                                              }}
                                            >
                                              <i className="ph-pencil"></i>
                                              <span>Edit</span>
                                            </button>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    {chapter.description && (
                                      <p className="mb-0 text-muted" style={{ fontSize: '14px', lineHeight: '1.5' }}>
                                        {chapter.description.length > 100 
                                          ? `${chapter.description.substring(0, 100)}...` 
                                          : chapter.description}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                            </div>
                          </>
                        )}
                      </div>
                    </Tab.Pane>
                  )}

                  {(category === VideoCategory.ACTION || category === VideoCategory.SOCIAL) && (
                  <Tab.Pane eventKey="player-details">
                    <div className="form-section">
                      <label className="form-section-label">Entity Details</label>
                      
                      <Form.Group className="mb-3">
                        <Form.Label>
                          Who is adding this video?
                          <span style={{ color: 'red', marginLeft: '4px' }}>*</span>
                        </Form.Label>
                        <Select
                          options={[
                            { value: EntityType.PLAYER, label: 'Player' },
                            { value: EntityType.ACADEMY, label: 'Academy' },
                            { value: EntityType.SFL_USER, label: 'SFL User' }
                          ]}
                          value={selectedEntityType ? { 
                            value: selectedEntityType, 
                            label: selectedEntityType === EntityType.SFL_USER ? 'SFL User' : selectedEntityType.charAt(0).toUpperCase() + selectedEntityType.slice(1)
                          } : null}
                          onChange={(option: any) => {
                            if (isEditMode) {
                              setHasFormChanges(true);
                            }
                            const newEntityType = option?.value || null;
                            setSelectedEntityType(newEntityType);
                            
                            // If SFL_USER is selected, automatically set entity
                            if (newEntityType === EntityType.SFL_USER) {
                              setSelectedEntity({
                                value: 'sfl',
                                label: 'SFL',
                                entityId: 'sfl',
                                entityName: 'sfl',
                                profilePic: 'shared/sfl-logo.png', // Update this path if needed
                                entityType: EntityType.SFL_USER
                              });
                            } else {
                              setSelectedEntity(null); // Reset entity when type changes
                            }
                          }}
                          placeholder="Select entity type"
                          isClearable
                          isDisabled={uploading}
                          classNamePrefix="react-select"
                          styles={{
                            control: (base) => ({ 
                              ...base, 
                              minHeight: '38px',
                              border: '1px solid #dee2e6',
                              borderRadius: '6px'
                            }),
                            menu: (base) => ({ 
                              ...base, 
                              zIndex: 9999 
                            }),
                            menuPortal: (base) => ({ 
                              ...base, 
                              zIndex: 9999 
                            })
                          }}
                          menuPortalTarget={document.body}
                        />
                      </Form.Group>

                      {selectedEntityType && selectedEntityType !== EntityType.SFL_USER && (
                <Form.Group className="mb-3">
                          <Form.Label>
                            {selectedEntityType === EntityType.PLAYER ? 'Player' : 
                             selectedEntityType === EntityType.COACH ? 'Coach' : 
                             'Academy'}
                            <span style={{ color: 'red', marginLeft: '4px' }}>*</span>
                          </Form.Label>
                          <AsyncSelect
                            key={`entity-select-sidebar-${selectedEntityType}-${selectedEntity?.entityId || 'none'}`}
                            cacheOptions
                            defaultOptions={selectedEntity ? [selectedEntity] : true}
                            loadOptions={(inputValue: string) => loadEntities(inputValue, selectedEntityType)}
                            value={selectedEntity}
                            onChange={(option: EntityOption | null) => {
                              if (isEditMode) {
                                setHasFormChanges(true);
                              }
                              setSelectedEntity(option);
                            }}
                            placeholder={`Search and select a ${selectedEntityType}`}
                    isClearable
                            isDisabled={uploading}
                    classNamePrefix="react-select"
                            getOptionValue={(option: EntityOption) => option.entityId}
                            getOptionLabel={(option: EntityOption) => option.entityName}
                    styles={{
                      control: (base) => ({ 
                        ...base, 
                        minHeight: '38px',
                        border: '1px solid #dee2e6',
                        borderRadius: '6px'
                      }),
                      menu: (base) => ({ 
                        ...base, 
                        zIndex: 9999 
                      }),
                      menuPortal: (base) => ({ 
                        ...base, 
                        zIndex: 9999 
                      })
                    }}
                    menuPortalTarget={document.body}
                            noOptionsMessage={({ inputValue }) => 
                              inputValue ? `No ${selectedEntityType}s found for "${inputValue}"` : `Start typing to search ${selectedEntityType}s`
                            }
                  />
                </Form.Group>
                      )}
                    </div>
                  </Tab.Pane>
                  )}
                </Tab.Content>

              </Tab.Container>
            )}

          </Form>
        </div>
        
        {/* Footer outside scrollable body - Always visible at bottom */}
        {/* Hide footer when inline and initialTab is 'chapters' (used in modal from detail page) */}
        {!(inline && initialTab === 'chapters') && (
        <div className="add-new-form-footer">
          <button 
            type="button" 
            className="cancel-btn" 
              onClick={() => {
                hasAutoAddedFirstChapter.current = false;
                setChapters([]);
                onHide();
              }}
            disabled={uploading}
          >
            Cancel
          </button>
          {/* Hide Update Video button when editing a series - Update Series button is shown in series-details tab instead */}
          {!(isEditMode && category === VideoCategory.LEVEL_UP && video?.videoType === 'series') && (
            <button 
              type="button" 
              className="upload-btn"
              onClick={(e) => {
                e.preventDefault();
                const form = document.querySelector('.add-new-form-form') as HTMLFormElement;
                if (form) {
                  form.requestSubmit();
                }
              }}
              disabled={uploading || uploadingChapters || !isFormValid() || (isEditMode && !hasFormChanges)}
            >
              {uploading || uploadingChapters ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  {category === VideoCategory.LEVEL_UP && activeTab === 'chapters' ? 'Uploading Chapters...' : 'Uploading...'}
                </>
              ) : (
                <>
                  <i className={isEditMode ? "ph-check" : "ph-upload"}></i>
                  {category === VideoCategory.LEVEL_UP && activeTab === 'chapters' && !isEditMode 
                    ? 'Upload Video +' 
                    : isEditMode 
                      ? (() => {
                          // Determine button text based on what we're editing
                          const isEditingChapter = category === VideoCategory.LEVEL_UP && (video?.videoType === 'chapter' || (video?.seriesId && video?.videoType !== 'series'));
                          
                          if (isEditingChapter) {
                            return 'Update Chapter';
                          } else if (category === VideoCategory.SOCIAL && postType === 'image') {
                            return 'Update Image';
                          } else {
                            return 'Update Video';
                          }
                        })()
                      : 'Upload Video +'}
                </>
              )}
            </button>
          )}
        </div>
        )}
      </div>
    </>
  );
};

export default AddNewCommunityForm;
